/**
 * Client HTTP verso il Control Plane.
 *
 * Il token JWT è conservato in localStorage: condiviso fra le schede e le
 * finestre dello stesso sito, così aprirne un'altra non chiede di rientrare
 * (prima stava in sessionStorage, che è per scheda). Il logout da una scheda
 * vale per tutte (evento `storage`, vedi AuthContext). La sessione scade per
 * inattività: la console rinnova il token mentre la si usa (`/auth/refresh`).
 */
import { t } from '../i18n/lingua'
import type {
  AccessAmministratore, AccessPrivilegio, PasswordLinkShown,
  AccessEvento, AccessFiltri, AccessFonte, AccessImpostazioni, AccessPanoramica, AccessPersona, AccessPostura, AccessPersonaDettaglio, AccessProvaTSA, AccessRegistro, AccessSiem, AccessRicercaSyslog, AccessSondaSyslog, AccessRilevamento, AccessRilevamentoDettaglio, AccessSigilli, AccessSigillo, AccessVerifica,
  DeviceLog,
  AddressContext,
  AddressInventory,
  AddressInventorySettings,
  AddressRecordInfo,
  AgentInstaller,
  AiAnswer,
  AiPrivacyView,
  AiSettings,
  AiStatus,
  AiUsageByAsset,
  AiUsageStats,
  AiUsageSummary,
  AssessmentResult,
  Asset,
  AssetCatalog,
  AssetContract,
  AssetDraft,
  AssetPool,
  AssetSummary,
  AuditPage,
  BackupLog,
  BackupPerformance,
  BackupRuns,
  BaselineState,
  BitLockerAccessRow,
  BitLockerKeyInfo,
  BitLockerOverview,
  BitLockerReveal,
  BitLockerRotationInfo,
  BitLockerSettings,
  BusinessApp,
  BusinessAppElementIn,
  BusinessAppSummary,
  BusinessService,
  Capability,
  CapacityReport,
  CapacityStorageHistory,
  CaptureProbe,
  CatalogoRuoli,
  CertAlertPreview,
  CertAlertRule,
  CertAlertRuleIn,
  CertAlertSentInfo,
  CertAlertsOverview,
  CIActivity,
  CIBusinessServices,
  CIChange,
  CIDetail,
  CIOverview,
  CIProvenance,
  CIRelation,
  CISource,
  CITypeCount,
  CloudEnvironment,
  CloudMapGraph,
  CloudResourceDetail,
  CodeProjectDetail,
  CodeProjectRow,
  ComplianceDeclaration,
  Composizione,
  ConditionGroup,
  ConfigBackupStatus,
  ConfigDiff,
  ConfigRevision,
  ConfigStats,
  ConfigurationItem,
  Connector,
  ConnectorCatalog,
  ConnectorMapping,
  ConnectorTestResult,
  ConsoleDashboard,
  ContainerInventory,
  ContainerItem,
  Contract,
  ContractCatalog,
  ContractEvent,
  ContractSummary,
  Conversation,
  CoppiaHa,
  CorrelationGraph,
  CostCenterSummary,
  CoveredAsset,
  CiContracts,
  Supplier,
  SupplierCandidate,
  SupplierFinding,
  SupplierOverview,
  SupplierProduct,
  SupplierAnswer,
  SupplierAssessment,
  SupplierCertification,
  SupplierCheck,
  SupplierDocument,
  CensoSbomCatalog,
  CensoSbomProduct,
  SupplierDocumentStatement,
  SupplierSbomComparison,
  SupplyChainCheck,
  SupplierRelated,
  SupplierRequest,
  SupplierRequestItem,
  SupplierRequestKind,
  SupplierStatement,
  SupplierWrite,
  SupportExtension,
  SupportExtensionCandidate,
  SupportExtensionPreview,
  SupportExtensionWrite,
  CraDeclared,
  CraDetection,
  CraOverview,
  CraProduct,
  CraProductDetail,
  CraProductIn,
  CraRelease,
  CraReport,
  CraRequirement,
  Credential,
  CredentialGroup,
  CredentialKind,
  Criticita,
  CustomFieldDefinition,
  CustomFieldWrite,
  CynetCoverage,
  CynetOverview,
  CynetRunResult,
  DashboardSezione,
  DemoData,
  DettaglioCluster,
  DeviceList,
  DevicePorts,
  DiscoveryKind,
  DiscoveryOptimization,
  DiscoveryPlan,
  DnsPiano,
  DnsRecordIn,
  DnsRicerca,
  DnsScritturaEsito,
  DnsServer,
  DnsZoneDetail,
  DnsZones,
  DobermannNotifPrefs,
  DobermannReport,
  DobermannSettings,
  DobermannStats,
  DobermannWatch,
  DoppioneCi,
  EagleAnagrafica,
  EagleAvailability,
  EagleStoricoAllarmi,
  ChiaveDelClienteIn,
  DashboardKind,
  EagleCI,
  EagleMonitorato,
  EagleNotifyTutto,
  EagleOverview,
  EagleReportDef,
  EagleReportEsito,
  EagleApplicaEsito,
  AwxConfronto,
  AwxInventario,
  AwxServer,
  EagleProfiliTutto,
  EagleProfilo,
  EagleProfiloDef,
  EagleRegolaProfilo,
  EagleReportSalvato,
  EagleScelta,
  EagleSerie,
  EagleSonda,
  EagleTop, EagleSerieMultipla,
  EditableTaxonomy,
  EndOfSupport,
  EnrollmentToken,
  EntryPoint,
  ExtraAddress,
  ExtraFqdn,
  FiltriComposizione,
  FirewallActivity,
  FirewallDashboard,
  FirewallSyncEsito,
  FirewallTraffic,
  FleetVersions,
  FlowBreakdown,
  FlowExporter,
  FlowFilterState,
  FlowSeries,
  FlowWindow,
  GateDecision,
  GatePlan,
  GatePolicyIn,
  GateSetResult,
  HoneypotAgent,
  HoneypotDecoy,
  HoneypotNotifPrefs,
  HoneypotReport,
  HoneypotStats,
  HostSighting,
  IdentityProvider,
  IdentityProviderInput,
  InventoryAgent,
  IpamCiRecord,
  IpamDocumentaEsito,
  IpamSubnetDetail,
  IpamSubnets,
  IpamVlans,
  Job,
  JobCapture,
  JobCreate,
  JobReport,
  LicenseStatus,
  MacReport,
  MappingProposal,
  MasterDataItem,
  MasterDataKind,
  MasterDataTutte,
  MibSummary,
  MyUsage,
  NetworkDevice,
  NetworkSecurityLive,
  Nis2AcnList,
  Nis2Deadlines,
  Nis2Exposure,
  Nis2Incident,
  Nis2IncidentInput,
  Nis2Obligation,
  Nis2ObligationInput,
  Nis2Profile,
  Nis2Report,
  Nis2Scope,
  Nis2ScopeCandidate,
  Nis2ScopeRules,
  Notifiche,
  ObjectSubtypes,
  ObservedAttribute,
  OggettoTaggabile,
  PassiveInventory,
  PassiveInventoryResult,
  PipelineGateInfo,
  PkiCa,
  PkiChain,
  PkiConnection,
  PkiDeployment,
  PkiDeployTarget,
  PkiKey,
  PkiRecipe,
  PkiRepository,
  PkiRequest,
  PkiTemplate,
  PlaintextTraffic,
  PlanPayload,
  PlanPreviewStep,
  PlanRun,
  PlanStep,
  PlanValidation,
  PoolPreview,
  PortMacEnrichResult,
  PortMacList,
  Principal,
  Probe,
  ProbeDetail,
  CensoOSEsitoRichiesta,
  PatchDetection,
  PatchDetectionDetail,
  PatchLifecycle,
  PatchSla,
  PatchSlaPolicy,
  PatchPolicy,
  PatchPolicyInput,
  PatchPolicyRun,
  PatchException,
  PatchExceptionRequest,
  PatchTicket,
  PatchTicketSettings,
  PatchCoverage,
  PatchReportSchedule,
  PatchReportScheduleInput,
  PatchEvidence,
  CensoOSImpostazioni,
  CensoOSRiepilogo,
  ProbeGroup,
  ProbeSighting,
  ProgramVulnerabilities,
  PublicIp,
  ReadingGap,
  RecognitionRule,
  RecognitionTrend,
  ReferenceComparison,
  ReferenceSignatures,
  ReferenceSourceKey,
  RegistryArtifactRow,
  RegolaPercorsoSoftware,
  RegolaTag,
  RegolaTagIn,
  RemoteObjectType,
  RenovateTriggerPlan,
  ReportInfo,
  ReportResult,
  RolloutResult,
  RouteList,
  RoutePath,
  RuleDraft,
  RulePreview,
  RuntimeView,
  RuoloPersonalizzato,
  RuoloPersonalizzatoIn,
  SbomView,
  ScanStats,
  Schedule,
  ScheduleBulk,
  ScheduleBulkResult,
  ScheduleCreate,
  ScheduleDraft,
  SearchResult,
  SecurityFinding,
  SecurityRisk,
  ServiceEntry,
  ServiceHop,
  ServiceMap,
  ServiceMembers,
  ServiceRuntime,
  ServiceSbom,
  SiteInfo,
  SKU,
  SKUDraft,
  SnmpDriverDraft,
  SoftwareDeadline,
  SoftwareFlow,
  SoftwareListItem,
  SoftwareMap,
  SoftwareMapComponent,
  SoftwareMapLicense,
  SourceEntity,
  StatoCifratura,
  StepCatalogue,
  Summary,
  SupplyChangeOptions,
  SupplyChangePlan,
  SupplyChangeRow,
  SupplyComparison,
  SupplyComponentRow,
  SupplyConnection,
  SupplyOverview,
  SupplyPipelinePlan,
  SupplyPipelineRow,
  SupplyVulnerabilityRow,
  SyncRun,
  TagCatalogo,
  TagCoverage,
  TagPiattaforma,
  TagPolicy,
  Taxonomy,
  EagleBersaglio,
  EagleWallLink,
  EagleWallLinkCreated,
  EagleWallLinkIn,
  EagleWallMe,
  TenantApiKeyCreated,
  TenantApiKeyIn,
  TenantApiKeysResponse,
  TenantHostnameRead,
  TenantMailer,
  TlsRegister,
  TokenResponse,
  TopologyGraph,
  TopologySeed,
  TopologyView,
  TopologyViewConfig,
  TrafficAnalysis,
  TrialExtensionStatus,
  User,
  UserPresence,
  VeeamCiBackup,
  VeeamJobs,
  VeeamObjects,
  VeeamRepositories,
  VeeamVmProtection,
  VexIn,
  VexRow,
  VInventory,
  VirtualEnvironment,
  VirtualHostDetail,
  WazuhCveHosts,
  WazuhFirewall,
  WazuhLive,
  WazuhOverview,
  WlanInventory,
  MedCampi,
  MedFonti,
  MedRichiami,
  MedRichiamo,
  MedStatoRichiamo,
  MedFileDispositivo,
  MedProfiloModello,
  MedScadenze,
  MedVerificheDispositivo,
  MedCespiteContratti,
  MedContratto,
  MedCmmsProposte,
  MedCollegamenti,
  MedMappa,
  MedDisponibilita,
  MedIdentitaFilo,
  MedSistemaDettaglio,
  MedSistemi,
  MedContesto,
  MedDoveRiga,
  MedRegola,
  MedUsoRiga,
  MedDispositivo,
  MedModello,
  MedModelloScheda,
  MedPanoramica,
  MedScheda,
  EagleSparklineParete,
  WallMonitor,
  WallMonitorIso,
  WallMonitorSettings,
} from './types'

const BASE = '/api/v1'
const TOKEN_KEY = 'censo.token'

/**
 * The link for the wall monitors: `/monitor/eagle/<token>` opens the Eagle
 * wall with no session. The token in the address is the bearer of the few
 * read-only calls the wall makes (see `eagle_wall_link_service` in the
 * Control Plane); it is never stored and never touches the session token.
 */
const TOKEN_CHIOSCO: string | null = (() => {
  if (typeof window === 'undefined') return null
  const m = /^\/monitor\/eagle\/([^/?#]+)/.exec(window.location.pathname)
  return m ? decodeURIComponent(m[1]) : null
})()

/** The wall-monitor token, when the page was opened through such a link. */
export function tokenChiosco(): string | null {
  return TOKEN_CHIOSCO
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /**
     * Il dettaglio **strutturato**, come il server l'ha mandato.
     *
     * Il messaggio è la frase da mostrare; questo è ciò che le sta dietro, e a
     * volte è la parte che serve davvero: l'elenco delle righe rifiutate di un
     * CSV con il loro numero, per esempio. «Tre ambiti non validi» dice che
     * qualcosa non va, non **quali** tre — e su duecento righe è un rifiuto
     * che non aiuta a rimediare.
     *
     * È l'estensione della regola già scritta qui sotto: un motivo che il
     * server si è preso la briga di mandare non si butta via.
     */
    public detail?: unknown,
  ) {
    super(message)
  }
}

export function getToken(): string | null {
  if (TOKEN_CHIOSCO) return TOKEN_CHIOSCO
  try {
    const condiviso = localStorage.getItem(TOKEN_KEY)
    if (condiviso) return condiviso
    // Una sessione aperta prima di questo aggiornamento stava nella scheda:
    // la si porta nello spazio condiviso invece di far rientrare.
    const vecchio = sessionStorage.getItem(TOKEN_KEY)
    if (vecchio) {
      localStorage.setItem(TOKEN_KEY, vecchio)
      sessionStorage.removeItem(TOKEN_KEY)
    }
    return vecchio
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    // Memoria del browser non disponibile (navigazione privata restrittiva):
    // la sessione vale finché la pagina resta aperta.
  }
}

/** La chiave con cui il token sta in localStorage: serve all'evento `storage`. */
export const CHIAVE_TOKEN = TOKEN_KEY

/** Scadenza e durata del token corrente (dai claim `exp`/`iat`), in ms. */
export function tempiToken(): { scade: number; durata: number } | null {
  const token = getToken()
  if (!token) return null
  try {
    const parte = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(parte)) as { exp?: number; iat?: number }
    if (!claims.exp) return null
    return { scade: claims.exp * 1000, durata: ((claims.exp - (claims.iat ?? claims.exp - 3600)) * 1000) }
  } catch {
    return null
  }
}

/** Estrae un messaggio leggibile dai diversi formati di errore dell'API. */
/**
 * Il motivo che il server ha mandato, qualunque forma abbia.
 *
 * La regola è una sola, e vale la pena scriverla: **un motivo che il server si è
 * preso la briga di mandare non si butta via**. Le forme del dettaglio sono
 * cinque — `message`, `messaggio`+`tipo`, `problemi`, la lista di FastAPI, il
 * testo semplice — e finché se ne riconoscevano due, le altre finivano nel
 * ripiego «Errore 400».
 *
 * Costava esattamente quello che sembra: una mappatura rifiutata perché scrive
 * sull'identificativo del sistema di destinazione diceva «Errore 400», e il
 * paragrafo che spiegava cosa togliere restava dentro il corpo della risposta.
 * Lo stesso per i connettori, che rispondono `502` con `tipo` e `messaggio`:
 * tutta la diagnostica scritta negli adattatori — quale colonna, quale
 * frammento, dove si rimedia — non arrivava sullo schermo.
 */
function parseError(status: number, body: unknown): ApiError {
  const detail = (body as { detail?: unknown })?.detail

  if (typeof detail === 'string') return new ApiError(status, 'error', detail)

  // Errori di validazione FastAPI: lista di problemi sui campi.
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { loc?: string[]; msg?: string }
    const field = first.loc?.slice(1).join('.') ?? t('richiesta')
    return new ApiError(status, 'validation_error', `${field}: ${first.msg ?? t('valore non valido')}`)
  }

  if (detail && typeof detail === 'object') {
    const d = detail as {
      code?: string
      message?: string
      tipo?: string
      messaggio?: string
      problemi?: unknown
      params?: unknown
    }
    // Il messaggio passa da `t`, come tutto il resto della console.
    //
    // Lo stesso meccanismo dei rapporti: il testo italiano **è** la chiave, e
    // `params` — quando il server lo manda — sono i valori dei segnaposto. Un
    // messaggio composto dal server («piano 'x' inesistente; disponibili: …»)
    // non si traduce finché non diventa un modello, e nel frattempo compare in
    // italiano: leggibile, che è il punto di aver scelto la frase come chiave.
    //
    // Il `code` resta quello che era: è ciò su cui il codice decide, e non deve
    // dipendere dalla lingua di nessuno.
    if (d.message) {
      return new ApiError(
        status,
        d.code ?? 'error',
        t(d.message, (d.params as Record<string, unknown>) ?? {}),
        detail,
      )
    }
    // La forma dei connettori: `tipo` dice **dove guardare** — rete, TLS,
    // credenziale, permesso, protocollo — e senza di lui il messaggio manda a
    // cercare nel posto sbagliato.
    if (d.messaggio) return new ApiError(status, d.tipo ?? 'error', t(d.messaggio))
    // La forma della validazione dei mapping: più problemi, tutti da correggere.
    // Mostrarne uno solo farebbe risolvere il primo e ricominciare da capo.
    if (Array.isArray(d.problemi) && d.problemi.length > 0) {
      return new ApiError(status, 'mapping_invalid', d.problemi.map(String).join(' · '))
    }
    // Una forma che non conosciamo **si mostra comunque**: illeggibile è meglio
    // di assente, perché almeno si può incollare a chi la sa leggere.
    return new ApiError(status, 'error', t('Errore {stato}: {dettaglio}', { stato: status, dettaglio: JSON.stringify(detail) }))
  }

  return new ApiError(status, 'error', t('Errore {stato}', { stato: status }))
}

/**
 * Un errore che l'applicazione non ha prodotto.
 *
 * L'API risponde sempre in JSON. Un corpo che JSON non è significa che la
 * richiesta si è fermata **prima**: quasi sempre il WAF, che risponde con una
 * pagina HTML. Il codice da solo non lo dice — 403 è anche «non hai il permesso»,
 * e i due si risolvono in posti opposti.
 */
function parseInterceptedError(resp: Response): ApiError {
  // Frasi intere, non «dove» + pezzo: l'ordine delle parole cambia con la lingua.
  if (resp.status === 403) {
    return new ApiError(
      403,
      'blocked_upstream',
      t('La richiesta è stata bloccata prima di arrivare all’applicazione (WAF). Se l’operazione è legittima, cerca l’id della regola nei log del servizio waf e aggiungi un’eccezione mirata.'),
    )
  }
  if (resp.status === 429) {
    return new ApiError(429, 'rate_limited', t('Troppi tentativi: riprova fra qualche minuto.'))
  }
  if (resp.status >= 500) {
    return new ApiError(
      resp.status,
      'upstream_error',
      t('La richiesta è stata bloccata prima di arrivare all’applicazione (bilanciatore o WAF): il servizio a valle non ha risposto.'),
    )
  }
  return new ApiError(
    resp.status,
    'error',
    t('La richiesta è stata bloccata prima di arrivare all’applicazione (Errore {stato}).', { stato: resp.status }),
  )
}

/**
 * Quanti identificativi per richiesta nelle azioni in blocco.
 *
 * Il WAF (regola 200007 della configurazione ModSecurity) rifiuta con 400 un
 * corpo JSON con più di mille argomenti, e ogni id di una lista è un
 * argomento: «seleziona tutti quelli del filtro» su duemila CI non passava.
 * Provato contro l'immagine vera del WAF. Le azioni in blocco si spezzano in
 * lotti da 500 e i conteggi si sommano.
 */
export const LOTTO_IDS = 500

/** Monitored parameters per request: five fields each, under the thousand
 *  JSON arguments of WAF rule 200007. */
export const LOTTO_PARAMETRI = 150

export async function aLotti<R>(ids: string[], fai: (lotto: string[]) => Promise<R>): Promise<R[]> {
  const esiti: R[] = []
  for (let i = 0; i < ids.length; i += LOTTO_IDS) esiti.push(await fai(ids.slice(i, i + LOTTO_IDS)))
  if (ids.length === 0) esiti.push(await fai([]))
  return esiti
}

const somma = <R extends object>(esiti: R[], chiavi: (keyof R)[]): R => {
  const fuori = { ...esiti[0] } as R
  for (const k of chiavi) (fuori as Record<keyof R, unknown>)[k] = esiti.reduce((a, e) => a + (Number(e[k]) || 0), 0)
  return fuori
}

/** Le chiamate che APRONO una sessione: non autenticate, non portano un token. */
function apreSessione(path: string): boolean {
  return (
    path.startsWith('/auth/login') ||
    path.startsWith('/auth/select-tenant') ||
    path.startsWith('/auth/set-password') ||
    path.startsWith('/auth/mfa/verify') ||
    path.startsWith('/auth/oidc/')
  )
}

/** Where a dashboard of that kind lives: the home one under /dashboards, Eagle's under /monitoring. */
function percorsoDashboard(kind: DashboardKind): string {
  return kind === 'home' ? '/dashboards' : '/monitoring/dashboards'
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  // Un token vecchio non va allegato a chi sta **entrando**: non autentica
  // nulla e, se un gate a monte lo rifiuta, farebbe comparire «sessione
  // scaduta» a chi sta semplicemente facendo il login (il token resta in
  // localStorage fra una sessione e l'altra, quindi capita spesso).
  if (token && !apreSessione(path)) headers.set('Authorization', `Bearer ${token}`)

  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, { ...init, headers })
  } catch {
    throw new ApiError(0, 'network_error', t('Control Plane non raggiungibile'))
  }

  if (resp.status === 401) {
    // Il 401 di CHI STA ENTRANDO non è una sessione scaduta: è il server che
    // rifiuta le credenziali, e va detto con le sue parole («credenziali non
    // valide»). Il messaggio unico mandava a ricaricare la pagina chi stava
    // semplicemente sbagliando la password — e a cercare un guasto che non
    // c'era. La sessione scaduta resta per le chiamate autenticate.
    if (apreSessione(path)) {
      const corpo = await resp.json().catch(() => null)
      if (corpo !== null) throw parseError(401, corpo)
      // Nessun corpo: è un 401 di un gate a monte su una richiesta d'accesso,
      // non una sessione scaduta (qui non c'era una sessione da perdere).
      throw new ApiError(401, 'unauthorized', t('Accesso non riuscito: riprova'))
    }
    if (TOKEN_CHIOSCO) {
      // A wall monitor: there is no session to drop, and the message is the
      // server's (link revoked, expired, unknown).
      const corpo = await resp.json().catch(() => null)
      if (corpo !== null) throw parseError(401, corpo)
      throw new ApiError(401, 'unauthorized', t('Link per i monitor non valido, revocato o scaduto'))
    }
    setToken(null)
    throw new ApiError(401, 'unauthorized', t('Sessione scaduta: effettua di nuovo l’accesso'))
  }

  if (resp.status === 204) return undefined as T

  const body = await resp.json().catch(() => null)
  if (!resp.ok) {
    // Un errore senza corpo JSON non viene dall'applicazione: viene da qualcosa
    // che sta in mezzo — il WAF che blocca la richiesta, il bilanciatore che la
    // rifiuta. Dirlo cambia dove si guarda: un 403 dell'API è un permesso
    // mancante e si risolve fra gli utenti, un 403 del WAF è una regola e si
    // risolve fra le esclusioni. Senza questa distinzione i due erano
    // indistinguibili, ed è già costato tempo: la console diceva «Errore 403» e
    // basta, per una richiesta che l'applicazione non aveva nemmeno visto.
    if (body === null) throw parseInterceptedError(resp)
    // Licenza scaduta: l'accesso al tenant è interrotto. Non è l'errore di una
    // pagina: lo si annuncia a tutta la console, che mostra una pagina sola.
    const dettaglio = (body as { detail?: { code?: string } })?.detail
    if (resp.status === 403 && dettaglio?.code === 'license_expired') {
      window.dispatchEvent(new CustomEvent('censo:licenza-scaduta', { detail: dettaglio }))
    }
    throw parseError(resp.status, body)
  }
  return body as T
}

/**
 * Scarica una risorsa binaria (es. un PDF) come Blob, con l'autenticazione della
 * sessione. `request` non va bene per i binari: forza `Content-Type: application/json`
 * e interpreta la risposta come JSON.
 */
/** Like {@link downloadBlob}, with a JSON body sent by POST: for files that
 *  need a secret in the request (a password), which must not go in a URL. */
export async function postBlob(path: string, body: unknown): Promise<Blob> {
  const token = getToken()
  const headers = new Headers({ 'Content-Type': 'application/json' })
  if (token) headers.set('Authorization', `Bearer ${token}`)
  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body) })
  } catch {
    throw new ApiError(0, 'network_error', t('Control Plane non raggiungibile'))
  }
  if (!resp.ok) {
    throw parseError(resp.status, await resp.json().catch(() => null))
  }
  return resp.blob()
}

// PUT di byte grezzi (un blocco di un caricamento firmware). Il corpo è il
// pezzo di file; la risposta è JSON con lo stato del caricamento.
export async function putBytes<T>(path: string, body: Blob, method: 'PUT' | 'POST' = 'PUT'): Promise<T> {
  const token = getToken()
  const headers = new Headers({ 'Content-Type': 'application/octet-stream' })
  if (token) headers.set('Authorization', `Bearer ${token}`)
  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, { method, headers, body })
  } catch {
    throw new ApiError(0, 'network_error', t('Control Plane non raggiungibile'))
  }
  if (!resp.ok) {
    throw parseError(resp.status, await resp.json().catch(() => null))
  }
  return resp.json() as Promise<T>
}

export async function downloadBlob(path: string): Promise<Blob> {
  const token = getToken()
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, { headers })
  } catch {
    throw new ApiError(0, 'network_error', t('Control Plane non raggiungibile'))
  }
  if (!resp.ok) {
    throw parseError(resp.status, await resp.json().catch(() => null))
  }
  return resp.blob()
}

const qs = (params: Record<string, string | number | boolean | undefined>): string => {
  // I booleani `false` si scartano insieme ai vuoti: un filtro spento non è un
  // filtro `=false`, e inviarlo farebbe chiedere all'API «solo quelli non
  // filtrati», che è un'altra domanda.
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '' && v !== false,
  )
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : ''
}

/** The query string of the CENSO ACCESS event filters. */
function parametriAccess(f: AccessFiltri, before?: number | null, limit?: number): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '') continue
    q.set(k, String(v))
  }
  if (before) q.set('before', String(before))
  if (limit) q.set('limit', String(limit))
  return q.toString()
}

export const api = {
  // --- Autenticazione ---
  login: (email: string, password: string) =>
    request<TokenResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  selectTenant: (tenantChoiceToken: string, tenantId: string) =>
    request<TokenResponse>('/auth/select-tenant', {
      method: 'POST',
      body: JSON.stringify({ tenant_choice_token: tenantChoiceToken, tenant_id: tenantId }),
    }),
  switchTenant: (tenantId: string) =>
    request<TokenResponse>('/auth/switch-tenant', {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId }),
    }),
  me: () => request<Principal>('/auth/me'),
  /** Rinnova il token mentre la console è in uso (sessione per inattività). */
  refreshSession: () => request<TokenResponse>('/auth/refresh', { method: 'POST' }),
  /** Durata della sessione del tenant (minuti di inattività) e le scelte ammesse. */
  sessionSettings: () => request<{ idle_minutes: number; choices: number[] }>('/auth/session-settings'),
  setSessionSettings: (idleMinutes: number) =>
    request<{ idle_minutes: number; choices: number[] }>('/auth/session-settings', {
      method: 'PUT',
      body: JSON.stringify({ idle_minutes: idleMinutes }),
    }),

  // --- Dashboard ---
  summary: () => request<Summary>('/dashboard/summary'),
  /** Le anagrafiche di riferimento: centri di costo, unità organizzative, sedi. */
  masterData: (kind: MasterDataKind, active = false) =>
    request<{ kind: MasterDataKind; items: MasterDataItem[] }>(`/master-data?kind=${kind}${active ? '&active=true' : ''}`),
  masterDataTutte: () => request<MasterDataTutte>('/master-data/all'),
  masterDataCrea: (kind: MasterDataKind, body: unknown) =>
    request<MasterDataItem>(`/master-data?kind=${kind}`, { method: 'POST', body: JSON.stringify(body) }),
  masterDataAggiorna: (kind: MasterDataKind, id: string, body: unknown) =>
    request<MasterDataItem>(`/master-data/${id}?kind=${kind}`, { method: 'PUT', body: JSON.stringify(body) }),
  masterDataElimina: (id: string) => request<{ deleted: boolean }>(`/master-data/${id}`, { method: 'DELETE' }),
  masterDataImporta: (kind: MasterDataKind, text: string) =>
    request<{ created: number; updated: number; errors: string[] }>(`/master-data/import?kind=${kind}`, { method: 'POST', body: JSON.stringify({ text }) }),
  /** Le dashboard personalizzabili: le mie e quelle del tenant. */
  // The dashboards of Eagle (cockpit, wall display) live under /monitoring:
  // they belong to the Eagle module, so a role that sees Eagle but not the
  // home dashboard reads and saves them all the same.
  dashboards: (kind: DashboardKind = 'home') => request<{ dashboards: ConsoleDashboard[]; admin: boolean }>(`${percorsoDashboard(kind)}?kind=${kind}`),
  dashboardCrea: (body: { name: string; kind: DashboardKind; shared: boolean; sections: DashboardSezione[]; images?: string; rotation_s: number; screen?: string | null; locked?: boolean }) =>
    request<ConsoleDashboard>(percorsoDashboard(body.kind), { method: 'POST', body: JSON.stringify(body) }),
  dashboardAggiorna: (id: string, body: { name: string; kind: DashboardKind; shared: boolean; sections: DashboardSezione[]; images?: string; rotation_s: number; screen?: string | null; locked?: boolean }) =>
    request<ConsoleDashboard>(`${percorsoDashboard(body.kind)}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  dashboardElimina: (id: string, kind: DashboardKind = 'home') => request<{ dashboards: ConsoleDashboard[] }>(`${percorsoDashboard(kind)}/${id}?kind=${kind}`, { method: 'DELETE' }),
  /** Utilizzo del proprio tenant: spazio stimato, conteggi esatti, quote del piano. */
  usage: () => request<MyUsage>('/usage'),
  fleetVersions: () => request<FleetVersions>('/fleet/versions'),
  /** Aggiorna il **binario** della sonda. `updateProbe` (sotto) ne cambia la
   *  configurazione: nomi vicini, operazioni opposte. */
  upgradeProbe: (id: string, force = false) =>
    request<unknown>(`/fleet/probes/${id}/update?force=${force}`, { method: 'POST' }),
  rolloutFleet: (concurrency = 2) =>
    request<RolloutResult>(`/fleet/update?concurrency=${concurrency}`, { method: 'POST' }),
  credentialGroups: () => request<CredentialGroup[]>('/credentials/groups'),
  createCredentialGroup: (name: string, credentialIds: string[], description?: string) =>
    request<CredentialGroup>('/credentials/groups', {
      method: 'POST',
      body: JSON.stringify({ name, credential_ids: credentialIds, description }),
    }),
  updateCredentialGroup: (
    id: string,
    name: string,
    credentialIds: string[],
    description?: string,
  ) =>
    request<CredentialGroup>(`/credentials/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, credential_ids: credentialIds, description }),
    }),
  deleteCredentialGroup: (id: string) =>
    request<void>(`/credentials/groups/${id}`, { method: 'DELETE' }),
  correlationGraph: (
    address: string,
    opts: { window?: string; levels?: number; fanout?: number; direction?: string } = {},
  ) => {
    const q = new URLSearchParams({ address })
    if (opts.window) q.set('window', opts.window)
    if (opts.levels != null) q.set('levels', String(opts.levels))
    if (opts.fanout != null) q.set('fanout', String(opts.fanout))
    if (opts.direction) q.set('direction', opts.direction)
    return request<CorrelationGraph>(`/flows/graph?${q}`)
  },
  flowFilter: () => request<FlowFilterState>('/flows/filter'),
  setFlowFilter: (excluded: string[], internal: string[], mode: string) =>
    request<FlowFilterState>('/flows/filter', {
      method: 'PUT',
      body: JSON.stringify({ excluded, internal, mode }),
    }),

  // --- Fleet ---
  probes: () => request<Probe[]>('/fleet/probes'),
  probe: (id: string) => request<ProbeDetail>(`/fleet/probes/${id}`),
  /** The probe's log on request: ask, then read (with the content when asked). */
  probeLogRichiedi: (id: string) => request<DeviceLog>(`/fleet/probes/${id}/logs`, { method: 'POST' }),
  probeLog: (id: string, content = false) => request<DeviceLog>(`/fleet/probes/${id}/logs${content ? '?content=true' : ''}`),
  /** Gli ultimi dispositivi avvistati dall'ascolto passivo di una sonda. */
  probeSightings: (id: string) => request<ProbeSighting[]>(`/fleet/probes/${id}/sightings`),
  /** CensoOS on the appliances: version on the mirror, window, brake, counts. */
  censoos: () => request<CensoOSRiepilogo>('/fleet/censoos'),
  censoosSettings: (body: CensoOSImpostazioni) =>
    request<CensoOSRiepilogo>('/fleet/censoos/settings', { method: 'PUT', body: JSON.stringify(body) }),
  /** «Update CensoOS» or «Restart», now or in the window (at most 500 probes). */
  censoosRequest: (body: { probe_ids: string[]; action: 'update' | 'reboot'; when: 'now' | 'window' }) =>
    request<CensoOSEsitoRichiesta>('/fleet/censoos/requests', { method: 'POST', body: JSON.stringify(body) }),
  /** Cancel a request the appliance has not taken yet. */
  censoosCancelRequest: (probeId: string) =>
    request<void>(`/fleet/probes/${probeId}/censoos`, { method: 'DELETE' }),
  censoosAuto: (body: { probe_ids: string[]; enabled: boolean }) =>
    request<{ updated: number }>('/fleet/censoos/auto', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * `capabilities` va omesso per lasciare la politica invariata, messo a `null`
   * per togliere ogni restrizione, e valorizzato (anche vuoto) per imporla:
   * sono tre intenzioni diverse, e il backend le distingue.
   */
  updateProbe: (
    id: string,
    payload: {
      name?: string
      group_id?: string | null
      role?: string
      labels?: Record<string, string>
      capabilities?: string[] | null
      enabled?: boolean
      auto_update?: boolean
      proxy?: {
        url?: string
        username?: string
        password?: string
        no_proxy?: string[]
        control_plane?: 'install' | 'direct' | 'proxy'
      }
    },
  ) => request<Probe>(`/fleet/probes/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteProbe: (id: string) => request<void>(`/fleet/probes/${id}`, { method: 'DELETE' }),
  groups: () => request<ProbeGroup[]>('/fleet/groups'),
  createGroup: (payload: { name: string; mode: string; assignment_policy: string }) =>
    request<ProbeGroup>('/fleet/groups', { method: 'POST', body: JSON.stringify(payload) }),
  updateGroup: (
    id: string,
    payload: { name?: string; mode?: string; assignment_policy?: string },
  ) =>
    request<ProbeGroup>(`/fleet/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteGroup: (id: string) => request<void>(`/fleet/groups/${id}`, { method: 'DELETE' }),
  capabilities: () => request<Capability[]>('/fleet/capabilities'),
  entryPoints: (params: {
    window?: string
    limit?: number
    ci_id?: string
    include_ephemeral?: boolean
  }) => request<EntryPoint[]>(`/flows/entry-points${qs(params)}`),

  createAgentInstaller: (payload: {
    group_id?: string | null
    capabilities?: string[] | null
    name?: string | null
  }) =>
    request<AgentInstaller>('/fleet/agent-installers', {
      method: 'POST',
      body: JSON.stringify({
        group_id: payload.group_id ?? null,
        capabilities: payload.capabilities ?? null,
        name: payload.name ?? null,
      }),
    }),

  createEnrollmentToken: (payload: {
    group_id?: string | null
    capabilities?: string[] | null
    name?: string | null
  }) =>
    request<EnrollmentToken>('/fleet/enrollment-tokens', {
      method: 'POST',
      body: JSON.stringify({
        group_id: payload.group_id ?? null,
        capabilities: payload.capabilities ?? null,
        name: payload.name ?? null,
      }),
    }),

  // --- Configuration Item ---
  cis: (
    params: {
      ci_type?: string
      ci_class?: string
      source?: string
      category?: string
      vendor?: string
      attribute?: string
      value?: string
      /** Stato nel tempo: `active`, `stale`, `missing`, `retired`. */
      state?: string
      /** Ruolo derivato: `hypervisor`, `out_of_band_managed`, `cluster_member`. */
      role?: string
      q?: string
      /** Condizioni per colonna, JSON `[{col, op, value}]`: filtro lato server su tutte le pagine. */
      filters?: string
      /** Solo i CI con questi tag («chiave:valore», più tag separati da virgola). */
      tag?: string
      /** With more tags: `all` (default) = all of them, `any` = at least one. */
      tag_mode?: 'all' | 'any' | string
      /** Only CIs not seen for at least N days, retired ones excluded. */
      unseen_days?: string
      /** «true»: only CIs not yet registered as assets (unmanaged hosts excluded). */
      unregistered?: string
      /** Only CIs inheriting this criticality from business services. */
      service_criticality?: string
      /** Ordinamento lato server: name, ci_class, vendor, source, primary_key, mgmt_ip, last_seen, attr:<attributo>. */
      sort?: string
      dir?: 'asc' | 'desc'
      limit?: number
      offset?: number
    } = {},
  ) =>
    request<ConfigurationItem[]>(`/cis${qs(params)}`),
  /** Il totale dei CI che rispettano i filtri (segue tutti i filtri, non la sola categoria). */
  /** L'URL dell'esportazione dell'elenco CI con i filtri in vigore (Excel o PDF): si scarica con `downloadBlob`. */
  cisExportPath: (params: Record<string, string | number | undefined>, format: 'xlsx' | 'pdf', columns: string[]) =>
    `/cis/export${qs({ ...params, format, columns: columns.join(',') })}`,
  cisCount: (params: Record<string, string | number | undefined> = {}) =>
    request<{ total: number }>(`/cis/count${qs(params)}`),
  /** Gli id di tutti i CI del filtro (fino a mille): per «seleziona tutti». */
  cisIds: (params: Record<string, string | number | undefined> = {}) =>
    request<{ ids: string[]; total: number; truncated: boolean }>(`/cis/ids${qs(params)}`),
  ci: (id: string) => request<CIDetail>(`/cis/${id}`),
  deleteCI: (id: string) => request<void>(`/cis/${id}`, { method: 'DELETE' }),
  /** La stessa azione su più CI (dismetti, annulla, classe, togli dalla CMDB). */
  bulkCis: (payload: {
    ids: string[]
    action: 'retire' | 'unretire' | 'classify' | 'delete'
    note?: string
    ci_class?: string | null
  }) =>
    aLotti(payload.ids, (ids) =>
      request<{ action: string; matched: number; changed: number; not_found: number }>('/cis/bulk', {
        method: 'POST',
        body: JSON.stringify({ ...payload, ids }),
      }),
    ).then((esiti) => somma(esiti, ['matched', 'changed', 'not_found'])),
  ciTypes: () => request<CITypeCount[]>('/cis/types'),
  createCi: (payload: {
    name: string
    ci_class: string
    serial?: string
    mgmt_ip?: string
    hostname?: string
    mac?: string
  }) => request<CIDetail>('/cis', { method: 'POST', body: JSON.stringify(payload) }),
  taxonomy: () => request<Taxonomy>('/cis/taxonomy'),
  ciSources: () => request<CISource[]>('/cis/sources'),
  ciOverview: () => request<CIOverview>('/cis/overview'),
  observedSchema: (ciClass?: string) =>
    request<ObservedAttribute[]>(`/cis/schema${qs({ ci_class: ciClass })}`),
  /** Anteprima dei doppioni del tenant (solo amministratori): non modifica niente. */
  ciDuplicates: () =>
    request<{ groups: DoppioneCi[]; absorbed: number; ha_pairs?: CoppiaHa[] }>('/cis/duplicates'),
  /** Registra subito le coppie in alta affidabilità trovate. */
  registerHaPairs: () => request<{ registered: number }>('/cis/ha-pairs', { method: 'POST' }),
  /** Unisce i gruppi scelti dall'anteprima. */
  mergeCiDuplicates: (keys: string[]) =>
    request<{ merged_groups: number; absorbed: number }>('/cis/duplicates/merge', {
      method: 'POST',
      body: JSON.stringify({ keys }),
    }),
  reclassify: () =>
    request<{ reclassified: number; unchanged: number; enriched: number }>(
      '/cis/reclassify',
      { method: 'POST' },
    ),
  ciRelations: (id: string) => request<CIRelation[]>(`/cis/${id}/relations`),
  /** Gli ambienti virtuali raccolti: ambiente → cluster → host, coi conteggi. */
  virtualMap: () => request<VirtualEnvironment[]>('/cis/virtual-map'),
  /** Il capacity plan: storage, compute e porte, dai dati in inventario. */
  capacity: () => request<CapacityReport>('/capacity'),
  /** Le notifiche: la coda di ciò che chiede attenzione. */
  notifiche: () => request<Notifiche>('/notifications'),
  notificheLette: () => request<{ stato: string }>('/notifications/read', { method: 'POST' }),
  /** L'honeypot: agent esca e hit ricevuti. */
  honeypot: () => request<HoneypotReport>('/honeypot'),
  creaHoneypotAgent: (payload: {
    name: string
    description?: string | null
    decoys?: HoneypotDecoy[]
    endpoint?: string | null
    interval_minutes?: number
    auto_update?: boolean
    ignored_sources?: string[]
    ttl_days?: number | null
  }) =>
    request<HoneypotAgent>('/honeypot/agents', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  modificaHoneypotAgent: (id: string, payload: {
    name?: string
    description?: string | null
    decoys?: HoneypotDecoy[]
    endpoint?: string | null
    interval_minutes?: number
    auto_update?: boolean
    ignored_sources?: string[]
  }) =>
    request<HoneypotAgent>(`/honeypot/agents/${id}`, {
      method: 'PATCH', body: JSON.stringify(payload),
    }),
  rigeneraHoneypotAgent: (id: string) =>
    request<HoneypotAgent>(`/honeypot/agents/${id}/regenerate`, { method: 'POST' }),
  revocaHoneypotAgent: (id: string) =>
    request<HoneypotAgent>(`/honeypot/agents/${id}/revoke`, { method: 'POST' }),
  eliminaHoneypotAgent: (id: string) =>
    request<void>(`/honeypot/agents/${id}`, { method: 'DELETE' }),
  setHoneypotHitState: (id: string, stato: string) =>
    request<{ id: string; stato: string }>(`/honeypot/hits/${id}/stato`, {
      method: 'POST', body: JSON.stringify({ stato }),
    }),
  honeypotStats: (days = 30) => request<HoneypotStats>(`/honeypot/stats?days=${days}`),
  honeypotNotifications: () => request<HoneypotNotifPrefs>('/honeypot/notifications'),
  setHoneypotNotifications: (body: { email: boolean; only_with_credentials: boolean }) =>
    request<HoneypotNotifPrefs>('/honeypot/notifications', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  /** Statistiche delle scansioni: nuovi e scomparsi nel tempo. */
  scanStats: (days = 30) =>
    request<ScanStats>(`/dashboard/scan-stats?days=${days}`),
  /** Ricerca globale: trova ovunque (CI, IP/MAC/porte, software, componenti). */
  ricercaGlobale: (q: string) =>
    request<SearchResult>(`/search?q=${encodeURIComponent(q)}`),
  /** La presenza di una persona: Wi-Fi, PC, VPN (quest'ultima in tempo reale). */
  presenza: (login: string) =>
    request<UserPresence>(`/presence?login=${encodeURIComponent(login)}`),
  /** L'audit del tenant: accessi e operazioni. */
  audit: (params: { category?: string; q?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams()
    if (params.category) qs.set('category', params.category)
    if (params.q) qs.set('q', params.q)
    qs.set('limit', String(params.limit ?? 25))
    qs.set('offset', String(params.offset ?? 0))
    return request<AuditPage>(`/audit?${qs.toString()}`)
  },
  /** La posta in uscita del tenant: canale e notifiche. */
  tenantMailer: () => request<TenantMailer>('/mailer'),
  setTenantMailer: (payload: Partial<TenantMailer> & { password?: string }) =>
    request<TenantMailer>('/mailer', { method: 'PUT', body: JSON.stringify(payload) }),
  testTenantMailer: (to: string) =>
    request<{ stato: string }>('/mailer/test', {
      method: 'POST', body: JSON.stringify({ to }),
    }),
  /** Il Dobermann: subnet sorvegliate e avvistamenti. */
  dobermann: () => request<DobermannReport>('/dobermann'),
  dobermannAddWatch: (cidr: string, label: string) =>
    request<DobermannWatch>('/dobermann/watches', {
      method: 'POST', body: JSON.stringify({ cidr, label }),
    }),
  dobermannRemoveWatch: (id: string) =>
    request<void>(`/dobermann/watches/${id}`, { method: 'DELETE' }),
  dobermannSetState: (id: string, stato: string, nota: string) =>
    request<{ id: string; stato: string }>(`/dobermann/sightings/${id}/stato`, {
      method: 'POST', body: JSON.stringify({ stato, nota }),
    }),
  /** «Questo indirizzo è di quel CI»: lo registra come indirizzo aggiuntivo e censisce l'avvistamento. */
  dobermannAssocia: (sightingId: string, ciId: string) =>
    request<{ id: string; stato: string; ci_id: string | null }>(
      `/dobermann/sightings/${sightingId}/associa`,
      { method: 'POST', body: JSON.stringify({ ci_id: ciId }) },
    ),
  /** Azzera tutti gli avvistamenti del Dobermann (solo amministratori del tenant). */
  dobermannResetSightings: () =>
    request<{ removed: number }>('/dobermann/sightings', { method: 'DELETE' }),
  dobermannScan: () => request<{ nuovi: number }>('/dobermann/scan', { method: 'POST' }),
  dobermannStats: (days = 30) => request<DobermannStats>(`/dobermann/stats?days=${days}`),
  dobermannNotifications: () => request<DobermannNotifPrefs>('/dobermann/notifications'),
  setDobermannNotifications: (body: { email: boolean; min_flows: number }) =>
    request<DobermannNotifPrefs>('/dobermann/notifications', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  dobermannConfigura: (enabled: boolean, intervalMinutes: number, regole?: {
    direction?: 'source' | 'both'; exclude_protocols?: number[]; exclude_ports?: number[]
  }) =>
    request<DobermannSettings>('/dobermann/settings', {
      method: 'PUT',
      body: JSON.stringify({ enabled, interval_minutes: intervalMinutes, ...(regole ?? {}) }),
    }),
  dobermannEditWatch: (id: string, cidr: string, label: string) =>
    request<DobermannWatch>(`/dobermann/watches/${id}`, {
      method: 'PUT', body: JSON.stringify({ cidr, label }),
    }),
  dobermannImport: (testo: string) =>
    request<{ aggiunte: number; aggiornate: number; totale: number }>(
      '/dobermann/watches/import',
      { method: 'POST', body: JSON.stringify({ testo }) },
    ),
  /** La serie storica di storage: totali giornalieri, crescita, proiezione. */
  capacityStorageHistory: (days = 180) =>
    request<CapacityStorageHistory>(`/capacity/storage/history?days=${days}`),
  /** Le VM di un host, coi datastore e le reti che quelle VM usano. */
  virtualMapHost: (hostId: string) =>
    request<VirtualHostDetail>(`/cis/virtual-map/host/${hostId}`),
  ciChanges: (id: string, limit = 100) =>
    request<CIChange[]>(`/cis/${id}/changes${qs({ limit })}`),
  ciActivity: (id: string, limit = 100) =>
    request<CIActivity[]>(`/cis/${id}/activity${qs({ limit })}`),
  /**
   * Assegna a mano la classe di un CI, o (con `null`) la restituisce alle regole.
   *
   * La scelta fatta qui **resiste alle scansioni successive**: senza il segno
   * che la distingue, la classificazione automatica la sovrascriverebbe la
   * notte stessa, che è il modo più sicuro di far smettere di usare la funzione.
   */
  cleanupPublicIps: (voci: string[]) =>
    request<{ tolti: number }>('/cis/public-ips/cleanup', {
      method: 'POST',
      body: JSON.stringify({ voci }),
    }),
  declarePublicIps: (voci: string[], name?: string, notes?: string) =>
    request<{ creati: number; esistenti: number; totale: number }>('/cis/public-ips', {
      method: 'POST',
      body: JSON.stringify({ voci, name: name ?? null, notes: notes ?? null }),
    }),
  publicIps: (id: string) => request<PublicIp[]>(`/cis/${id}/public-ips`),
  ciProvenance: (id: string) => request<CIProvenance>(`/cis/${id}/provenance`),
  ciAddresses: (id: string) => request<ExtraAddress[]>(`/cis/${id}/addresses`),
  /** Di chi è questo indirizzo aggiuntivo e cosa se ne sa (vuoto se il CI non è un indirizzo). */
  ciAddressContext: (id: string) => request<AddressContext>(`/cis/${id}/address-context`),
  /** Il cluster per intero: tipo, nodi con stato, ruoli col nodo proprietario, VIP. */
  ciCluster: (id: string) => request<DettaglioCluster>(`/cis/${id}/cluster`),
  addCiAddress: (id: string, body: { address: string; fqdn: string; function: string; note: string; vrf?: string }) =>
    request<ExtraAddress>(`/cis/${id}/addresses`, { method: 'POST', body: JSON.stringify(body) }),
  /** FQDN, function and note of an additional address (the IP does not change). */
  updateCiAddress: (id: string, addressId: string, body: { fqdn: string; function: string; note: string; vrf?: string }) =>
    request<ExtraAddress>(`/cis/${id}/addresses/${addressId}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeCiAddress: (id: string, addressId: string) =>
    request<void>(`/cis/${id}/addresses/${addressId}`, { method: 'DELETE' }),
  ciFqdns: (id: string) => request<ExtraFqdn[]>(`/cis/${id}/fqdns`),
  addCiFqdn: (id: string, body: { fqdn: string; function: string; note: string }) =>
    request<ExtraFqdn>(`/cis/${id}/fqdns`, { method: 'POST', body: JSON.stringify(body) }),
  updateCiFqdn: (id: string, fqdn: string, body: { fqdn: string; function: string; note: string }) =>
    request<ExtraFqdn>(`/cis/${id}/fqdns/${encodeURIComponent(fqdn)}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeCiFqdn: (id: string, fqdn: string) =>
    request<void>(`/cis/${id}/fqdns/${encodeURIComponent(fqdn)}`, { method: 'DELETE' }),
  setPublicIps: (id: string, ips: string[]) =>
    request<PublicIp[]>(`/cis/${id}/public-ips`, {
      method: 'PATCH',
      body: JSON.stringify({ ips }),
    }),
  checkPublicIps: (id: string) =>
    request<{ esiti: Record<string, unknown>; segnalati: number }>(
      `/cis/${id}/public-ips/check`,
      { method: 'POST' },
    ),
  setClassification: (id: string, ciClass: string | null) =>
    request<CIDetail>(`/cis/${id}/classification`, {
      method: 'PATCH',
      body: JSON.stringify({ ci_class: ciClass }),
    }),
  /** Il rating ntopng del CI in tempo reale (via sonda): score, allarmi, con chi parla. */
  ciNetworkSecurity: (id: string) =>
    request<NetworkSecurityLive>(`/cis/${id}/network-security`),
  /** Lo stato Wazuh del CI in tempo reale (via sonda): agente + conformità SCA. */
  ciWazuh: (id: string) => request<WazuhLive>(`/cis/${id}/wazuh`),
  /** I server DNS configurati (PowerDNS, Microsoft DNS). */
  // --- Scrittura verso DNS e IPAM (se accesa nelle impostazioni del modulo) ---
  dnsAddRecord: (zoneId: string, record: DnsRecordIn, server?: string | null) =>
    request<DnsScritturaEsito>(`/dns/zones/${encodeURIComponent(zoneId)}/records${qs({ server: server ?? undefined })}`, {
      method: 'POST',
      body: JSON.stringify(record),
    }),
  dnsUpdateRecord: (zoneId: string, record: DnsRecordIn & { old_content: string; remove_old_ptr?: boolean }, server?: string | null) =>
    request<DnsScritturaEsito>(`/dns/zones/${encodeURIComponent(zoneId)}/records${qs({ server: server ?? undefined })}`, {
      method: 'PUT',
      body: JSON.stringify(record),
    }),
  dnsDeleteRecord: (zoneId: string, record: { name: string; type: string; content: string; remove_ptr?: boolean }, server?: string | null) =>
    request<DnsScritturaEsito>(
      `/dns/zones/${encodeURIComponent(zoneId)}/records/delete${qs({ server: server ?? undefined })}`,
      { method: 'POST', body: JSON.stringify(record) },
    ),
  /** Che cosa comporta una scrittura (conflitti, PTR), letto dal server: non scrive. */
  dnsPlan: (zoneId: string, body: { action: 'add' | 'update' | 'delete'; name: string; type: string; content: string; old_content?: string }, server?: string | null) =>
    request<DnsPiano>(
      `/dns/zones/${encodeURIComponent(zoneId)}/records/plan${qs({ server: server ?? undefined })}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Un nome o un valore in tutte le zone già lette del server. */
  dnsSearch: (q: string, server?: string | null) =>
    request<DnsRicerca>(`/dns/search${qs({ q, server: server ?? undefined })}`),
  ipamDocument: (subnetId: string, ips: string[], server?: string) =>
    request<IpamDocumentaEsito>(`/ipam/subnets/${encodeURIComponent(subnetId)}/document${qs({ server })}`, {
      method: 'POST',
      body: JSON.stringify({ ips }),
    }),
  ipamDocumentAddress: (ip: string, server?: string) =>
    request<IpamDocumentaEsito>(`/ipam/addresses/document${qs({ server })}`, {
      method: 'POST',
      body: JSON.stringify({ ip }),
    }),
  ipamAlignName: (addressId: string, server?: string) =>
    request<{ ip: string; before: string; after: string }>(
      `/ipam/addresses/${encodeURIComponent(addressId)}/align-name${qs({ server })}`,
      { method: 'POST' },
    ),
  ipamDeleteAddress: (addressId: string, server?: string) =>
    request<{ ip: string }>(`/ipam/addresses/${encodeURIComponent(addressId)}/delete${qs({ server })}`, {
      method: 'POST',
    }),
  dnsServers: () => request<DnsServer[]>('/dns/servers'),
  /** Le zone di un server DNS, lette in tempo reale via sonda. */
  dnsZones: (server?: string | null, refresh = false) =>
    request<DnsZones>(`/dns/zones${qs({ server: server ?? undefined, refresh: refresh ? '1' : undefined })}`),
  /** I record di una zona DNS (dalla copia; `refresh` rilegge in sottofondo). */
  dnsZoneRecords: (zoneId: string, server?: string | null, refresh = false) =>
    request<DnsZoneDetail>(
      `/dns/zones/${encodeURIComponent(zoneId)}/records${qs({ server: server ?? undefined, refresh: refresh ? '1' : undefined })}`,
    ),
  /** Lo stato dei job di backup Veeam, letto in tempo reale via sonda. */
  backupJobs: (refresh = false) => request<VeeamJobs>(`/backup/jobs${refresh ? '?refresh=1' : ''}`),
  /** Gli IPAM configurati (phpIPAM). */
  ipamServers: () => request<{ id: string; name: string; write_enabled?: boolean }[]>('/ipam/servers'),
  /** Le subnet documentate, con il riepilogo del confronto con la CMDB. */
  ipamSubnets: (server?: string, refresh = false) =>
    request<IpamSubnets>(`/ipam/subnets${qs({ server, refresh: refresh ? '1' : undefined })}`),
  /** The phpIPAM VLANs with their L2 domain and subnets (from the copy; `refresh` reads again). */
  ipamVlans: (server?: string, refresh = false) =>
    request<IpamVlans>(`/ipam/vlans${qs({ server, refresh: refresh ? '1' : undefined })}`),
  /** Una subnet con il confronto indirizzo per indirizzo (dalla copia; `refresh` rilegge in sottofondo). */
  ipamSubnet: (id: string, server?: string, refresh = false) =>
    request<IpamSubnetDetail>(
      `/ipam/subnets/${encodeURIComponent(id)}${qs({ server, refresh: refresh ? '1' : undefined })}`,
    ),
  /** Che cosa dice phpIPAM degli indirizzi di un CI. */
  ciIpam: (id: string) => request<IpamCiRecord>(`/cis/${id}/ipam`),
  /** Le repository di backup Veeam (capacità e spazio). */
  backupRepositories: (refresh = false) => request<VeeamRepositories>(`/backup/repositories${refresh ? '?refresh=1' : ''}`),
  /** Gli oggetti protetti (VM di cui Veeam ha un backup). */
  backupObjects: (refresh = false) => request<VeeamObjects>(`/backup/objects${refresh ? '?refresh=1' : ''}`),
  /** Le ultime esecuzioni di un job di backup (esito, durata, dati; per VM su Veeam). */
  backupJobRuns: (vendor: string, id: string, name?: string | null, refresh = false) =>
    request<BackupRuns>(
      `/backup/jobs/${encodeURIComponent(vendor)}/${encodeURIComponent(id)}/runs` +
        qs({ name: name || undefined, refresh: refresh ? 1 : undefined }),
    ),
  /** Le esecuzioni recenti di tutti i job, per le prestazioni della pagina Backup. */
  backupPerformance: (refresh = false) => request<BackupPerformance>(`/backup/performance${refresh ? '?refresh=1' : ''}`),
  /** Il log di un'esecuzione di backup. */
  backupRunLog: (vendor: string, runId: string, refresh = false) =>
    request<BackupLog>(`/backup/runs/${encodeURIComponent(vendor)}/${encodeURIComponent(runId)}/log${refresh ? '?refresh=1' : ''}`),
  /** Le VM della CMDB divise fra protette e non protette (dashboard). */
  backupVmProtection: (refresh = false) => request<VeeamVmProtection>(`/backup/vm-protection${refresh ? '?refresh=1' : ''}`),
  /** La protezione Veeam di un CI (oggetti di backup abbinati per nome). */
  ciBackup: (id: string) => request<VeeamCiBackup>(`/cis/${id}/backup`),
  /** La tabella di routing L3 dell'apparato (raccolta via SNMP). */
  ciRoutes: (id: string) => request<RouteList>(`/cis/${id}/routes`),
  /** La tabella MAC di uno switch per porta, arricchita (OUI, CI, catalogo CENSO). */
  ciPortMacs: (id: string) => request<PortMacList>(`/cis/${id}/port-macs`),
  /** Segnala un MAC visto su una porta all'amministrazione di CENSO. */
  reportPortMac: (id: string, payload: { mac: string; if_name?: string; note?: string }) =>
    request<MacReport>(`/cis/${id}/port-macs/report`, { method: 'POST', body: JSON.stringify(payload) }),
  /** Le segnalazioni di questo tenant, con lo stato. */
  portMacReports: () => request<MacReport[]>('/cis/port-macs/reports'),
  /** Riconosce con Fingerbank gli indirizzi di una porta (o di tutto lo switch). */
  enrichPortMacs: (id: string, ifName?: string) =>
    request<PortMacEnrichResult>(`/cis/${id}/port-macs/enrich`, { method: 'POST', body: JSON.stringify({ if_name: ifName ?? '' }) }),
  /** La WLAN di un controller wireless: AP, SSID e client (inventario via SSH). */
  ciWlan: (id: string) => request<WlanInventory>(`/cis/${id}/wlan`),
  /** Sincronizza le rotte di un firewall Palo Alto via API (PA non le dà in SNMP). */
  ciFirewallRoutesSync: (id: string, firewallId?: string) =>
    request<FirewallSyncEsito & { count?: number }>(
      `/cis/${id}/firewall-routes${qs({ firewall_id: firewallId })}`,
      { method: 'POST' },
    ),
  /** Legge le ottiche (SFP/QSFP) di un firewall via API e le registra fra i componenti del CI. */
  ciFirewallTransceiversSync: (id: string, firewallId?: string) =>
    request<FirewallSyncEsito & { count?: number; added?: number; removed?: number; parts_created?: number; parts_found?: number }>(
      `/cis/${id}/firewall-transceivers${qs({ firewall_id: firewallId })}`,
      { method: 'POST' },
    ),
  /** Analizzatore di percorso L3: il giro da src a dst dalle tabelle di routing. */
  routeAnalyze: (src: string, dst: string) =>
    request<RoutePath>(
      `/routing/analyze?src=${encodeURIComponent(src)}&dst=${encodeURIComponent(dst)}`,
    ),
  /** Gli ambienti (cluster) per il report d'inventario virtuale. */
  vinventoryEnvironments: () =>
    request<{ environments: { id: string; name: string }[] }>(
      '/reports/vinventory/environments',
    ),
  /** Il report d'inventario dell'ambiente virtuale (tipo RVTools), a video. */
  // --- La chiave dei segreti del tenant (amministratori del tenant) ---
  /** «Chiedi a CENSO»: stato, domanda, scelte del tenant. */
  aiStatus: () => request<AiStatus>('/ai'),
  aiAsk: (question: string, history: { role: 'user' | 'assistant'; text: string }[]) =>
    request<AiAnswer>('/ai/ask', { method: 'POST', body: JSON.stringify({ question, history }) }),
  aiSettings: () => request<AiSettings>('/ai/settings'),
  updateAiSettings: (payload: Partial<AiSettings>) =>
    request<AiSettings>('/ai/settings', { method: 'PUT', body: JSON.stringify(payload) }),
  /** L'ambiente di esempio del tenant: stato, caricamento, «passa all'ambiente vuoto». */
  demoData: () => request<DemoData>('/demo-data'),
  loadDemoData: () => request<DemoData>('/demo-data', { method: 'POST' }),
  clearDemoData: () => request<{ loaded: boolean; removed: Record<string, number> }>('/demo-data', { method: 'DELETE' }),
  encryptionStatus: () => request<StatoCifratura>('/encryption'),
  rotateEncryptionKey: () => request<StatoCifratura>('/encryption/rotate', { method: 'POST' }),
  encryptionKekTest: (body: ChiaveDelClienteIn) => request<{ ok: boolean; ref?: string; modo?: string }>('/encryption/kek/test', { method: 'POST', body: JSON.stringify(body) }),
  encryptionKekAdopt: (body: ChiaveDelClienteIn) => request<StatoCifratura>('/encryption/kek', { method: 'POST', body: JSON.stringify(body) }),
  encryptionKekDrop: () => request<StatoCifratura>('/encryption/kek', { method: 'DELETE' }),
  // --- Tag ---
  tags: () => request<TagCatalogo[]>('/tags'),
  createTag: (tag: { key: string; value?: string; color?: string; description?: string }) =>
    request<TagPiattaforma>('/tags', { method: 'POST', body: JSON.stringify(tag) }),
  updateTag: (id: string, tag: { key: string; value?: string; color?: string; description?: string }) =>
    request<TagPiattaforma>(`/tags/${id}`, { method: 'PUT', body: JSON.stringify(tag) }),
  deleteTag: (id: string) => request<void>(`/tags/${id}`, { method: 'DELETE' }),
  tagsOf: (tipo: OggettoTaggabile, ids?: string[]) =>
    request<Record<string, TagPiattaforma[]>>(`/tags/objects/${tipo}${qs({ ids: ids?.join(',') })}`),
  assignTags: (tipo: OggettoTaggabile, objectIds: string[], tags: { tag_ids?: string[]; tags?: string[] }) =>
    aLotti(objectIds, (ids) =>
      request<{ assigned: number }>('/tags/assign', {
        method: 'POST',
        body: JSON.stringify({ object_type: tipo, object_ids: ids, tag_ids: tags.tag_ids ?? [], tags: tags.tags ?? [] }),
      }),
    ).then((esiti) => somma(esiti, ['assigned'])),
  unassignTags: (tipo: OggettoTaggabile, objectIds: string[], tagIds: string[]) =>
    aLotti(objectIds, (ids) =>
      request<{ removed: number }>('/tags/unassign', {
        method: 'POST',
        body: JSON.stringify({ object_type: tipo, object_ids: ids, tag_ids: tagIds }),
      }),
    ).then((esiti) => somma(esiti, ['removed'])),
  tagPolicy: () => request<TagPolicy>('/tags/policy'),
  setTagPolicy: (p: TagPolicy) =>
    request<TagPolicy>('/tags/policy', { method: 'PUT', body: JSON.stringify(p) }),
  tagCoverage: (objectType = 'ci') =>
    request<TagCoverage[]>(`/tags/coverage${qs({ object_type: objectType })}`),
  tagCoverageMissing: (key: string, objectType = 'ci', limit = 5000) =>
    request<{ key: string; object_type: string; ids: string[] }>(
      `/tags/coverage/${encodeURIComponent(key)}/missing${qs({ object_type: objectType, limit })}`,
    ),
  tagRules: () => request<RegolaTag[]>('/tags/rules'),
  previewTagRule: (r: RegolaTagIn) =>
    request<{ matched: number; examples: { id: string; name: string; tag: string }[] }>('/tags/rules/preview', {
      method: 'POST',
      body: JSON.stringify(r),
    }),
  createTagRule: (r: RegolaTagIn) =>
    request<RegolaTag & { result: { added: number; removed: number; matched: number } }>('/tags/rules', {
      method: 'POST',
      body: JSON.stringify(r),
    }),
  updateTagRule: (id: string, r: RegolaTagIn) =>
    request<RegolaTag & { result: { added: number; removed: number; matched: number } }>(`/tags/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(r),
    }),
  applyTagRule: (id: string) =>
    request<RegolaTag & { result: { added: number; removed: number; matched: number } }>(`/tags/rules/${id}/apply`, {
      method: 'POST',
    }),
  deleteTagRule: (id: string) => request<void>(`/tags/rules/${id}`, { method: 'DELETE' }),
  /** Cost Center: i costi cloud del periodo (default dal primo del mese scorso a oggi). */
  costCenter: (q: { from?: string; to?: string; provider?: string; account?: string; currency?: string } = {}) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v) p.set(k, v)
    const qs = p.toString()
    return request<CostCenterSummary>(`/cost-center${qs ? `?${qs}` : ''}`)
  },
  vinventory: (environment?: string) =>
    request<VInventory>(
      `/reports/vinventory${environment ? `?environment=${encodeURIComponent(environment)}` : ''}`,
    ),
  /** Il percorso (relativo) per scaricare l'inventario virtuale in un formato. */
  vinventoryExportPath: (fmt: 'xlsx' | 'pdf', environment?: string) =>
    `/reports/vinventory?fmt=${fmt}${environment ? `&environment=${encodeURIComponent(environment)}` : ''}`,
  /** CENSO MED: whether the area is active for the tenant (403 = not for this role). */
  medStatus: () => request<{ licensed: boolean; devices?: number; synced_at?: string | null }>('/medical/status'),
  // --- CENSO ACCESS (ADR-0030): the register of the accesses, a segregated area ---
  accessStatus: () => request<{ licensed: boolean; write: boolean }>('/access/status'),
  accessOverview: () => request<AccessPanoramica>('/access/overview'),
  accessPosture: () => request<AccessPostura>('/access/posture'),
  accessEvents: (filtri: AccessFiltri, before?: number | null, limit = 200) =>
    request<{ events: AccessEvento[]; next_before: number | null }>(`/access/events?${parametriAccess(filtri, before, limit)}`),
  /** The CSV of the filtered events: the path for downloadBlob. */
  accessEventsExportPath: (filtri: AccessFiltri) => `/access/events/export?${parametriAccess(filtri)}`,
  accessPeople: (privileged = false, q?: string) =>
    request<{ people: AccessPersona[] }>(`/access/people?privileged=${privileged}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  accessPerson: (id: string) => request<AccessPersonaDettaglio>(`/access/people/${id}`),
  accessPersonUpdate: (id: string, body: Partial<Pick<AccessPersona, 'display_name' | 'kind' | 'on_call' | 'note'>>) =>
    request<AccessPersona>(`/access/people/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  accessPersonMerge: (id: string, into: string) =>
    request<AccessPersona>(`/access/people/${id}/merge`, { method: 'POST', body: JSON.stringify({ into }) }),
  accessPersonSplit: (id: string, key: string) =>
    request<AccessPersona>(`/access/people/${id}/split`, { method: 'POST', body: JSON.stringify({ key }) }),
  accessPrivileges: (tutti = false) =>
    request<{ privileges: AccessPrivilegio[]; administrators: AccessAmministratore[] }>(`/access/privileges${tutti ? '?all=true' : ''}`),
  accessSources: () => request<{ sources: AccessFonte[]; enabled: Record<string, boolean> }>('/access/sources'),
  accessEntraCredentials: () =>
    request<{ credentials: { id: string; name: string; in_use: boolean }[] }>('/access/sources/entra/credentials'),
  accessEntraAdd: (credential_id: string) =>
    request<AccessFonte>('/access/sources/entra', { method: 'POST', body: JSON.stringify({ credential_id }) }),
  accessSourceSet: (id: string, enabled: boolean) =>
    request<AccessFonte>(`/access/sources/${id}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
  accessSettings: () => request<AccessImpostazioni>('/access/settings'),
  accessSettingsSave: (body: Partial<AccessImpostazioni> & { standard_users_basis?: string }) =>
    request<AccessImpostazioni>('/access/settings', { method: 'PUT', body: JSON.stringify(body) }),
  accessLedger: () => request<AccessRegistro>('/access/ledger'),
  accessDetections: (f: { status?: string; identity?: string; rule?: string; severity?: string } = {}) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) if (v) q.set(k, v)
    return request<{ detections: AccessRilevamento[] }>(`/access/detections?${q.toString()}`)
  },
  accessDetection: (id: string) => request<AccessRilevamentoDettaglio>(`/access/detections/${id}`),
  accessDetectionSet: (id: string, status: AccessRilevamento['status'], note?: string) =>
    request<AccessRilevamento>(`/access/detections/${id}`, { method: 'PUT', body: JSON.stringify({ status, note }) }),
  accessSyslog: () => request<{ probes: AccessSondaSyslog[] }>('/access/syslog'),
  accessSyslogSearch: (body: { probe_id: string; from?: string; to?: string; contains?: string; device?: string; limit?: number }) =>
    request<AccessRicercaSyslog>('/access/syslog/search', { method: 'POST', body: JSON.stringify(body) }),
  accessSyslogSearchResult: (id: string) => request<AccessRicercaSyslog>(`/access/syslog/search/${id}`),
  accessSiem: () => request<AccessSiem>('/access/siem'),
  accessSiemSave: (body: Record<string, unknown>) => request<AccessSiem>('/access/siem', { method: 'PUT', body: JSON.stringify(body) }),
  accessSiemTest: () => request<{ ok: boolean; queued?: boolean; error?: string }>('/access/siem/test', { method: 'POST' }),
  accessSeals: () => request<AccessSigilli>('/access/seals'),
  accessSealsExportPath: () => '/access/seals/export',
  accessSealNow: () => request<AccessSigillo>('/access/seals/now', { method: 'POST' }),
  accessSealRotate: () => request<AccessSigilli['state']>('/access/seals/rotate', { method: 'POST' }),
  accessTsaTest: () => request<AccessProvaTSA>('/access/settings/tsa-test', { method: 'POST' }),
  accessVerify: () => request<AccessVerifica>('/access/ledger/verify', { method: 'POST' }),
  medOverview: () => request<MedPanoramica>('/medical/overview'),
  medDevices: (opzioni: { includeOut?: boolean; includeExcluded?: boolean; includeRetired?: boolean; external?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (opzioni.includeOut) q.set('include_out', 'true')
    if (opzioni.includeExcluded) q.set('include_excluded', 'true')
    if (opzioni.includeRetired) q.set('include_retired', 'true')
    if (opzioni.external) q.set('external', 'true')
    const qs = q.toString()
    return request<{ devices: MedDispositivo[]; truncated: boolean }>(`/medical/devices${qs ? `?${qs}` : ''}`)
  },
  medDevice: (id: string) => request<MedScheda>(`/medical/devices/${encodeURIComponent(id)}`),
  medDeviceUpdate: (id: string, fields: MedCampi) =>
    request<{ changed: string[]; device: MedScheda }>(`/medical/devices/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify({ fields }),
    }),
  medDevicesBulk: (ids: string[], fields: MedCampi) =>
    request<{ requested: number; found: number; changed: number }>('/medical/devices/bulk', {
      method: 'POST', body: JSON.stringify({ ids, fields }),
    }),
  medDeviceExclude: (id: string, excluded: boolean, reason?: string) =>
    request<{ device: MedScheda }>(`/medical/devices/${encodeURIComponent(id)}/exclude`, {
      method: 'POST', body: JSON.stringify({ excluded, reason: reason ?? null }),
    }),
  medDevicesExportPath: (fmt: 'xlsx' | 'pdf', o: { includeOut?: boolean; includeExcluded?: boolean; includeRetired?: boolean } = {}) =>
    `/medical/devices/export?format=${fmt}${o.includeOut ? '&include_out=true' : ''}${o.includeExcluded ? '&include_excluded=true' : ''}${o.includeRetired ? '&include_retired=true' : ''}`,
  medMasterData: () => request<MasterDataTutte>('/medical/master-data'),
  medMasterDataCreate: (kind: 'org_unit' | 'location', voce: { name: string; code?: string | null }) =>
    request<MasterDataItem>(`/medical/master-data?kind=${kind}`, { method: 'POST', body: JSON.stringify(voce) }),
  medModels: () => request<{ models: MedModello[] }>('/medical/models'),
  medModel: (key: string) => request<MedModelloScheda>(`/medical/models/card?key=${encodeURIComponent(key)}`),
  medModelsExportPath: (fmt: 'xlsx' | 'pdf') => `/medical/models/export?format=${fmt}`,
  medDeviceAsset: (id: string) => request<MedCespiteContratti>(`/medical/devices/${encodeURIComponent(id)}/asset`),
  medDeviceAssetCreate: (id: string, fields: Record<string, unknown>) =>
    request<MedCespiteContratti>(`/medical/devices/${encodeURIComponent(id)}/asset`, {
      method: 'POST', body: JSON.stringify({ fields }),
    }),
  medContracts: () => request<{ contracts: MedContratto[] }>('/medical/contracts'),
  medContractCreate: (body: Record<string, unknown>) =>
    request<{ contract_id: string; contracts: MedContratto[] }>('/medical/contracts', { method: 'POST', body: JSON.stringify(body) }),
  medDeviceContractLink: (id: string, contractId: string) =>
    request<MedCespiteContratti>(`/medical/devices/${encodeURIComponent(id)}/contracts/${encodeURIComponent(contractId)}`, { method: 'POST' }),
  medDeviceContractUnlink: (id: string, contractId: string) =>
    request<MedCespiteContratti>(`/medical/devices/${encodeURIComponent(id)}/contracts/${encodeURIComponent(contractId)}`, { method: 'DELETE' }),
  /** CENSO MED photos and PDFs: the body is the file (octet-stream, WAF rule 1019). */
  medDeviceFileUpload: (id: string, file: Blob, q: { kind: 'photo' | 'document'; name: string; caption?: string; thumbOf?: string }) =>
    putBytes<{ id: string }>(`/medical/devices/${encodeURIComponent(id)}/files${qs({ kind: q.kind, name: q.name, caption: q.caption || undefined, thumb_of: q.thumbOf })}`, file, 'POST'),
  medModelFileUpload: (key: string, file: Blob, q: { name: string; caption?: string; thumbOf?: string }) =>
    putBytes<{ id: string }>(`/medical/models/files${qs({ key, name: q.name, caption: q.caption || undefined, thumb_of: q.thumbOf })}`, file, 'POST'),
  medDeviceFiles: (id: string) => request<MedFileDispositivo>(`/medical/devices/${encodeURIComponent(id)}/files`),
  medFilePath: (fileId: string, size: 'full' | 'thumb' = 'full') => `/medical/files/${encodeURIComponent(fileId)}?size=${size}`,
  medFileCaption: (fileId: string, caption: string) =>
    request<{ id: string }>(`/medical/files/${encodeURIComponent(fileId)}`, { method: 'PUT', body: JSON.stringify({ caption }) }),
  medFileDelete: (fileId: string) => request<{ deleted: string }>(`/medical/files/${encodeURIComponent(fileId)}`, { method: 'DELETE' }),
  medChecks: () => request<MedScadenze>('/medical/checks'),
  medDeviceChecks: (id: string) => request<MedVerificheDispositivo>(`/medical/devices/${encodeURIComponent(id)}/checks`),
  medPlanSave: (id: string, body: Record<string, unknown>) =>
    request<MedVerificheDispositivo>(`/medical/devices/${encodeURIComponent(id)}/plans`, { method: 'POST', body: JSON.stringify(body) }),
  medPlanDefaults: (id: string) =>
    request<MedVerificheDispositivo & { plans_created: number; record_fields: string[] }>(`/medical/devices/${encodeURIComponent(id)}/plans/defaults`, { method: 'POST' }),
  medPlanDelete: (planId: string) => request<{ deleted: string }>(`/medical/plans/${encodeURIComponent(planId)}`, { method: 'DELETE' }),
  medCheckRecord: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; propose_out_of_service: boolean }>(`/medical/devices/${encodeURIComponent(id)}/checks`, { method: 'POST', body: JSON.stringify(body) }),
  medCheckDelete: (checkId: string) => request<{ deleted: string }>(`/medical/checks/${encodeURIComponent(checkId)}`, { method: 'DELETE' }),
  medCheckFileUpload: (checkId: string, file: Blob, name: string) =>
    putBytes<{ id: string }>(`/medical/checks/${encodeURIComponent(checkId)}/files${qs({ name })}`, file, 'POST'),
  medModelProfile: (key: string) => request<{ profile: MedProfiloModello | null }>(`/medical/models/profile?key=${encodeURIComponent(key)}`),
  medModelProfileSave: (key: string, body: Record<string, unknown>) =>
    request<{ profile: MedProfiloModello }>(`/medical/models/profile?key=${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(body) }),
  medNotify: () => request<{ recipients: string[]; last_run: string | null; event_enabled: boolean }>('/medical/notify'),
  medNotifySave: (recipients: string[]) =>
    request<{ recipients: string[] }>('/medical/notify', { method: 'PUT', body: JSON.stringify({ recipients }) }),
  medRecalls: () => request<MedRichiami>('/medical/recalls'),
  medRecallSave: (id: string, status: MedStatoRichiamo, note?: string) =>
    request<MedRichiamo>(`/medical/recalls/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ status, note }) }),
  medSources: () => request<MedFonti>('/medical/sources'),
  medSourcesSave: (country: string, sources: Record<string, boolean>) =>
    request<MedFonti>('/medical/sources', { method: 'PUT', body: JSON.stringify({ country, sources }) }),
  medInsight: (id: string) => request<MedContesto>(`/medical/devices/${encodeURIComponent(id)}/insight`),
  medWhere: () => request<{ devices: MedDoveRiga[]; computed_at: string | null }>('/medical/where'),
  medWhereAccept: (items: { device_id: string; field: string; value?: string; entry_id?: string | null; source?: string }[]) =>
    request<{ accepted: number; errors: { device_id: string; error: string }[] }>('/medical/where/accept', {
      method: 'POST', body: JSON.stringify({ items }),
    }),
  medLocationRules: () => request<{ rules: MedRegola[] }>('/medical/location-rules'),
  medLocationRuleCreate: (r: { kind: string; value: string; org_unit_id?: string | null; location_id?: string | null; switch_ci_id?: string | null }) =>
    request<{ rules: MedRegola[] }>('/medical/location-rules', { method: 'POST', body: JSON.stringify(r) }),
  medLocationRuleDelete: (id: string) =>
    request<{ rules: MedRegola[] }>(`/medical/location-rules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  medConnections: () => request<MedCollegamenti>('/medical/connections'),
  medSystems: () => request<MedSistemi>('/medical/systems'),
  medSystem: (key: string) => request<MedSistemaDettaglio>(`/medical/systems/detail?key=${encodeURIComponent(key)}`),
  medAvailability: () => request<MedDisponibilita>('/medical/availability'),
  medWireIdentities: () => request<{ identities: MedIdentitaFilo[]; enabled: boolean }>('/medical/wire-identities'),
  medMap: (focus?: string) =>
    request<MedMappa>(focus ? `/medical/map?focus=${encodeURIComponent(focus)}` : '/medical/map'),
  medUsage: () => request<{ devices: MedUsoRiga[]; threshold_bytes: number; retention_days: number; last_usage_day: string | null; idle_after_days: number }>('/medical/usage'),
  medSettings: (s: { threshold_bytes?: number; retention_days?: number; agent_conversations?: boolean; wire_identity?: boolean }) =>
    request<{ threshold_bytes: number; retention_days: number }>('/medical/settings', { method: 'PUT', body: JSON.stringify(s) }),
  medInsightRefresh: () => request<{ devices: number }>('/medical/insight/refresh', { method: 'POST' }),
  medCmmsProposals: (table: string) =>
    request<MedCmmsProposte>('/medical/import/cmms/proposals', { method: 'POST', body: JSON.stringify({ table }) }),
  medCmmsApply: (items: { device_id: string; fields: Record<string, unknown> }[], createEntries: boolean) =>
    request<{ applied: number; created_entries: string[]; errors: { device_id: string; error: string }[] }>(
      '/medical/import/cmms/apply', { method: 'POST', body: JSON.stringify({ items, create_entries: createEntries }) }),
  /** Eagle (monitoraggio): se è attivo per il tenant. */
  eagleStatus: () => request<{ licensed: boolean; standalone?: boolean }>('/monitoring/status'),
  eagleCis: () => request<{ licensed: boolean; cis: EagleMonitorato[] }>('/monitoring/cis'),
  eagleOverview: (hours = 24) => request<EagleOverview>(`/monitoring/overview?hours=${hours}`),
  eagleTop: (kind: string, limit = 10, hours = 6) =>
    request<EagleTop>(`/monitoring/top?kind=${encodeURIComponent(kind)}&limit=${limit}&hours=${hours}`),
  /** One parameter on every monitored CI, as sparklines (one read): the wall's ping graphs. */
  eagleSparklineParete: (key: 'latency' | 'ping_fast' | 'ping' = 'latency', hours = 1) =>
    request<EagleSparklineParete>(`/monitoring/sparklines?key=${key}&hours=${hours}`),
  /** Più parametri in una chiamata (al massimo 12), per il grafico personalizzato. */
  eagleSerieMultiple: (coppie: { ci_id: string; key: string }[], hours = 24) =>
    request<{ licensed: boolean; hours: number; series: EagleSerieMultipla[] }>(
      `/monitoring/series?pairs=${encodeURIComponent(coppie.map((c) => `${c.ci_id}:${c.key}`).join(','))}&hours=${hours}`),
  eagleAvailability: (hours = 24, limit = 50) => request<EagleAvailability>(`/monitoring/availability?hours=${hours}&limit=${limit}`),
  eagleStoricoAllarmi: (hours = 24, limit = 200) => request<EagleStoricoAllarmi>(`/monitoring/alarm-history?hours=${hours}&limit=${limit}`),
  eagleNotify: () => request<EagleNotifyTutto>('/monitoring/notify'),
  eagleNotifyCrea: (anagrafica: EagleAnagrafica, body: unknown) =>
    request<{ licensed: boolean; item: unknown }>(`/monitoring/notify/${anagrafica}`, { method: 'POST', body: JSON.stringify(body) }),
  eagleNotifyAggiorna: (anagrafica: EagleAnagrafica, id: string, body: unknown) =>
    request<{ licensed: boolean; item: unknown }>(`/monitoring/notify/${anagrafica}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  eagleNotifyElimina: (anagrafica: EagleAnagrafica, id: string) =>
    request<EagleNotifyTutto>(`/monitoring/notify/${anagrafica}/${id}`, { method: 'DELETE' }),
  eagleReports: () => request<{ licensed: boolean; reports: EagleReportSalvato[]; blocks: Record<string, unknown> }>('/monitoring/reports'),
  eagleReportCrea: (body: EagleReportDef) =>
    request<{ licensed: boolean; report: EagleReportSalvato }>('/monitoring/reports', { method: 'POST', body: JSON.stringify(body) }),
  eagleReportAggiorna: (id: string, body: EagleReportDef) =>
    request<{ licensed: boolean; report: EagleReportSalvato }>(`/monitoring/reports/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  eagleReportElimina: (id: string) => request<{ licensed: boolean }>(`/monitoring/reports/${id}`, { method: 'DELETE' }),
  eagleReportAnteprima: (body: EagleReportDef) =>
    request<EagleReportEsito>('/monitoring/reports/preview', { method: 'POST', body: JSON.stringify(body) }),
  eagleReportEsegui: (id: string) => request<EagleReportEsito>(`/monitoring/reports/${id}/run`),
  eagleProfili: () => request<EagleProfiliTutto>('/monitoring/profiles'),
  awxServers: () => request<AwxServer[]>('/awx/servers'),
  awxInventories: (server?: string) =>
    request<{ available: boolean; reason?: string; inventories?: AwxInventario[] }>(`/awx/inventories${server ? `?server=${encodeURIComponent(server)}` : ''}`),
  awxConfronta: (p: { server?: string; inventory?: string; classes?: string; tags?: string; os?: string }) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(p)) if (v) q.set(k, v)
    return request<AwxConfronto>(`/awx/compare${q.toString() ? `?${q}` : ''}`)
  },
  eagleProfiloCrea: (body: EagleProfiloDef) =>
    request<{ licensed: boolean; profile: EagleProfilo }>('/monitoring/profiles', { method: 'POST', body: JSON.stringify(body) }),
  eagleProfiloAggiorna: (id: string, body: EagleProfiloDef) =>
    request<{ licensed: boolean; profile: EagleProfilo }>(`/monitoring/profiles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  eagleProfiloElimina: (id: string) => request<{ licensed: boolean }>(`/monitoring/profiles/${id}`, { method: 'DELETE' }),
  /** Un profilo salvato (sui CI dati, o senza CI su quelli col suo tag) o regole al volo sui CI dati. */
  eagleProfiloApplica: (body: { ids?: string[]; profile_id?: string; rules?: EagleRegolaProfilo[]; replace?: boolean; save_as?: string | null }) =>
    request<EagleApplicaEsito>('/monitoring/profiles/apply', { method: 'POST', body: JSON.stringify(body) }),
  ciEagleAssegna: (ciId: string, body: { notify_group_id?: string | null; sla_id?: string | null }) =>
    request<EagleCI>(`/cis/${ciId}/monitoring/assign`, { method: 'PUT', body: JSON.stringify(body) }),
  ciEagle: (ciId: string) => request<EagleCI>(`/cis/${ciId}/monitoring`),
  /** Monitoraggio in blocco dall'elenco dei CI: col profilo della classe, o spento. */
  bulkEagle: (ids: string[], action: 'enable' | 'disable') =>
    aLotti(ids, (lotto) =>
      request<{ matched: number; changed: number; skipped: { id: string; name: string; reason: string }[]; not_found: number }>(
        '/monitoring/bulk', { method: 'POST', body: JSON.stringify({ ids: lotto, action }) }),
    ).then((esiti) => ({ ...somma(esiti, ['matched', 'changed', 'not_found']), skipped: esiti.flatMap((e) => e.skipped) })),
  // A long manual choice goes in batches: the first turns monitoring on, the
  // others add to it (the Control Plane updates only the rows it receives).
  ciEagleAttiva: async (ciId: string, body: { mode: 'profile' | 'manual'; params?: EagleScelta[]; interval_s?: number | null }) => {
    const tutte = body.params ?? []
    let esito = await request<EagleCI>(`/cis/${ciId}/monitoring`, { method: 'POST',
      body: JSON.stringify({ ...body, params: tutte.slice(0, LOTTO_PARAMETRI) }) })
    for (let i = LOTTO_PARAMETRI; i < tutte.length; i += LOTTO_PARAMETRI) {
      esito = await request<EagleCI>(`/cis/${ciId}/monitoring`, { method: 'PUT',
        body: JSON.stringify({ params: tutte.slice(i, i + LOTTO_PARAMETRI) }) })
    }
    return esito
  },
  ciEagleAggiorna: async (ciId: string, params: EagleScelta[]) => {
    let esito = await request<EagleCI>(`/cis/${ciId}/monitoring`, { method: 'PUT',
      body: JSON.stringify({ params: params.slice(0, LOTTO_PARAMETRI) }) })
    for (let i = LOTTO_PARAMETRI; i < params.length; i += LOTTO_PARAMETRI) {
      esito = await request<EagleCI>(`/cis/${ciId}/monitoring`, { method: 'PUT',
        body: JSON.stringify({ params: params.slice(i, i + LOTTO_PARAMETRI) }) })
    }
    return esito
  },
  ciEagleSerie: (ciId: string, key: string, hours = 24) =>
    request<EagleSerie>(`/cis/${ciId}/monitoring/series?key=${encodeURIComponent(key)}&hours=${hours}`),
  ciEagleDisattiva: (ciId: string) => request<EagleCI>(`/cis/${ciId}/monitoring`, { method: 'DELETE' }),
  ciEagleNuovo: (ciId: string, body: {
    type: 'service_group' | 'process'; name?: string; services?: string[]; pattern?: string; min?: number | null; process?: string
  }) => request<EagleCI>(`/cis/${ciId}/monitoring/custom`, { method: 'POST', body: JSON.stringify(body) }),
  ciEagleTogli: (ciId: string, key: string) =>
    request<EagleCI>(`/cis/${ciId}/monitoring/custom?key=${encodeURIComponent(key)}`, { method: 'DELETE' }),
  ciEaglePunti: (ciId: string, applianceIds: string[]) =>
    request<EagleCI>(`/cis/${ciId}/monitoring/points`, { method: 'PUT', body: JSON.stringify({ appliance_ids: applianceIds }) }),
  eagleSonde: () => request<{ licensed: boolean; probes: EagleSonda[] }>('/monitoring/probes'),
  eagleSondaRuolo: (probeId: string, body: { role: EagleSonda['role']; networks: string[]; reset_max_params?: boolean }) =>
    request<{ licensed: boolean; probes: EagleSonda[] }>(`/monitoring/probes/${probeId}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** Gli ambienti cloud (un elemento per account). */
  cloudMap: () => request<CloudEnvironment[]>('/cis/cloud-map'),
  /** La mappa di un account cloud, eventualmente di una regione sola. */
  cloudMapGraph: (key: string, region?: string) =>
    request<CloudMapGraph>(
      `/cis/cloud-map/graph?key=${encodeURIComponent(key)}${region ? `&region=${encodeURIComponent(region)}` : ''}`,
    ),
  cloudMapResource: (ciId: string) => request<CloudResourceDetail>(`/cis/cloud-map/resource/${ciId}`),
  /** Gli account per l'inventario cloud. */
  cinventoryEnvironments: () =>
    request<{ environments: { id: string; name: string }[] }>('/reports/cinventory/environments'),
  /** L'inventario cloud (tipo RVTools), a video. */
  cinventory: (environment?: string) =>
    request<VInventory>(
      `/reports/cinventory${environment ? `?environment=${encodeURIComponent(environment)}` : ''}`,
    ),
  cinventoryExportPath: (fmt: 'xlsx' | 'pdf', environment?: string) =>
    `/reports/cinventory?fmt=${fmt}${environment ? `&environment=${encodeURIComponent(environment)}` : ''}`,
  /** L'elenco dei firewall configurati, Palo Alto e FortiGate (pagina Sicurezza → Firewall). */
  firewalls: () =>
    request<{ firewalls: { id: string; name: string; vendor?: 'paloalto' | 'fortinet'; appliance_id: string | null }[] }>(
      '/firewall/firewalls',
    ),
  /** Il cruscotto di un firewall (dalla pagina Sicurezza → Firewall). */
  firewallDashboard: (firewallId?: string) =>
    request<FirewallDashboard>(
      `/firewall/dashboard${firewallId ? `?firewall_id=${encodeURIComponent(firewallId)}` : ''}`,
    ),
  /** L'attività di un firewall (minacce, URL, top, accessi falliti). */
  firewallActivity: (
    view: 'threat' | 'url' | 'top_apps' | 'top_denied' | 'top_attacks' | 'gp_fail' | 'system_fail',
    firewallId?: string,
  ) => {
    const q = new URLSearchParams({ view })
    if (firewallId) q.set('firewall_id', firewallId)
    return request<FirewallActivity>(`/firewall/activity?${q.toString()}`)
  },
  /** Log Query tool: traffico per IP sorgente e/o destinazione su un firewall. */
  firewallQuery: (opts: { firewallId?: string; src?: string; dst?: string; blocked?: boolean }) => {
    const q = new URLSearchParams()
    if (opts.firewallId) q.set('firewall_id', opts.firewallId)
    if (opts.src) q.set('src', opts.src)
    if (opts.dst) q.set('dst', opts.dst)
    if (opts.blocked) q.set('blocked', 'true')
    const qs = q.toString()
    return request<FirewallTraffic>(`/firewall/query${qs ? `?${qs}` : ''}`)
  },
  /** Il traffico bloccato dal firewall per questo CI, dai log in Wazuh. */
  ciFirewall: (id: string) => request<WazuhFirewall>(`/cis/${id}/firewall`),
  /** Il traffico letto direttamente dal firewall, Palo Alto o FortiGate (via sonda). Con
   * `blocked` filtra i soli bloccati; con `firewallId` sceglie quale firewall. */
  ciFirewallTraffic: (id: string, opts?: { blocked?: boolean; firewallId?: string }) => {
    const q = new URLSearchParams()
    if (opts?.blocked) q.set('blocked', 'true')
    if (opts?.firewallId) q.set('firewall_id', opts.firewallId)
    const qs = q.toString()
    return request<FirewallTraffic>(`/cis/${id}/firewall-traffic${qs ? `?${qs}` : ''}`)
  },
  /** Il cruscotto del firewall, Palo Alto o FortiGate (sistema, sessioni, VPN, admin). */
  ciFirewallDashboard: (id: string, firewallId?: string) =>
    request<FirewallDashboard>(
      `/cis/${id}/firewall-dashboard${firewallId ? `?firewall_id=${encodeURIComponent(firewallId)}` : ''}`,
    ),
  /** Attività recente del firewall: minacce, URL filtering o top applicazioni. */
  ciFirewallActivity: (
    id: string,
    view: 'threat' | 'url' | 'top_apps' | 'top_denied' | 'top_attacks' | 'gp_fail' | 'system_fail',
    firewallId?: string,
  ) => {
    const q = new URLSearchParams({ view })
    if (firewallId) q.set('firewall_id', firewallId)
    return request<FirewallActivity>(`/cis/${id}/firewall-activity?${q.toString()}`)
  },
  /** La panoramica Cynet EDR: host, allarmi, vulnerabilità, riepilogo copertura. */
  cynetOverview: (refresh = false) => request<CynetOverview>(`/cynet/overview${refresh ? '?refresh=1' : ''}`),
  /** La copertura EDR, asset per asset (protetti, non protetti, solo in Cynet). */
  cynetCoverage: (refresh = false) => request<CynetCoverage>(`/cynet/coverage${refresh ? '?refresh=1' : ''}`),
  /** Sincronizza adesso Cynet: host → CI, allarmi/vulnerabilità → reperti. */
  runCynet: () => request<CynetRunResult>('/cynet/run', { method: 'POST' }),
  /** La panoramica Wazuh del parco (dashboard): agenti, allarmi, vulnerabilità. */
  wazuhOverview: (days?: number) =>
    request<WazuhOverview>(`/wazuh/overview${days ? `?days=${days}` : ''}`),
  /** Gli host colpiti da una CVE (drill-down dalla dashboard), coi pacchetti. */
  wazuhVulnerabilityHosts: (cve: string) =>
    request<WazuhCveHosts>(`/wazuh/vulnerability-hosts?cve=${encodeURIComponent(cve)}`),
  /** Scrive i valori dei campi personalizzati di un CI (insieme completo). */
  setCiCustomFields: (id: string, values: Record<string, unknown>) =>
    request<CIDetail>(`/cis/${id}/custom-fields`, {
      method: 'PATCH',
      body: JSON.stringify({ values }),
    }),
  /**
   * Dichiara dismesso un CI, o (con `null`) lo restituisce al calcolo.
   *
   * «Dismesso» è l'unico stato che nessuna scansione può dedurre: dall'esterno
   * una macchina rottamata e una spenta perché è saltato il gruppo di
   * continuità sono identiche. Finché non era esprimibile, chi voleva dire
   * «questa l'abbiamo dismessa» aveva un solo gesto — cancellare il CI — che
   * butta la storia e, se l'apparato è ancora acceso, si annulla da solo alla
   * scansione dopo.
   */
  setLifecycle: (id: string, state: 'retired' | null, note?: string) =>
    request<CIDetail>(`/cis/${id}/lifecycle`, {
      method: 'PATCH',
      body: JSON.stringify({ state, note: note ?? null }),
    }),
  // ------------------------------------------------ tassonomia e catalogo
  editableTaxonomy: () => request<EditableTaxonomy>('/taxonomy'),
  createCategory: (body: { key: string; label: string; description?: string }) =>
    request<{ id: string }>('/taxonomy/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteCategory: (id: string) =>
    request<void>(`/taxonomy/categories/${id}`, { method: 'DELETE' }),
  createClass: (body: {
    key: string
    label: string
    category: string
    description?: string
    key_attributes?: string[]
  }) =>
    request<{ id: string }>('/taxonomy/classes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteClass: (id: string) =>
    request<void>(`/taxonomy/classes/${id}`, { method: 'DELETE' }),
  baselineState: () => request<BaselineState>('/taxonomy/baseline'),
  /**
   * Distribuisce le voci della baseline che mancano.
   *
   * Non tocca nulla di ciò che c'è: una voce modificata resta modificata, una
   * cancellata resta cancellata. Ripetibile senza conseguenze.
   */
  applyBaseline: () =>
    request<{ applied: number; rules: number; classes: number; categories: number }>(
      '/taxonomy/baseline/apply',
      { method: 'POST' },
    ),
  rules: () => request<RecognitionRule[]>('/taxonomy/rules'),
  createRule: (body: RuleDraft) =>
    request<RecognitionRule>('/taxonomy/rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateRule: (id: string, body: RuleDraft) =>
    request<RecognitionRule>(`/taxonomy/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteRule: (id: string) =>
    request<void>(`/taxonomy/rules/${id}`, { method: 'DELETE' }),
  /**
   * Cosa farebbe una regola, **prima** di salvarla.
   *
   * È il pezzo che rende la funzione usabile: una regola agisce su tutta la
   * CMDB e a ogni scansione, e scoprirne l'effetto l'indomani — a inventario
   * già riscritto — sarebbe una funzione che nessuno usa due volte.
   */
  previewRule: (body: RuleDraft) =>
    request<RulePreview>('/taxonomy/rules/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  skus: () => request<SKU[]>('/taxonomy/skus'),
  createSku: (body: SKUDraft) =>
    request<SKU>('/taxonomy/skus', { method: 'POST', body: JSON.stringify(body) }),
  updateSku: (id: string, body: SKUDraft) =>
    request<SKU>(`/taxonomy/skus/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteSku: (id: string) =>
    request<void>(`/taxonomy/skus/${id}`, { method: 'DELETE' }),
  /**
   * I tipi di oggetto che usano un frammento, letti dall'istanza.
   *
   * Serve a scegliere il tipo da creare senza saperlo a memoria: in Matrix42 un
   * asset è un oggetto di un tipo, e i tipi dipendono dall'installazione.
   */
  objectSubtypes: (connectorId: string, objectType: string) =>
    request<ObjectSubtypes>(
      `/connectors/${connectorId}/subtypes/${encodeURIComponent(objectType)}`,
    ),
  networkDevices: () => request<NetworkDevice[]>('/network/devices'),
  readingCoverage: () => request<ReadingGap[]>('/network/coverage'),
  /** Vendor MIBs of the tenant: product registrations for sysObjectID. */
  mibs: () => request<MibSummary>('/network/mibs'),
  /** One MIB as text, already without comments and quoted texts (`stripMib`). */
  uploadMib: (fileName: string, text: string) =>
    request<MibSummary>('/network/mibs', { method: 'POST', body: JSON.stringify({ file_name: fileName, text }) }),
  deleteMib: (id: string) => request<void>(`/network/mibs/${id}`, { method: 'DELETE' }),
  devicePorts: (id: string) => request<DevicePorts>(`/network/devices/${id}/ports`),
  /**
   * Il grafo, con il perimetro deciso dal server.
   *
   * `focus` e `depth` si passano **al server**: espandere nella console
   * richiederebbe di aver già scaricato tutto il grafo, cioè esattamente la
   * cosa che rendeva la pagina inutilizzabile.
   */
  topology: (params: {
    relTypes?: string[]
    ciClasses?: string[]
    focus?: string | null
    depth?: number
  } = {}) => {
    const search = new URLSearchParams()
    for (const t of params.relTypes ?? []) search.append('rel_type', t)
    for (const c of params.ciClasses ?? []) search.append('ci_class', c)
    if (params.focus) search.set('focus', params.focus)
    if (params.depth) search.set('depth', String(params.depth))
    const query = search.toString()
    return request<TopologyGraph>(`/cis/topology/graph${query ? `?${query}` : ''}`)
  },
  topologySearch: (q: string) =>
    request<TopologySeed[]>(`/cis/topology/search${qs({ q })}`),
  topologyViews: () => request<TopologyView[]>('/cis/topology/views'),

  // --- Piani di discovery di rete ---
  //
  // La validazione è **del server**: l'editor calcola cosa offrire, il Control
  // Plane decide se il piano è eseguibile. Due validatori divergerebbero, con
  // l'editor che dice «va bene» e il salvataggio che rifiuta — o il contrario.
  planSteps: () => request<StepCatalogue>('/discovery-plans/steps'),
  discoveryPlans: () => request<DiscoveryPlan[]>('/discovery-plans'),
  discoveryPlan: (id: string) => request<DiscoveryPlan>(`/discovery-plans/${id}`),
  validatePlan: (body: { name: string; steps: PlanStep[] }) =>
    request<PlanValidation>('/discovery-plans/validate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createPlan: (body: PlanPayload) =>
    request<DiscoveryPlan>('/discovery-plans', { method: 'POST', body: JSON.stringify(body) }),
  updatePlan: (id: string, body: PlanPayload) =>
    request<DiscoveryPlan>(`/discovery-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deletePlan: (id: string) =>
    request<void>(`/discovery-plans/${id}`, { method: 'DELETE' }),
  // Eseguire il piano adesso. Era l'unica cosa che di un piano non si potesse
  // fare: si creava, si modificava, si cancellava — e restava un documento.
  runPlan: (id: string, body: PlanRun = {}) =>
    request<Job>(`/discovery-plans/${id}/run`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  previewPlan: (id: string, body: { facts: Record<string, unknown>; statuses?: Record<string, string> }) =>
    request<PlanPreviewStep[]>(`/discovery-plans/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  saveTopologyView: (body: {
    name: string
    description?: string | null
    config: TopologyViewConfig
    positions: Record<string, { x: number; y: number }>
  }) =>
    request<TopologyView>('/cis/topology/views', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteTopologyView: (id: string) =>
    request<void>(`/cis/topology/views/${id}`, { method: 'DELETE' }),

  // --- Credenziali ---
  credentials: () => request<Credential[]>('/credentials'),
  credentialKinds: () => request<CredentialKind[]>('/credentials/kinds'),
  /** Genera la chiave API di un Palo Alto da utente/password, via la sonda scelta
   * (keygen): la console riempie il campo «Chiave API» senza incollarla a mano. */
  paloaltoKeygen: (payload: {
    endpoint: string
    username: string
    password: string
    appliance_id?: string
    insecure_tls?: boolean
  }) =>
    request<{ api_key: string }>('/credentials/paloalto/keygen', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  createCredential: (payload: {
    name: string
    kind: string
    secret: Record<string, string>
    meta?: Record<string, unknown>
  }) =>
    request<Credential>('/credentials', {
      method: 'POST',
      body: JSON.stringify({ ...payload, meta: payload.meta ?? {} }),
    }),
  /**
   * Modifica una credenziale salvata.
   *
   * `secret` è un aggiornamento **parziale**: i campi non inviati restano quelli
   * salvati. È ciò che permette di correggere un endpoint senza reinserire la
   * password — che è la parte che chi corregge un endpoint non ha sottomano.
   * Un campo si azzera inviandolo a `null`.
   */
  updateCredential: (
    id: string,
    payload: {
      name?: string
      secret?: Record<string, string | null>
      // Metadati non segreti (es. la sonda scelta per una sorgente live in
      // `appliance_id`): il Control Plane li fonde con quelli salvati.
      meta?: Record<string, unknown>
    },
  ) => request<Credential>(`/credentials/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteCredential: (id: string) => request<void>(`/credentials/${id}`, { method: 'DELETE' }),

  // --- Discovery ---
  discoveryKinds: () => request<DiscoveryKind[]>('/discovery/kinds'),
  discoveryOptimization: () => request<DiscoveryOptimization>('/discovery/optimization'),

  // --- Job ---
  jobs: (status?: string, finestra?: { days?: number; limit?: number }) =>
    request<Job[]>(`/jobs${qs({ status, days: finestra?.days, limit: finestra?.limit })}`),
  createJob: (payload: JobCreate) =>
    request<Job>('/jobs', { method: 'POST', body: JSON.stringify(payload) }),
  /** Titolo e descrizione di un job, anche dopo l'esecuzione. */
  updateJob: (id: string, payload: { title?: string | null; description?: string | null }) =>
    request<Job>(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  /** Rilancia un job: ne crea uno nuovo con la stessa configurazione. */
  retryJob: (id: string) => request<Job>(`/jobs/${id}/retry`, { method: 'POST' }),
  jobReport: (id: string, filters?: { level?: string; stage?: string }) =>
    request<JobReport>(`/jobs/${id}/report${qs({ ...filters })}`),
  /** Lo stato di un singolo job, per seguire una cattura di prova in corso. */
  job: (id: string) => request<Job>(`/jobs/${id}`),
  /** Remote installation of the CENSO agent on a CI from a probe. */
  ciInstallAgentOptions: (ciId: string) => request<{
    os_family: 'linux' | 'windows' | null
    address: string | null
    probes: Array<{ id: string; name: string; online: boolean; capable: boolean; relay: boolean; discovered_it: boolean }>
    credentials: Array<{ id: string; name: string; kind: string }>
    existing_agent: { id: string; name: string } | null
  }>(`/cis/${ciId}/install-agent`),
  ciInstallAgent: (ciId: string, payload: {
    probe_id: string; credential_id: string; os_family?: 'linux' | 'windows' | null
    port?: number | null; winrm_tls?: boolean; force?: boolean
  }) => request<{ job_id: string; agent_id: string; agent_name: string }>(`/cis/${ciId}/install-agent`, {
    method: 'POST', body: JSON.stringify(payload),
  }),
  /** La cattura MIB di un job di campionamento, da scaricare e usare per un driver. */
  jobCapture: (id: string) => request<JobCapture>(`/jobs/${id}/capture`),
  /** The SNMP driver drafted from this sample, if any. */
  jobDriverDraft: (id: string) => request<SnmpDriverDraft | null>(`/jobs/${id}/capture/driver-draft`),
  createJobDriverDraft: (id: string) => request<SnmpDriverDraft>(`/jobs/${id}/capture/driver-draft`, { method: 'POST' }),
  /** Il pcap di una cattura di prova (`traffic_sample`), da scaricare (Wireshark). */
  jobPcap: (id: string) => downloadBlob(`/jobs/${id}/pcap`),
  /**
   * Una scansione già fatta, riletta come modulo di pianificazione già compilato.
   *
   * La bozza non crea niente: torna al modulo, che la manda a `POST /schedules`
   * come una pianificazione qualunque. Farla creare al server sarebbe più corto
   * e sbagliato — la creazione verifica credenziali, licenza e piano *adesso*, e
   * conserva il piano per **riferimento** mentre il job ne ha una copia
   * congelata dei passi.
   */
  scheduleDraft: (jobId: string) =>
    request<ScheduleDraft>(`/jobs/${jobId}/schedule-draft`),

  // --- Agenti d'inventario ---
  //
  // Macchine che una sonda non raggiunge e che parlano loro. Il registro dice
  // chi ha il permesso di consegnare; la configurazione, che contiene il
  // segreto in chiaro, torna **una volta sola**.
  // --- Configurazioni degli apparati di rete ---
  /** Cosa è cambiato di recente: ogni riga è una modifica vera. */
  configChanges: () =>
    request<ConfigRevision[]>('/device-configs/changes'),
  /**
   * Gli apparati che hanno una configurazione salvata, uno per riga.
   *
   * Con quaranta apparati l'elenco delle modifiche è illeggibile — centinaia di
   * righe — e la domanda «com'è messo lo switch di Milano» non ha una riga a cui
   * corrispondere. Questo elenco ce l'ha.
   */
  /** La politica di conservazione, e quante revisioni toglierebbe adesso. */
  configRetention: () =>
    request<{
      max_versions: number | null
      max_days: number | null
      would_remove: number
    }>('/device-configs/retention'),
  setConfigRetention: (payload: {
    max_versions: number | null
    max_days: number | null
  }) =>
    request<{
      max_versions: number | null
      max_days: number | null
      would_remove: number
    }>('/device-configs/retention', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteConfigRevision: (id: string) =>
    request<void>(`/device-configs/revisions/${id}`, { method: 'DELETE' }),
  /** Cancella tutta la storia di un apparato: serve quando viene dismesso. */
  deleteConfigHistory: (ciId: string) =>
    request<void>(`/device-configs/${ciId}`, { method: 'DELETE' }),
  configDevices: () =>
    request<{
      devices: {
        ci_id: string
        ci_name: string | null
        address: string
        family: string
        transport: string
        revisions: number
        last_change: string | null
      }[]
      /** Due CI sullo stesso indirizzo: lo stesso apparato visto due volte. */
      duplicate_addresses: string[]
    }>('/device-configs/devices'),
  configRevisions: (ciId: string) =>
    request<ConfigRevision[]>(`/device-configs/${ciId}/revisions`),
  ciFindings: (ciId: string, opts?: { excludeSbom?: boolean }) =>
    request<SecurityFinding[]>(`/security/findings${qs({ ci_id: ciId, exclude_sbom: opts?.excludeSbom })}`),
  /**
   * Rivaluta la postura sui fatti in inventario.
   *
   * `networkChecks` accende i controlli sui servizi esposti: certificati
   * scaduti, TLS obsoleto, cifrari rotti. Sono l'unica parte che **tocca la
   * rete** — tutto il resto rilegge ciò che è già in CMDB — e per questo va
   * sempre a una sonda, anche su un asset solo: dalla nuvola la rete del
   * cliente non risponde.
   */
  runAssessment: (ciIds?: string[], networkChecks = false) =>
    request<AssessmentResult>('/security/assess', {
      method: 'POST',
      body: JSON.stringify({ ci_ids: ciIds ?? [], network_checks: networkChecks }),
    }),
  securityRisk: () => request<SecurityRisk>('/security/risk'),
  abuseipdb: () => request<{ configured: boolean }>('/security/abuseipdb'),
  setAbuseipdb: (apiKey: string) =>
    request<{ configured: boolean }>('/security/abuseipdb', {
      method: 'PUT',
      body: JSON.stringify({ api_key: apiKey }),
    }),
  nis2: (scope: 'perimetro' | 'tutto' = 'perimetro') => request<Nis2Report>(`/security/nis2?scope=${scope}`),
  addressInventory: () => request<AddressInventory>('/addresses'),
  saveAddressInventorySettings: (body: Partial<AddressInventorySettings>) =>
    request<AddressInventorySettings>('/addresses/settings', { method: 'PUT', body: JSON.stringify(body) }),
  updateAddressRecord: (id: string, body: { description?: string; category?: string; owner?: string }) =>
    request<AddressRecordInfo>(`/addresses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** Reverse DNS now for these addresses (rows created if missing); empty = a full pass from the flows. */
  resolveAddresses: (addresses: string[]) =>
    request<Record<string, unknown>>('/addresses/resolve', { method: 'POST', body: JSON.stringify({ addresses }) }),
  /** «Documenta su IPAM» for an address-inventory row (phpIPAM, write enabled on the credential). */
  ipamDocumentRecord: (recordId: string) =>
    request<{ documented: { ip: string; hostname: string; subnet?: string }[]; errors: { ip: string; reason: string }[] }>(
      `/ipam/addresses/document-record/${recordId}`, { method: 'POST' }),
  bitlocker: () => request<BitLockerOverview>('/bitlocker'),
  saveBitLockerSettings: (body: { collect_keys: boolean; rotate_after_reveal: boolean }) =>
    request<BitLockerSettings>('/bitlocker/settings', { method: 'PUT', body: JSON.stringify(body) }),
  bitlockerRequestInventory: () => request<{ requested: number }>('/bitlocker/request-inventory', { method: 'POST' }),
  bitlockerSearch: (q: string) => request<BitLockerKeyInfo[]>(`/bitlocker/search?q=${encodeURIComponent(q)}`),
  /** Reads a recovery key: the reason is required and written down. */
  revealBitLockerKey: (keyId: string, reason: string) =>
    request<BitLockerReveal>(`/bitlocker/keys/${keyId}/reveal`, { method: 'POST', body: JSON.stringify({ reason }) }),
  rotateBitLockerKey: (keyId: string, reason: string) =>
    request<BitLockerRotationInfo>(`/bitlocker/keys/${keyId}/rotate`, { method: 'POST', body: JSON.stringify({ reason }) }),
  cancelBitLockerRotation: (rotationId: string) =>
    request<BitLockerRotationInfo>(`/bitlocker/rotations/${rotationId}/cancel`, { method: 'POST' }),
  bitlockerRotations: () => request<BitLockerRotationInfo[]>('/bitlocker/rotations'),
  bitlockerAccess: () => request<BitLockerAccessRow[]>('/bitlocker/access'),
  nis2Profile: () => request<Nis2Profile>('/nis2/profile'),
  saveNis2Profile: (body: Partial<Nis2Profile>) =>
    request<Nis2Profile>('/nis2/profile', { method: 'PUT', body: JSON.stringify(body) }),
  nis2Scope: () => request<Nis2Scope>('/nis2/scope'),
  saveNis2Scope: (body: Partial<Nis2ScopeRules>) =>
    request<Nis2Scope>('/nis2/scope', { method: 'PUT', body: JSON.stringify(body) }),
  nis2ScopeInclude: (ciIds: string[]) =>
    request<Nis2Scope>('/nis2/scope/include', { method: 'POST', body: JSON.stringify({ ci_ids: ciIds }) }),
  nis2ScopeExclude: (ciIds: string[]) =>
    request<Nis2Scope>('/nis2/scope/exclude', { method: 'POST', body: JSON.stringify({ ci_ids: ciIds }) }),
  nis2ScopeCandidates: () => request<Nis2ScopeCandidate[]>('/nis2/scope/candidates'),
  nis2Exposure: () => request<Nis2Exposure>('/nis2/exposure'),
  nis2ExposureRefresh: () => request<Nis2Exposure>('/nis2/exposure/refresh', { method: 'POST' }),
  nis2AcnList: () => request<Nis2AcnList>('/nis2/exposure/acn'),
  nis2Deadlines: () => request<Nis2Deadlines>('/nis2/deadlines'),
  nis2SeedObligations: () => request<Nis2Deadlines>('/nis2/obligations/seed', { method: 'POST' }),
  createNis2Obligation: (body: Nis2ObligationInput) =>
    request<Nis2Obligation>('/nis2/obligations', { method: 'POST', body: JSON.stringify(body) }),
  updateNis2Obligation: (id: string, body: Nis2ObligationInput) =>
    request<Nis2Obligation>(`/nis2/obligations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  completeNis2Obligation: (id: string, evidence: string) =>
    request<{ done: Nis2Obligation; next: Nis2Obligation | null }>(`/nis2/obligations/${id}/complete`, {
      method: 'POST', body: JSON.stringify({ evidence }),
    }),
  deleteNis2Obligation: (id: string) => request<void>(`/nis2/obligations/${id}`, { method: 'DELETE' }),
  nis2Incidents: () => request<Nis2Incident[]>('/nis2/incidents'),
  openNis2Incident: (body: Nis2IncidentInput) =>
    request<Nis2Incident>('/nis2/incidents', { method: 'POST', body: JSON.stringify(body) }),
  updateNis2Incident: (id: string, body: Nis2IncidentInput) =>
    request<Nis2Incident>(`/nis2/incidents/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  dora: () => request<Nis2Report>('/security/dora'),
  /** Compila (o aggiorna) la dichiarazione di un requisito non misurato. */
  dichiaraConformita: (
    framework: string,
    requirementId: string,
    payload: { stato: string; nota: string; riferimento: string; responsabile: string },
  ) =>
    request<ComplianceDeclaration>(
      `/security/compliance/${framework}/declarations/${requirementId}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
  endOfSupport: () => request<EndOfSupport>('/security/end-of-support'),
  securityFindings: (includeAccepted = false) =>
    request<SecurityFinding[]>(
      `/security/findings${qs({ include_accepted: includeAccepted || undefined })}`,
    ),
  acceptFinding: (id: string, reason: string, days: number) =>
    request<SecurityFinding>(`/security/findings/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify({ reason, days }),
    }),
  revokeFindingAcceptance: (id: string) =>
    request<void>(`/security/findings/${id}/accept`, { method: 'DELETE' }),
  backupSettings: () =>
    request<{ netconf: boolean; mask_secrets: boolean }>('/device-configs/settings'),
  setBackupSettings: (netconf: boolean, maskSecrets: boolean) =>
    request<{ netconf: boolean; mask_secrets: boolean }>('/device-configs/settings', {
      method: 'PUT',
      body: JSON.stringify({ netconf, mask_secrets: maskSecrets }),
    }),
  configStats: (ciId: string) => request<ConfigStats>(`/device-configs/${ciId}/stats`),
  configStatus: (ciId: string) =>
    request<ConfigBackupStatus>(`/device-configs/${ciId}/status`),
  configRevision: (id: string) =>
    request<ConfigRevision & { content: string }>(
      `/device-configs/revisions/${id}`,
    ),
  configDiff: (id: string, against?: string) =>
    request<ConfigDiff>(
      `/device-configs/revisions/${id}/diff` +
        (against ? `?against=${encodeURIComponent(against)}` : ''),
    ),
  inventoryAgents: () => request<InventoryAgent[]>('/inventory-agents'),
  /**
   * Dove può consegnare un agente: il cloud, o una sonda che inoltra.
   *
   * L'elenco lo costruisce il server perché è lui a sapere quali sonde hanno il
   * ricevitore acceso — che è spento di default. Chiederlo a chi configura
   * significherebbe fargli indovinare la porta di qualcosa che potrebbe non
   * essere nemmeno in ascolto.
   */
  softwarePaths: () => request<{ rules: RegolaPercorsoSoftware[] }>('/inventory-agents/software-paths'),
  saveSoftwarePaths: (rules: RegolaPercorsoSoftware[]) =>
    request<{ rules: RegolaPercorsoSoftware[] }>('/inventory-agents/software-paths', {
      method: 'PUT', body: JSON.stringify({ rules }) }),
  inventoryDeliveryTargets: () =>
    request<
      { value: string; label: string; hint: string; available: boolean }[]
    >('/inventory-agents/delivery-targets'),
  createInventoryAgent: (payload: {
    name: string
    installation_key?: boolean
    description?: string | null
    ttl_days?: number
    interval_minutes?: number
    endpoint?: string | null
    mode?: 'fast' | 'full' | 'software' | 'all'
    auto_update?: boolean
  }) =>
    request<InventoryAgent>('/inventory-agents', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateInventoryAgent: (
    id: string,
    payload: {
      name?: string
      description?: string | null
      revoked?: boolean
      /** Le impostazioni arrivano alla macchina con la prossima consegna. */
      interval_minutes?: number
      mode?: 'fast' | 'full' | 'software' | 'all'
      /** Stringa vuota = torna al Control Plane. */
      endpoint?: string | null
      auto_update?: boolean
      /** Ascolto DHCP: solo sull'agente installato sul server DHCP. */
      dhcp_listen?: boolean
      sbom?: boolean
      runtime?: boolean
      app_observe?: boolean
    },
  ) =>
    request<InventoryAgent>(`/inventory-agents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  /**
   * Genera un segreto nuovo: il vecchio smette di valere **subito**.
   *
   * È ciò che serve quando una macchina è compromessa o dismessa, e una
   * sovrapposizione «per non interrompere il servizio» lascerebbe valido
   * proprio il segreto che si sta revocando.
   */
  /** The agent's log on request: ask, then read (with the content when asked). */
  agentLogRichiedi: (id: string) => request<DeviceLog>(`/inventory-agents/${id}/logs`, { method: 'POST' }),
  agentLog: (id: string, content = false) => request<DeviceLog>(`/inventory-agents/${id}/logs${content ? '?content=true' : ''}`),
  regenerateInventoryAgent: (id: string) =>
    request<InventoryAgent>(`/inventory-agents/${id}/regenerate`, { method: 'POST' }),
  /** «Inventario adesso»: l'agente residente lo riceve entro un minuto. */
  inventoryAgentNow: (id: string) =>
    request<InventoryAgent>(`/inventory-agents/${id}/inventory-now`, { method: 'POST' }),
  deleteInventoryAgent: (id: string) =>
    request<void>(`/inventory-agents/${id}`, { method: 'DELETE' }),
  /** Le stesse impostazioni (features, manutenzione) su più agenti selezionati. */
  bulkInventoryAgents: (
    ids: string[],
    patch: {
      interval_minutes?: number
      mode?: string
      auto_update?: boolean
      dhcp_listen?: boolean
      sbom?: boolean
      runtime?: boolean
      app_observe?: boolean
    },
  ) =>
    aLotti(ids, (lotto) =>
      request<{ matched: number; changed: number }>('/inventory-agents/bulk', {
        method: 'POST',
        body: JSON.stringify({ ...patch, ids: lotto }),
      }),
    ).then((esiti) => somma(esiti, ['matched', 'changed'])),

  // --- Pool di asset ---
  //
  // Elenchi di ambiti con un nome, richiamabili da discovery e pianificazioni.
  // Contengono **ambiti** — dove cercare — non righe di `Asset`.
  pools: () => request<AssetPool[]>('/pools'),
  pool: (id: string) => request<AssetPool>(`/pools/${id}`),
  createPool: (payload: {
    name: string
    description?: string | null
    kind?: 'static' | 'dynamic'
    entries?: { value: string; note?: string | null }[]
    match?: ConditionGroup[]
  }) => request<AssetPool>('/pools', { method: 'POST', body: JSON.stringify(payload) }),
  updatePool: (
    id: string,
    payload: { name?: string; description?: string | null; match?: ConditionGroup[] },
  ) => request<AssetPool>(`/pools/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  /**
   * Che cosa prenderebbero queste condizioni, adesso, senza salvare niente.
   *
   * Una condizione si scrive a occhio e si sbaglia a occhio: «il nome contiene
   * SRV» prende anche `PRINT-SRV-2`, e la scansione che ne nasce **riesce** —
   * guarda un perimetro diverso da quello inteso e restituisce un inventario
   * plausibile. Vedere il numero mentre si scrive toglie l'intera classe di
   * errore.
   */
  previewPool: (match: ConditionGroup[]) =>
    request<PoolPreview>('/pools/preview', {
      method: 'POST',
      body: JSON.stringify({ match }),
    }),
  /**
   * Sostituisce l'elenco degli ambiti.
   *
   * Sostituisce e non aggiunge: il modulo mostra l'elenco intero e lo rimanda
   * intero, e una semantica additiva renderebbe impossibile **togliere** una
   * riga — che è metà del lavoro di mantenere un pool.
   */
  setPoolEntries: (id: string, entries: { value: string; note?: string | null }[]) =>
    request<AssetPool>(`/pools/${id}/entries`, {
      method: 'PUT',
      // Un solo testo (ambito, tab, nota; una riga per ambito) e non la
      // lista: il WAF rifiuta un corpo JSON con più di mille argomenti, e un
      // pool di cinquecento reti con le note li supera.
      body: JSON.stringify({
        text: entries
          .map((e) => `${e.value.replace(/[\t\r\n]+/g, ' ')}\t${(e.note ?? '').replace(/[\t\r\n]+/g, ' ')}`)
          .join('\n'),
      }),
    }),
  /**
   * Importa ambiti da un CSV.
   *
   * Di norma **o entra tutto, o non entra niente**: un file con tre righe
   * sbagliate importato a metà produce un pool che sembra completo, e la
   * scansione che ne nasce restituisce un inventario plausibile e incompleto.
   * Con `skipInvalid` si importa il resto, e la risposta dice quante righe sono
   * rimaste indietro.
   */
  importPool: (
    id: string,
    csv: string,
    options: { replace?: boolean; skipInvalid?: boolean } = {},
  ) =>
    request<AssetPool>(
      `/pools/${id}/import${qs({
        replace: options.replace ? 'true' : undefined,
        skip_invalid: options.skipInvalid ? 'true' : undefined,
      })}`,
      // Il CSV dentro JSON come tutto il resto: un solo endpoint con un tipo
      // di contenuto diverso è la riga che qualcuno dimentica, e il file
      // arriverebbe interpretato come JSON con un errore che parla di sintassi
      // invece che di ambiti.
      { method: 'POST', body: JSON.stringify({ csv }) },
    ),
  deletePool: (id: string) => request<void>(`/pools/${id}`, { method: 'DELETE' }),

  // --- Pianificazioni ---
  schedules: () => request<Schedule[]>('/schedules'),
  createSchedule: (payload: ScheduleCreate) =>
    request<Schedule>('/schedules', { method: 'POST', body: JSON.stringify(payload) }),
  updateSchedule: (id: string, payload: Partial<ScheduleCreate>) =>
    request<Schedule>(`/schedules/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteSchedule: (id: string) => request<void>(`/schedules/${id}`, { method: 'DELETE' }),
  bulkSchedules: (payload: ScheduleBulk) =>
    request<ScheduleBulkResult>('/schedules/bulk', { method: 'POST', body: JSON.stringify(payload) }),
  runScheduleNow: (id: string) => request<Job>(`/schedules/${id}/run`, { method: 'POST' }),
  scheduleRuns: (id: string) => request<Job[]>(`/schedules/${id}/runs`),

  // --- Utenti ---
  users: () => request<User[]>('/users'),

  // --- Ruoli personalizzati (permessi per modulo) ---
  roleCatalog: () => request<CatalogoRuoli>('/roles/catalog'),
  customRoles: () => request<RuoloPersonalizzato[]>('/roles'),
  createCustomRole: (payload: RuoloPersonalizzatoIn) =>
    request<RuoloPersonalizzato>('/roles', { method: 'POST', body: JSON.stringify(payload) }),
  updateCustomRole: (id: string, payload: RuoloPersonalizzatoIn) =>
    request<RuoloPersonalizzato>(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteCustomRole: (id: string) => request<void>(`/roles/${id}`, { method: 'DELETE' }),

  // --- Chiavi API del tenant (profilo per modulo; enterprise) ---
  tenantApiKeys: () => request<TenantApiKeysResponse>('/tenant-api-keys'),
  createTenantApiKey: (payload: TenantApiKeyIn) =>
    request<TenantApiKeyCreated>('/tenant-api-keys', { method: 'POST', body: JSON.stringify(payload) }),
  revokeTenantApiKey: (id: string) =>
    request<void>(`/tenant-api-keys/${id}`, { method: 'DELETE' }),

  // --- Link per i monitor: il sinottico di Eagle senza sessione ---
  wallLinks: () => request<{ links: EagleWallLink[]; path: string }>('/monitoring/wall-links'),
  wallLinkCrea: (payload: EagleWallLinkIn) =>
    request<EagleWallLinkCreated>('/monitoring/wall-links', { method: 'POST', body: JSON.stringify(payload) }),
  wallLinkAggiorna: (id: string, payload: EagleWallLinkIn) =>
    request<EagleWallLink>(`/monitoring/wall-links/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  wallLinkRevoca: (id: string) => request<void>(`/monitoring/wall-links/${id}`, { method: 'DELETE' }),
  /** What the monitor shows through its link: title and dashboard. */
  wallLinkMe: () => request<EagleWallMe>('/monitoring/wall-links/me'),
  // --- CENSO Monitor: the paired screens (signage) ---
  wallMonitors: () => request<{ monitors: WallMonitor[]; server: string; downloads: Record<string, string>; iso: WallMonitorIso | null }>('/monitoring/monitors'),
  wallMonitorAccoppia: (body: { code: string; name: string; detail?: string | null; dashboard_id: string | null; title: string | null }) =>
    request<WallMonitor>('/monitoring/monitors/pair', { method: 'POST', body: JSON.stringify(body) }),
  /** `name` renames when not empty; `detail` and `comment` are written only when present in the body. */
  wallMonitorImposta: (id: string, body: { name?: string; detail?: string; comment?: string; dashboard_id: string | null; title: string | null; fullscreen: boolean; screen: 'on' | 'off'; settings?: WallMonitorSettings }) =>
    request<WallMonitor>(`/monitoring/monitors/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  wallMonitorComanda: (id: string, kind: string) =>
    request<WallMonitor>(`/monitoring/monitors/${id}/command`, { method: 'POST', body: JSON.stringify({ kind }) }),
  wallMonitorScollega: (id: string) => request<void>(`/monitoring/monitors/${id}`, { method: 'DELETE' }),
  wallMonitorAnnullaComandi: (id: string) => request<WallMonitor>(`/monitoring/monitors/${id}/commands`, { method: 'DELETE' }),
  /** The target for an address (a CI created once, ping + fast ping on, the chosen probe as its point). */
  eagleBersaglio: (body: { address: string; name: string | null; probe_id: string | null }) =>
    request<EagleBersaglio>('/monitoring/targets', { method: 'POST', body: JSON.stringify(body) }),

  // --- Provider di identità (OpenID Connect) ---
  //
  // Uno per tenant, configurato da chi amministra quel tenant. Il segreto del
  // client si scrive e non si rilegge mai.
  identityProviders: () => request<IdentityProvider[]>('/identity-providers'),
  createIdentityProvider: (payload: IdentityProviderInput) =>
    request<IdentityProvider>('/identity-providers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateIdentityProvider: (id: string, payload: IdentityProviderInput) =>
    request<IdentityProvider>(`/identity-providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  /**
   * Verifica la configurazione senza dover fare un accesso vero.
   *
   * Non può dire tutto — il client secret e l'indirizzo di ritorno l'IdP li
   * verifica solo quando qualcuno arriva davvero — e la risposta lo dichiara:
   * un esito verde che si legge come «funziona tutto» è peggio di nessuna
   * prova.
   */
  testIdentityProvider: (id: string) =>
    request<{
      ok: boolean
      problems: string[]
      discovered: Record<string, string | null>
      not_checked: string[]
    }>(`/identity-providers/${id}/test`, { method: 'POST' }),
  deleteIdentityProvider: (id: string) =>
    request<void>(`/identity-providers/${id}`, { method: 'DELETE' }),
  /**
   * Dice se un indirizzo si autentica con un provider, e dove mandarlo.
   *
   * Risponde con i campi vuoti quando il dominio non è federato: è il caso
   * normale, e un errore lo renderebbe rumore. Non dice se l'utenza esiste —
   * quello si ricaverebbe provando indirizzi a caso.
   */
  mfaVerify: (mfaToken: string, code: string) =>
    request<TokenResponse>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ mfa_token: mfaToken, code }),
    }),
  mfaStatus: () => request<{ enabled: boolean }>('/auth/mfa'),
  mfaSetup: () =>
    request<{ secret: string; otpauth_uri: string }>('/auth/mfa/setup', { method: 'POST' }),
  mfaActivate: (code: string) =>
    request<void>('/auth/mfa/activate', { method: 'POST', body: JSON.stringify({ code }) }),
  mfaDisable: (code: string) =>
    request<void>('/auth/mfa/disable', { method: 'POST', body: JSON.stringify({ code }) }),
  setPassword: (token: string, password: string) =>
    request<void>('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  oidcStart: (email: string, redirectUri: string) =>
    request<{ authorization_url: string | null; provider_name: string | null }>(
      '/auth/oidc/start',
      { method: 'POST', body: JSON.stringify({ email, redirect_uri: redirectUri }) },
    ),
  oidcCallback: (state: string, code: string) =>
    request<TokenResponse>('/auth/oidc/callback', {
      method: 'POST',
      body: JSON.stringify({ state, code }),
    }),
  createUser: (payload: {
    email: string
    display_name?: string | null
    password: string
    roles: string[]
  }) => request<User>('/users', { method: 'POST', body: JSON.stringify(payload) }),
  inviteMember: (payload: { email: string; roles: string[] }) =>
    request<User>('/users/invite', { method: 'POST', body: JSON.stringify(payload) }),
  /** Invita una persona nuova: nasce senza password e riceve un'email con un
   *  collegamento a scadenza per impostarla. `expiry_hours` è configurabile. */
  inviteNewUser: (payload: {
    email: string
    display_name?: string | null
    roles: string[]
    expiry_hours?: number
  }) => request<User>('/users/invita-nuovo', { method: 'POST', body: JSON.stringify(payload) }),
  /** La scadenza predefinita degli inviti del tenant (ore). */
  inviteSettings: () => request<{ expiry_hours: number }>('/users/invite-settings'),
  setInviteSettings: (expiry_hours: number) =>
    request<{ expiry_hours: number }>('/users/invite-settings', {
      method: 'PUT',
      body: JSON.stringify({ expiry_hours }),
    }),
  updateUser: (
    id: string,
    payload: { roles?: string[]; status?: string; display_name?: string; custom_role_id?: string | null },
  ) =>
    request<User>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteUser: (id: string) => request<void>(`/users/${id}`, { method: 'DELETE' }),
  /** A link to choose the password: the invitation again, or a reset link. */
  sendPasswordLink: (id: string) =>
    request<{ sent: 'invite' | 'reset'; email: string }>(`/users/${id}/password-link`, { method: 'POST' }),
  /** Generate the link and show it (once): only for accounts of this tenant. */
  showPasswordLink: (id: string) =>
    request<PasswordLinkShown>(`/users/${id}/password-link/show`, { method: 'POST' }),
  resetUserPassword: (id: string, newPassword: string) =>
    request<void>(`/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    }),
  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    request<void>('/users/me/password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  // --- Licenza (tenant: sola lettura) ---
  license: () => request<LicenseStatus>('/license'),
  /** Che estensione della prova si può chiedere (risponde anche a licenza scaduta). */
  trialExtension: () => request<TrialExtensionStatus>('/license/trial-extension'),
  /** Chiede un'estensione della prova: la prima si approva da sola. */
  requestTrialExtension: () =>
    request<TrialExtensionStatus>('/license/trial-extension', { method: 'POST' }),

  // --- Licenze (provider: assegnazione) ---
  // Le licenze dei clienti vivono sul **piano di amministrazione**, su porta e
  // processo separati: questa superficie non le espone, per progetto. Averle qui
  // significava una schermata che chiamava una rotta inesistente e mostrava
  // «Nessun tenant» — una risposta plausibile a una domanda mai arrivata.
  // Vedi `src/admin/api.ts`.

  // --- Traffico osservato ---
  flowWindows: () => request<FlowWindow[]>('/flows/windows'),
  conversations: (params: { window?: string; ci_id?: string; limit?: number } = {}) =>
    request<Conversation[]>(`/flows/conversations${qs(params)}`),
  flowSeries: (
    params: {
      window?: string
      ci_id?: string
      peer_ci_id?: string
      src_addr?: string
      dst_addr?: string
      port?: number
      protocol?: number
    } = {},
  ) => request<FlowSeries>(`/flows/series${qs(params)}`),
  flowBreakdown: (
    params: { by: 'service' | 'protocol'; window?: string; ci_id?: string; peer_ci_id?: string },
  ) => request<FlowBreakdown[]>(`/flows/breakdown${qs(params)}`),
  /** Traffic Analysis: traffico decodificato, pacchetti intercettati, dispositivi. */
  trafficAnalysis: (hours: number) => request<TrafficAnalysis>(`/flows/analysis?hours=${hours}`),

  /** L'andamento del riconoscimento dei dispositivi, un punto al giorno. */
  recognitionTrend: (days = 30) =>
    request<RecognitionTrend>(`/flows/analysis/recognition-trend?days=${days}`),
  /** Gli host con fatti dei protocolli sul filo, per protocollo (o «medical», «all»). */
  trafficAnalysisHosts: (protocol: string) =>
    request<{ protocol: string; count: number; rows: HostSighting[] }>(`/flows/analysis/hosts?protocol=${encodeURIComponent(protocol)}&limit=1000`),
  /** I fatti del filo per gli indirizzi di un CI. */
  ciWireSightings: (ciId: string) => request<HostSighting[]>(`/cis/${ciId}/wire-sightings`),
  /** La lista dietro un numero di Traffic Analysis. */
  /** Azzera gli avvistamenti del tenant (solo amministratori): si ricostruiscono da soli. */
  clearSightings: () => request<{ deleted: number }>('/flows/analysis/sightings', { method: 'DELETE' }),
  /** Inventari di riferimento (Armis, Medigate, Ingegneria Clinica). */
  /** Il foglio viaggia come un solo testo separato da tab (`tabellaTsv`): il
   *  WAF rifiuta un corpo JSON con più di mille argomenti, e una lista di
   *  righe e celle li supera già con poche centinaia di dispositivi. */
  importReferences: (source: ReferenceSourceKey, fileName: string, table: string) =>
    request<{ source: string; imported: number; skipped: number; columns: string[] }>('/flows/analysis/references', {
      method: 'POST', body: JSON.stringify({ source, file_name: fileName, table }),
    }),
  referenceComparison: () => request<ReferenceComparison>('/flows/analysis/references'),
  /** Passive inventory: recognised devices become CIs without being contacted. */
  passiveInventory: () => request<PassiveInventory>('/flows/analysis/passive-inventory'),
  savePassiveInventory: (mode: PassiveInventory['mode'], maxAgeDays: number) =>
    request<PassiveInventory>('/flows/analysis/passive-inventory', {
      method: 'PUT', body: JSON.stringify({ mode, max_age_days: maxAgeDays }),
    }),
  runPassiveInventory: () => request<PassiveInventoryResult>('/flows/analysis/passive-inventory/run', { method: 'POST' }),
  deleteReferences: (source: ReferenceSourceKey) =>
    request<{ deleted: number }>(`/flows/analysis/references?source=${encodeURIComponent(source)}`, { method: 'DELETE' }),
  referenceSignatures: () => request<ReferenceSignatures>('/flows/analysis/references/signatures'),
  learnReferences: (keys: string[]) =>
    request<{ written: Record<string, number> }>('/flows/analysis/references/learn', { method: 'POST', body: JSON.stringify({ keys }) }),
  trafficAnalysisDevices: (filter: string, value?: string) =>
    request<DeviceList>(`/flows/analysis/devices?filter=${encodeURIComponent(filter)}${value ? `&value=${encodeURIComponent(value)}` : ''}&limit=1000`),
  flowExporters: () => request<FlowExporter[]>('/flows/exporters'),
  flowCaptureProbes: () => request<CaptureProbe[]>('/flows/capture-probes'),
  setExporterSampling: (address: string, sample_every: number) =>
    request<{ address: string; sample_every: number }>(
      `/flows/exporters/${encodeURIComponent(address)}/sampling`,
      { method: 'PUT', body: JSON.stringify({ sample_every }) },
    ),

  // --- Servizi di business ---
  services: () => request<BusinessService[]>('/services'),
  servicesExportPath: (fmt: 'xlsx' | 'pdf') => `/services/export?fmt=${fmt}`,
  service: (id: string) => request<BusinessService>(`/services/${id}`),
  createService: (payload: {
    name: string
    description?: string | null
    criticality?: string
    owner?: string | null
    max_depth?: number
    entries: ServiceEntry[]
    hops?: ServiceHop[]
    composition_filters?: FiltriComposizione
  }) => request<BusinessService>('/services', { method: 'POST', body: JSON.stringify(payload) }),
  /** The dependencies of a service as last saved (entries, components, infrastructure). */
  serviceMembers: (id: string) => request<ServiceMembers>(`/services/${id}/members`),
  refreshServiceMembers: (id: string) =>
    request<{ members: number; added: number; removed: number }>(`/services/${id}/members/refresh`, { method: 'POST' }),
  /** Tags on the dependencies: all, the chosen CIs, or the chosen roles. */
  tagServiceMembers: (id: string, payload: { tags: string[]; ci_ids?: string[]; roles?: string[] }) =>
    request<{ cis: number; tags: number; assigned: number }>(`/services/${id}/members/tags`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  criticalitySettings: () => request<{ infrastructure_inherits: boolean }>('/services/criticality-settings'),
  saveCriticalitySettings: (infrastructureInherits: boolean) =>
    request<{ infrastructure_inherits: boolean }>('/services/criticality-settings', {
      method: 'PUT', body: JSON.stringify({ infrastructure_inherits: infrastructureInherits }),
    }),
  /** The Business Apps a service uses: it inherits their criticality. */
  setServiceApps: (id: string, appIds: string[]) =>
    request<BusinessService>(`/services/${id}/apps`, { method: 'PUT', body: JSON.stringify({ app_ids: appIds }) }),
  /** Mappa Software: the application flow around a CI. */
  softwareFlow: (ciId: string, opts: { depth?: number; windowHours?: number; windowMinutes?: number; common?: boolean; flows?: boolean; exclude?: string[] } = {}) => {
    const p = new URLSearchParams({
      depth: String(opts.depth ?? 2),
      window_hours: String(opts.windowHours ?? 24),
      common: opts.common ? 'true' : 'false',
      flows: opts.flows === false ? 'false' : 'true',
    })
    if (opts.windowMinutes) p.set('window_minutes', String(opts.windowMinutes))
    for (const e of opts.exclude ?? []) p.append('exclude', e)
    return request<SoftwareFlow>(`/software-flow/ci/${ciId}?${p.toString()}`)
  },
  /** The vulnerable libraries and package CVEs of one program of the map. */
  programVulnerabilities: (ciId: string, opts: { applicationId?: string | null; pkg?: string | null }) => {
    const p = new URLSearchParams()
    if (opts.applicationId) p.set('application_id', opts.applicationId)
    if (opts.pkg) p.set('package', opts.pkg)
    return request<ProgramVulnerabilities>(`/software-flow/ci/${ciId}/vulnerabilities?${p.toString()}`)
  },
  /** Unencrypted traffic between servers (connections, eBPF/OpenTelemetry, flows). */
  plaintextTraffic: (hours = 24) => request<PlaintextTraffic>(`/software-flow/plaintext?hours=${hours}`),
  /** Gestione certificati: CA connections, repository, requests (pki_service). */
  pkiConnections: () => request<PkiConnection[]>('/pki/connections'),
  pkiCas: (credentialId: string) =>
    request<{ mode: string; cas: PkiCa[]; templates: PkiTemplate[] }>(`/pki/connections/${credentialId}/cas`),
  pkiSync: (credentialId: string, caConfig: string | null) =>
    request<{ added: number; updated: number; truncated: boolean }>(`/pki/connections/${credentialId}/sync`,
      { method: 'POST', body: JSON.stringify({ ca_config: caConfig }) }),
  pkiCertificates: () => request<PkiRepository>('/pki/certificates'),
  pkiImport: (payload: { text: string; password?: string | null; owner?: string | null; notes?: string | null; ci_id?: string | null }) =>
    request<{ imported: number; keys: number; ids: string[] }>('/pki/certificates/import', { method: 'POST', body: JSON.stringify(payload) }),
  pkiCertificate: (id: string) => request<PkiChain>(`/pki/certificates/${id}`),
  pkiExport: (id: string, body: { format: 'pfx' | 'pfx_legacy' | 'key_pem'; password: string; include_chain: boolean }) =>
    postBlob(`/pki/certificates/${id}/export`, body),
  pkiKeys: () => request<PkiKey[]>('/pki/keys'),
  pkiDeployTargets: () => request<PkiDeployTarget[]>('/pki/deploy-targets'),
  pkiDeploy: (certificateId: string, body: { ci_id: string; target: Record<string, unknown> }) =>
    request<PkiDeployment>(`/pki/certificates/${certificateId}/deploy`, { method: 'POST', body: JSON.stringify(body) }),
  pkiDeployments: (certificateId?: string) =>
    request<PkiDeployment[]>(`/pki/deployments${certificateId ? `?certificate_id=${certificateId}` : ''}`),
  pkiRetryDeployment: (id: string) => request<{ ok: boolean }>(`/pki/deployments/${id}/retry`, { method: 'POST' }),
  pkiCancelDeployment: (id: string) => request<{ ok: boolean }>(`/pki/deployments/${id}/cancel`, { method: 'POST' }),
  pkiDeleteKey: (id: string) => request<void>(`/pki/keys/${id}`, { method: 'DELETE' }),
  pkiGenerate: (payload: {
    credential_id: string; template: string; cn: string; sans: string[]; key: string; organization?: string
    country?: string; ca_config?: string | null; ci_id?: string | null; notes?: string | null; replaces_sha256?: string | null
  }) => request<PkiRequest>('/pki/requests/generate', { method: 'POST', body: JSON.stringify(payload) }),
  pkiConvert: (body: { text: string; target: string; password_in?: string | null; password_out?: string | null; include_chain: boolean }) =>
    postBlob('/pki/convert', body),
  pkiUpdateCertificate: (id: string, payload: { owner?: string | null; notes?: string | null; ci_id?: string | null }) =>
    request<{ ok: boolean }>(`/pki/certificates/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  pkiDeleteCertificate: (id: string) => request<void>(`/pki/certificates/${id}`, { method: 'DELETE' }),
  pkiDownload: (id: string, format: 'pem' | 'der' | 'fullchain' | 'cachain' | 'p7b') =>
    downloadBlob(`/pki/certificates/${id}/download?format=${format}`),
  pkiRequests: () => request<PkiRequest[]>('/pki/requests'),
  pkiCreateRequest: (payload: {
    credential_id: string; template: string; csr_pem: string; ca_config?: string | null
    ci_id?: string | null; notes?: string | null; replaces_sha256?: string | null
  }) => request<PkiRequest>('/pki/requests', { method: 'POST', body: JSON.stringify(payload) }),
  pkiSubmit: (id: string) => request<PkiRequest>(`/pki/requests/${id}/submit`, { method: 'POST' }),
  pkiRefresh: (id: string) => request<PkiRequest>(`/pki/requests/${id}/refresh`, { method: 'POST' }),
  pkiDeleteRequest: (id: string) => request<void>(`/pki/requests/${id}`, { method: 'DELETE' }),
  pkiRecipe: (payload: {
    cn: string; sans: string[]; key: string; organization?: string; unit?: string; locality?: string
    country?: string; exportable?: boolean
  }) => request<PkiRecipe>('/pki/csr-recipe', { method: 'POST', body: JSON.stringify(payload) }),
  /** The TLS certificates the tenant's machines expose. */
  tlsCertificates: () => request<TlsRegister>('/software-flow/certificates'),
  /** Software prodotto: GitLab, Artifactory and Nexus, build SBOMs by source. */
  supplyOverview: () => request<SupplyOverview>('/supply-chain'),
  supplyProjects: () => request<CodeProjectRow[]>('/supply-chain/projects'),
  supplyProject: (id: string) => request<CodeProjectDetail>(`/supply-chain/projects/${id}`),
  supplyPipelines: () => request<SupplyPipelineRow[]>('/supply-chain/pipelines'),
  supplyArtifacts: (provider?: string) =>
    request<RegistryArtifactRow[]>(`/supply-chain/artifacts${provider ? `?provider=${provider}` : ''}`),
  supplyComponents: (source: string) =>
    request<{ source: string; rows: SupplyComponentRow[]; truncated: boolean }>(`/supply-chain/components?source=${source}`),
  supplyVulnerabilities: (source: string) =>
    request<{ source: string; rows: SupplyVulnerabilityRow[]; truncated: boolean }>(`/supply-chain/vulnerabilities?source=${source}`),
  supplyComparison: (kind: 'vulnerabilities' | 'components' | 'artifacts') =>
    request<SupplyComparison>(`/supply-chain/comparison?kind=${kind}`),
  supplyRefresh: (credentialId?: string) =>
    request<{ requested: number }>('/supply-chain/refresh', { method: 'POST', body: JSON.stringify({ credential_id: credentialId ?? null }) }),
  supplyStatus: () => request<SupplyConnection[]>('/supply-chain/status'),
  supplySbomDocumentPath: (id: string) => `/supply-chain/sboms/${id}/document`,
  supplyRefs: (projectId: string) =>
    request<{ branches: string[]; tags: string[]; default_branch: string }>(`/supply-chain/projects/${projectId}/refs`),
  supplyPipelinePreview: (projectId: string, body: { action: string; ref?: string; pipeline_id?: number | null }) =>
    request<SupplyPipelinePlan>(`/supply-chain/projects/${projectId}/pipelines/preview`, { method: 'POST', body: JSON.stringify(body) }),
  supplyPipelineAction: (projectId: string, body: { action: string; ref?: string; pipeline_id?: number | null }) =>
    request<{ id: string; status: string; pipeline_id: number | null }>(`/supply-chain/projects/${projectId}/pipelines`, { method: 'POST', body: JSON.stringify(body) }),
  supplyChangePreview: (projectId: string, body: { kind: string; options: SupplyChangeOptions }) =>
    request<SupplyChangePlan>(`/supply-chain/projects/${projectId}/changes/preview`, { method: 'POST', body: JSON.stringify(body) }),
  supplyChangeOpen: (projectId: string, body: { kind: string; options: SupplyChangeOptions; base_commit: string }) =>
    request<{ id: string; status: string; mr_iid: number | null; mr_url: string; branch: string }>(`/supply-chain/projects/${projectId}/changes`, { method: 'POST', body: JSON.stringify(body) }),
  supplyChanges: () => request<SupplyChangeRow[]>('/supply-chain/changes'),
  supplyGate: (projectId: string) =>
    request<{ gate: PipelineGateInfo | null; decisions: GateDecision[] }>(`/supply-chain/projects/${projectId}/gate`),
  supplyGatePreview: (projectId: string, body: GatePolicyIn) =>
    request<GatePlan>(`/supply-chain/projects/${projectId}/gate/preview`, { method: 'POST', body: JSON.stringify(body) }),
  supplyGateSet: (projectId: string, body: GatePolicyIn) =>
    request<GateSetResult>(`/supply-chain/projects/${projectId}/gate`, { method: 'POST', body: JSON.stringify(body) }),
  supplyGateToken: (projectId: string) =>
    request<GateSetResult>(`/supply-chain/projects/${projectId}/gate/token`, { method: 'POST' }),
  supplyGateOff: (projectId: string) =>
    request<{ gate: PipelineGateInfo; variable_removed: boolean }>(`/supply-chain/projects/${projectId}/gate`, { method: 'DELETE' }),
  supplyGateDecisions: () => request<GateDecision[]>('/supply-chain/gate/decisions'),
  supplyGateDecision: (id: string) => request<GateDecision>(`/supply-chain/gate/decisions/${id}`),
  supplyVex: (projectId?: string) =>
    request<VexRow[]>(`/supply-chain/vex${projectId ? `?project_id=${projectId}` : ''}`),
  supplyVexCreate: (body: VexIn) => request<VexRow>('/supply-chain/vex', { method: 'POST', body: JSON.stringify(body) }),
  supplyVexUpdate: (id: string, body: Pick<VexIn, 'state' | 'justification' | 'responses' | 'detail' | 'expires_at'>) =>
    request<VexRow>(`/supply-chain/vex/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  supplyVexRevoke: (id: string) => request<VexRow>(`/supply-chain/vex/${id}/revoke`, { method: 'POST' }),
  /** The path of the project's VEX export, to download with scaricaJson. */
  supplyVexExportPath: (projectId: string, format: 'cyclonedx' | 'openvex') =>
    `/supply-chain/projects/${projectId}/vex/export?format=${format}`,
  cra: () => request<CraOverview>('/cra'),
  craRefresh: () => request<{ new_detections: number }>('/cra/refresh', { method: 'POST' }),
  craProducts: () => request<CraProduct[]>('/cra/products'),
  craProduct: (id: string) => request<CraProductDetail>(`/cra/products/${id}`),
  craProductCreate: (body: CraProductIn) => request<CraProduct>('/cra/products', { method: 'POST', body: JSON.stringify(body) }),
  craImportBusinessApps: (ids: string[]) =>
    request<{ created: CraProduct[]; skipped: number }>('/cra/products/from-business-apps',
      { method: 'POST', body: JSON.stringify({ ids }) }),
  craProductUpdate: (id: string, body: CraProductIn) =>
    request<CraProduct>(`/cra/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  craProductDelete: (id: string) => request<{ deleted: boolean }>(`/cra/products/${id}`, { method: 'DELETE' }),
  craReleaseAdd: (productId: string, body: { sbom_id?: string; version?: string; released_at?: string | null;
    supported_until?: string | null; notes?: string; sbom?: string }) =>
    request<CraRelease>(`/cra/products/${productId}/releases`, { method: 'POST', body: JSON.stringify(body) }),
  craReleaseUpdate: (id: string, body: { status: string; supported_until: string | null; notes: string }) =>
    request<CraRelease>(`/cra/releases/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** The path of a release's CycloneDX (with vulnerabilities and VEX), for scaricaJson. */
  craReleaseCyclonedxPath: (id: string) => `/cra/releases/${id}/cyclonedx`,
  craDetections: (openOnly = true) => request<CraDetection[]>(`/cra/detections?open_only=${openOnly}`),
  craProductVex: (productId: string, body: VexIn) =>
    request<VexRow>(`/cra/products/${productId}/vex`, { method: 'POST', body: JSON.stringify(body) }),
  craReports: () => request<CraReport[]>('/cra/reports'),
  craReport: (id: string) => request<CraReport>(`/cra/reports/${id}`),
  craReportOpen: (body: { product_id: string; kind: string; title?: string; description?: string; vuln_id?: string;
    aliases?: string[]; release_ids?: string[]; detection_id?: string; aware_at?: string | null; malicious?: boolean | null }) =>
    request<CraReport>('/cra/reports', { method: 'POST', body: JSON.stringify(body) }),
  craReportAware: (id: string, aware_at: string) =>
    request<CraReport>(`/cra/reports/${id}/aware`, { method: 'POST', body: JSON.stringify({ aware_at }) }),
  craReportStep: (id: string, step: string, body: { at?: string | null; reference?: string; note?: string }) =>
    request<CraReport>(`/cra/reports/${id}/steps/${step}`, { method: 'POST', body: JSON.stringify(body) }),
  craReportMitigation: (id: string, body: { at?: string | null; category: string; description: string; fixed_version: string }) =>
    request<CraReport>(`/cra/reports/${id}/mitigation`, { method: 'POST', body: JSON.stringify(body) }),
  craReportIntermediate: (id: string, due_at: string) =>
    request<CraReport>(`/cra/reports/${id}/intermediate`, { method: 'POST', body: JSON.stringify({ due_at }) }),
  craReportClose: (id: string, reason: string) =>
    request<CraReport>(`/cra/reports/${id}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** The path of the users' advisory (CSAF 2.0 VEX), for scaricaJson. */
  craReportCsafPath: (id: string) => `/cra/reports/${id}/csaf`,
  craRequirements: () =>
    request<{ products: Array<{ id: string; name: string; items: CraRequirement[] }>; declared: CraDeclared[] }>('/cra/requirements'),
  craDeclare: (requirementId: string, body: { stato: string; nota: string; riferimento: string; responsabile: string }) =>
    request<unknown>(`/cra/declarations/${requirementId}`, { method: 'PUT', body: JSON.stringify(body) }),
  supplyRenovatePreview: (projectId: string) =>
    request<RenovateTriggerPlan>(`/supply-chain/projects/${projectId}/renovate/preview`, { method: 'POST' }),
  supplyRenovateRun: (projectId: string) =>
    request<{ id: string; status: string; pipeline_id: number | null; web_url: string }>(`/supply-chain/projects/${projectId}/renovate`, { method: 'POST' }),
  /** Certificate notifications: rules, history, recipient suggestions. */
  certAlerts: () => request<CertAlertsOverview>('/software-flow/certificates/alerts'),
  certAlertHistory: (key: string) =>
    request<CertAlertSentInfo[]>(`/software-flow/certificates/alerts/history?key=${encodeURIComponent(key)}`),
  createCertAlertRule: (body: CertAlertRuleIn) =>
    request<CertAlertRule>('/software-flow/certificates/alerts/rules', { method: 'POST', body: JSON.stringify(body) }),
  updateCertAlertRule: (id: string, body: Partial<CertAlertRuleIn>) =>
    request<CertAlertRule>(`/software-flow/certificates/alerts/rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteCertAlertRule: (id: string) =>
    request<void>(`/software-flow/certificates/alerts/rules/${id}`, { method: 'DELETE' }),
  /** What the rule in the form would send at the next pass, and to whom. */
  previewCertAlertRule: (body: CertAlertRuleIn & { rule_id?: string }) =>
    request<CertAlertPreview>('/software-flow/certificates/alerts/preview', { method: 'POST', body: JSON.stringify(body) }),
  /** The rule's mail to the signed-in user only; nothing is recorded. */
  testCertAlertRule: (body: CertAlertRuleIn & { rule_id?: string }) =>
    request<{ sent_to: string; certificates: number }>('/software-flow/certificates/alerts/test', { method: 'POST', body: JSON.stringify(body) }),
  businessApps: () => request<BusinessAppSummary[]>('/business-apps'),
  businessAppsExportPath: (fmt: 'xlsx' | 'pdf') => `/business-apps/export?fmt=${fmt}`,

  // Utilizzo AI: quali AI esterne si usano in rete e da chi (modulo ai_usage).
  aiUsageStatus: () => request<{ available: boolean; reason: string | null }>('/ai-usage/status'),
  aiUsageDiagnostics: () => request<{
    licensed: boolean; catalog_count: number; last_sighting_at: string | null;
    usage_rows: number; ip_map_count: number; ip_map_sni: number
    netflow_visible?: string[]; netflow_blind?: string[]; agents_reading?: number; flow_keep_count?: number
  }>('/ai-usage/diagnostics'),
  aiUsageStats: (periodo: AiPeriodo) =>
    request<AiUsageStats>(`/ai-usage/stats?${periodoQuery(periodo)}`),
  aiUsageSummary: (days: number) =>
    request<AiUsageSummary>(`/ai-usage/summary?days=${days}`),
  /** L'export con il fuso del browser: la mappa giorno × ora va nell'ora di chi la legge. */
  aiUsageExportPath: (fmt: 'xlsx' | 'pdf', periodo: AiPeriodo) =>
    `/ai-usage/export?fmt=${fmt}&${periodoQuery(periodo)}&tz=${encodeURIComponent(fusoOrario())}`,
  aiUsageSeries: (days: number) =>
    request<{ day: string; count: number }[]>(`/ai-usage/series?days=${days}`),
  aiUsageByAsset: (body: { days: number; from_date?: string; to_date?: string; pc_key?: string; user_key?: string }) =>
    request<AiUsageByAsset>('/ai-usage/by-asset', { method: 'POST', body: JSON.stringify(body) }),
  aiUsagePrivacy: () => request<AiPrivacyView>('/ai-usage/privacy'),
  aiUsagePrivacySave: (body: { anonymize?: boolean; pc_key?: string | null; user_key?: string | null }) =>
    request<AiPrivacyView>('/ai-usage/privacy', { method: 'PUT', body: JSON.stringify(body) }),
  businessApp: (id: string) => request<BusinessApp>(`/business-apps/${id}`),
  createBusinessApp: (payload: {
    name: string
    description?: string | null
    owner?: string | null
    elements?: BusinessAppElementIn[]
    functions?: Array<{ name: string; criticality: Criticita; description?: string | null }>
    service_ids?: string[]
  }) => request<BusinessApp>('/business-apps', { method: 'POST', body: JSON.stringify(payload) }),
  updateBusinessApp: (id: string, payload: { name?: string; description?: string | null; owner?: string | null }) =>
    request<BusinessApp>(`/business-apps/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteBusinessApp: (id: string) => request<void>(`/business-apps/${id}`, { method: 'DELETE' }),
  addBusinessAppElements: (id: string, elements: BusinessAppElementIn[]) =>
    request<BusinessApp>(`/business-apps/${id}/elements`, { method: 'POST', body: JSON.stringify({ elements }) }),
  removeBusinessAppElement: (id: string, elementId: string) =>
    request<BusinessApp>(`/business-apps/${id}/elements/${elementId}`, { method: 'DELETE' }),
  addBusinessAppFunction: (id: string, payload: { name: string; criticality: Criticita; description?: string | null; element_ids?: string[] }) =>
    request<BusinessApp>(`/business-apps/${id}/functions`, { method: 'POST', body: JSON.stringify(payload) }),
  updateBusinessAppFunction: (id: string, functionId: string, payload: { name?: string; criticality?: Criticita; description?: string | null; element_ids?: string[] }) =>
    request<BusinessApp>(`/business-apps/${id}/functions/${functionId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteBusinessAppFunction: (id: string, functionId: string) =>
    request<BusinessApp>(`/business-apps/${id}/functions/${functionId}`, { method: 'DELETE' }),
  setBusinessAppServices: (id: string, serviceIds: string[]) =>
    request<BusinessApp>(`/business-apps/${id}/services`, { method: 'PUT', body: JSON.stringify({ service_ids: serviceIds }) }),
  /** The business services a CI is part of, and the criticality it inherits. */
  ciBusinessServices: (ciId: string) => request<CIBusinessServices>(`/cis/${ciId}/business-services`),
  /** CENSO SBOM: the software map of the tenant, a CI's bill of materials, a service's software. */
  softwareMap: () => request<SoftwareMap>('/software-map'),
  softwareMapComponents: (q: { q?: string; ecosystem?: string; scope?: string; vulnerable?: boolean } = {}) => {
    const p = new URLSearchParams()
    if (q.q) p.set('q', q.q)
    if (q.ecosystem) p.set('ecosystem', q.ecosystem)
    if (q.scope) p.set('scope', q.scope)
    if (q.vulnerable) p.set('vulnerable', 'true')
    return request<{ components: SoftwareMapComponent[]; total: number; truncated: boolean }>(
      `/software-map/components?${p.toString()}`)
  },
  softwareMapWhere: (ecosystem: string, name: string, version: string) =>
    request<Array<{ ci_id: string; ci_name: string; application: string; path: string; scope: string }>>(
      `/software-map/components/where?${new URLSearchParams({ ecosystem, name, version }).toString()}`),
  softwareMapLicenses: () => request<SoftwareMapLicense[]>('/software-map/licenses'),
  // Export (Excel/PDF) delle sezioni dell'inventario software: i path si danno a
  // downloadBlob (il token va in header, non si può usare un <a href>).
  softwareListExportPath: (fmt: 'xlsx' | 'pdf', q: { q?: string; kind?: string } = {}) => {
    const p = new URLSearchParams({ fmt })
    if (q.q) p.set('q', q.q)
    if (q.kind) p.set('kind', q.kind)
    return `/software-inventory/list/export?${p.toString()}`
  },
  softwareDeadlinesExportPath: (fmt: 'xlsx' | 'pdf') => `/software-inventory/deadlines/export?fmt=${fmt}`,
  softwareMapExportPath: (fmt: 'xlsx' | 'pdf') => `/software-map/export?fmt=${fmt}`,
  softwareLicensesExportPath: (fmt: 'xlsx' | 'pdf') => `/software-map/licenses/export?fmt=${fmt}`,
  containerInventory: () => request<ContainerInventory>('/container-inventory'),
  softwareList: (q: { q?: string; kind?: string } = {}) =>
    request<{ items: SoftwareListItem[]; total: number; counts: { system: number; extra: number }; hosts: number; truncated: boolean }>(
      `/software-inventory/list?${new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]).toString()}`),
  softwareWhere: (name: string, version: string) =>
    request<Array<{ ci_id: string; ci_name: string; version: string; path: string; source: string }>>(
      `/software-inventory/where?${new URLSearchParams({ name, version }).toString()}`),
  softwareDeadlines: () =>
    request<{ items: SoftwareDeadline[]; counts: { expired: number; behind: number; expiring: number; extended: number; extended_expiring: number }; systems: number; software: number }>(
      '/software-inventory/deadlines'),
  licenseComponents: (license: string) =>
    request<Array<{ ecosystem: string; name: string; version: string; servers: number; example_ci_id: string }>>(
      `/software-map/licenses/components?${new URLSearchParams({ license }).toString()}`),
  ciContainers: (ciId: string) =>
    request<{ runtimes: Array<{ name: string; version: string }>; containers: ContainerItem[] }>(`/container-inventory/ci/${ciId}`),
  ciSbom: (ciId: string) => request<SbomView>(`/software-map/ci/${ciId}`),
  ciSbomCycloneDxPath: (ciId: string) => `/software-map/ci/${ciId}/cyclonedx`,
  /** One application of the CI as its own CycloneDX document (also the «os»/«extra» groups). */
  applicationSbomCycloneDxPath: (ciId: string, applicationId: string) =>
    `/software-map/ci/${ciId}/applications/${encodeURIComponent(applicationId)}/cyclonedx`,
  importSbom: (ciId: string, document: string, application = '') =>
    request<{ application: string; format: string; components: number; vulnerable: number }>(
      `/software-map/ci/${ciId}/import`, { method: 'POST', body: JSON.stringify({ document, application }) }),
  deleteSbomApplication: (id: string) => request<void>(`/software-map/applications/${id}`, { method: 'DELETE' }),
  serviceSbom: (serviceId: string) => request<ServiceSbom>(`/software-map/services/${serviceId}`),
  serviceSbomCycloneDxPath: (serviceId: string) => `/software-map/services/${serviceId}/cyclonedx`,
  ciRuntime: (ciId: string, hours = 24) => request<RuntimeView>(`/software-map/ci/${ciId}/runtime?hours=${hours}`),
  serviceRuntime: (serviceId: string, hours = 24) =>
    request<ServiceRuntime>(`/software-map/services/${serviceId}/runtime?hours=${hours}`),
  serviceComposition: (
    id: string,
    opts: { windowHours?: number; common?: boolean; infra?: boolean } = {},
  ) =>
    request<Composizione>(
      `/services/${id}/composition${qs({
        window_hours: opts.windowHours ?? 24,
        common: opts.common ? 'true' : 'false',
        infra: opts.infra === false ? 'false' : 'true',
      })}`,
    ),
  flowComposition: (payload: {
    address: string
    window?: string
    depth?: number
    fanout?: number
    common?: boolean
    infra?: boolean
    hops?: ServiceHop[]
    filters?: FiltriComposizione
  }) => request<Composizione>('/flows/composition', { method: 'POST', body: JSON.stringify(payload) }),
  updateService: (id: string, payload: Record<string, unknown>) =>
    request<BusinessService>(`/services/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteService: (id: string) => request<void>(`/services/${id}`, { method: 'DELETE' }),
  serviceMap: (id: string, windowHours = 24) =>
    request<ServiceMap>(`/services/${id}/map${qs({ window_hours: windowHours })}`),

  // --- Asset ---
  assets: (params: {
    q?: string
    category?: string
    status?: string
    cost_center?: string
    cost_center_id?: string
    org_unit_id?: string
    location_id?: string
    uncovered?: boolean
    unlinked?: boolean
    limit?: number
  } = {}) => request<Asset[]>(`/assets${qs(params)}`),
  asset: (id: string) => request<Asset>(`/assets/${id}`),
  assetSummary: () => request<AssetSummary>('/assets/summary'),
  assetCatalog: () => request<AssetCatalog>('/assets/catalog'),
  /** Bozza precompilata dalla discovery: mostra ciò che si sa già. */
  assetDraft: (ciId: string) => request<AssetDraft>(`/assets/draft/${ciId}`),
  createAsset: (payload: Record<string, unknown>) =>
    request<Asset>('/assets', { method: 'POST', body: JSON.stringify(payload) }),
  updateAsset: (id: string, patch: Record<string, unknown>) =>
    request<Asset>(`/assets/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteAsset: (id: string) => request<void>(`/assets/${id}`, { method: 'DELETE' }),

  // --- Campi personalizzati (definizioni) ---
  customFields: (entity?: 'ci' | 'asset') =>
    request<CustomFieldDefinition[]>(`/custom-fields${qs({ entity })}`),
  createCustomField: (payload: CustomFieldWrite) =>
    request<CustomFieldDefinition>('/custom-fields', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCustomField: (id: string, payload: CustomFieldWrite) =>
    request<CustomFieldDefinition>(`/custom-fields/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteCustomField: (id: string) =>
    request<void>(`/custom-fields/${id}`, { method: 'DELETE' }),

  // --- Contratti ---
  contracts: (params: {
    q?: string
    vendor?: string
    contract_type?: string
    status?: string
    cost_center_id?: string
    org_unit_id?: string
    location_id?: string
    eagle_sla_id?: string
    asset_id?: string
    expiring_days?: number
  } = {}) => request<Contract[]>(`/contracts${qs(params)}`),
  contract: (id: string) => request<Contract>(`/contracts/${id}`),
  contractSummary: () => request<ContractSummary>('/contracts/summary'),
  contractCatalog: () => request<ContractCatalog>('/contracts/catalog'),
  createContract: (payload: Record<string, unknown>) =>
    request<Contract>('/contracts', { method: 'POST', body: JSON.stringify(payload) }),
  updateContract: (id: string, patch: Record<string, unknown>) =>
    request<Contract>(`/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteContract: (id: string) => request<void>(`/contracts/${id}`, { method: 'DELETE' }),
  renewContract: (id: string, payload: Record<string, unknown>) =>
    request<Contract>(`/contracts/${id}/renew`, { method: 'POST', body: JSON.stringify(payload) }),
  terminateContract: (id: string, payload: { at?: string | null; reason?: string | null }) =>
    request<Contract>(`/contracts/${id}/terminate`, { method: 'POST', body: JSON.stringify(payload) }),
  duplicateContract: (id: string, reference?: string) =>
    request<Contract>(`/contracts/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ reference: reference ?? null }) }),
  contractEvents: (id: string) => request<ContractEvent[]>(`/contracts/${id}/events`),
  addContractNote: (id: string, note: string) =>
    request<ContractEvent>(`/contracts/${id}/events`, { method: 'POST', body: JSON.stringify({ note }) }),
  assetContracts: (id: string) => request<AssetContract[]>(`/assets/${id}/contracts`),
  contractAssets: (id: string) => request<CoveredAsset[]>(`/contracts/${id}/assets`),
  // --- Supporto esteso (support_extension_service) ---
  supportExtensions: (contractId: string) =>
    request<SupportExtension[]>(`/contracts/${contractId}/support-extensions`),
  supportExtensionCandidates: () =>
    request<SupportExtensionCandidate[]>('/contracts/support-extensions/candidates'),
  previewSupportExtension: (contractId: string, body: SupportExtensionWrite) =>
    request<SupportExtensionPreview>(`/contracts/${contractId}/support-extensions/preview`,
      { method: 'POST', body: JSON.stringify(body) }),
  createSupportExtension: (contractId: string, body: SupportExtensionWrite) =>
    request<SupportExtension>(`/contracts/${contractId}/support-extensions`,
      { method: 'POST', body: JSON.stringify(body) }),
  updateSupportExtension: (contractId: string, id: string, body: SupportExtensionWrite) =>
    request<SupportExtension>(`/contracts/${contractId}/support-extensions/${id}`,
      { method: 'PUT', body: JSON.stringify(body) }),
  deleteSupportExtension: (contractId: string, id: string) =>
    request<void>(`/contracts/${contractId}/support-extensions/${id}`, { method: 'DELETE' }),
  ciContracts: (ciId: string) => request<CiContracts>(`/cis/${ciId}/contracts`),
  // --- Supplier registry (supplier_service) ---
  supplierOverview: () => request<SupplierOverview>('/suppliers/overview'),
  suppliers: () => request<Supplier[]>('/suppliers'),
  supplierOptions: () => request<{ id: string; name: string; status: string }[]>('/suppliers/options'),
  supplier: (id: string) => request<Supplier>(`/suppliers/${id}`),
  createSupplier: (body: SupplierWrite) =>
    request<Supplier>('/suppliers', { method: 'POST', body: JSON.stringify(body) }),
  updateSupplier: (id: string, body: SupplierWrite) =>
    request<Supplier>(`/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteSupplier: (id: string) => request<void>(`/suppliers/${id}`, { method: 'DELETE' }),
  mergeSupplier: (id: string, intoId: string) =>
    request<Supplier>(`/suppliers/${id}/merge`, { method: 'POST', body: JSON.stringify({ into_id: intoId }) }),
  detachSupplierAlias: (id: string, aliasId: string) =>
    request<void>(`/suppliers/${id}/aliases/${aliasId}`, { method: 'DELETE' }),
  refreshSuppliers: () =>
    request<{ names: number; new_candidates: number; linked: number }>('/suppliers/refresh', { method: 'POST' }),
  supplierCandidates: (state: 'proposed' | 'ignored') =>
    request<SupplierCandidate[]>(`/suppliers/candidates?state=${state}`),
  acceptSupplierCandidates: (keys: string[]) =>
    request<{ created: number }>('/suppliers/candidates/accept', { method: 'POST', body: JSON.stringify({ keys }) }),
  mergeSupplierCandidates: (keys: string[], supplierId: string) =>
    request<{ merged: number }>('/suppliers/candidates/merge',
      { method: 'POST', body: JSON.stringify({ keys, supplier_id: supplierId }) }),
  ignoreSupplierCandidates: (keys: string[]) =>
    request<{ ignored: number }>('/suppliers/candidates/ignore', { method: 'POST', body: JSON.stringify({ keys }) }),
  restoreSupplierCandidates: (keys: string[]) =>
    request<{ restored: number }>('/suppliers/candidates/restore', { method: 'POST', body: JSON.stringify({ keys }) }),
  supplierProducts: (id: string, q = '') =>
    request<{ items: SupplierProduct[]; total: number }>(`/suppliers/${id}/products${qs({ q: q || undefined, limit: 500 })}`),
  supplierInstallations: (id: string, name: string, version?: string) =>
    request<{ ci_id: string; ci_name: string; ci_type: string; version: string }[]>(
      `/suppliers/${id}/installations${qs({ name, version })}`),
  supplierFindings: (id: string) => request<SupplierFinding[]>(`/suppliers/${id}/findings`),
  supplierRelated: (id: string) => request<SupplierRelated>(`/suppliers/${id}/related`),
  suppliersExportPath: (fmt: 'xlsx' | 'pdf') => `/suppliers/export?fmt=${fmt}`,
  supplierRequestItems: (id: string, kind: SupplierRequestKind) =>
    request<SupplierRequestItem[]>(`/suppliers/${id}/request-items?kind=${kind}`),
  supplierRequests: (id: string) => request<SupplierRequest[]>(`/suppliers/${id}/requests`),
  allSupplierRequests: () => request<SupplierRequest[]>('/suppliers/requests/all'),
  createSupplierRequest: (id: string, body: { kind: SupplierRequestKind; item_keys: string[]; message: string;
    due_days: number; recipients?: string[]; cc?: string[]; reply_to?: string }) =>
    request<SupplierRequest>(`/suppliers/${id}/requests`, { method: 'POST', body: JSON.stringify(body) }),
  updateSupplierRequest: (rid: string, body: { message?: string; due_days?: number; recipients?: string[];
    cc?: string[]; reply_to?: string }) =>
    request<SupplierRequest>(`/suppliers/requests/${rid}`, { method: 'PUT', body: JSON.stringify(body) }),
  previewSupplierRequest: (rid: string) =>
    request<{ subject: string; text: string; html: string; to: string[]; cc: string[]; reply_to: string }>(
      `/suppliers/requests/${rid}/preview`),
  sendSupplierRequest: (rid: string) =>
    request<SupplierRequest>(`/suppliers/requests/${rid}/send`, { method: 'POST' }),
  answerSupplierRequest: (rid: string, answers: Record<string, SupplierAnswer>) =>
    request<SupplierRequest>(`/suppliers/requests/${rid}/answers`, { method: 'POST', body: JSON.stringify({ answers }) }),
  applySupplierRequest: (rid: string, expiresAt: string) =>
    request<{ statements: number }>(`/suppliers/requests/${rid}/apply`,
      { method: 'POST', body: JSON.stringify({ expires_at: expiresAt }) }),
  closeSupplierRequest: (rid: string, reason: string) =>
    request<SupplierRequest>(`/suppliers/requests/${rid}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),
  supplierStatements: (id: string) => request<SupplierStatement[]>(`/suppliers/${id}/statements`),
  supplierAssessments: (id: string) =>
    request<{ assessments: SupplierAssessment[]; certifications: SupplierCertification[]; check: SupplierCheck }>(
      `/suppliers/${id}/assessments`),
  createSupplierAssessment: (id: string, body: Partial<SupplierAssessment>) =>
    request<SupplierAssessment>(`/suppliers/${id}/assessments`, { method: 'POST', body: JSON.stringify(body) }),
  deleteSupplierAssessment: (aid: string) => request<void>(`/suppliers/assessments/${aid}`, { method: 'DELETE' }),
  createSupplierCertification: (id: string, body: Partial<SupplierCertification>) =>
    request<SupplierCertification>(`/suppliers/${id}/certifications`, { method: 'POST', body: JSON.stringify(body) }),
  deleteSupplierCertification: (cid: string) => request<void>(`/suppliers/certifications/${cid}`, { method: 'DELETE' }),
  supplyChainCheck: () => request<SupplyChainCheck>('/suppliers/checks/supply-chain'),
  doraRegisterExportPath: (fmt: 'xlsx' | 'pdf') => `/suppliers/dora-register/export?fmt=${fmt}`,
  supplierDocuments: (id: string) => request<SupplierDocument[]>(`/suppliers/${id}/documents`),
  uploadSupplierDocument: (id: string, body: { kind: SupplierDocument['kind']; document: string; product: string;
    versions: string; filename: string }) =>
    request<SupplierDocument>(`/suppliers/${id}/documents`, { method: 'POST', body: JSON.stringify(body) }),
  supplierDocumentStatements: (did: string) => request<SupplierDocumentStatement[]>(`/suppliers/documents/${did}/statements`),
  supplierDocumentComparison: (did: string) => request<SupplierSbomComparison>(`/suppliers/documents/${did}/comparison`),
  applySupplierDocument: (did: string, expiresAt: string) =>
    request<{ applied: number; without_justification: number }>(`/suppliers/documents/${did}/apply`,
      { method: 'POST', body: JSON.stringify({ expires_at: expiresAt }) }),
  deleteSupplierDocument: (did: string) => request<void>(`/suppliers/documents/${did}`, { method: 'DELETE' }),
  supplierDocumentOriginalPath: (did: string) => `/suppliers/documents/${did}/original`,
  censoSbom: () => request<CensoSbomCatalog>('/censo/sbom'),
  censoSbomPath: (slug: CensoSbomProduct['slug'], kind: 'sbom' | 'vex') => `/censo/sbom/${slug}/${kind}`,
  revokeSupplierStatement: (id: string, sid: string) =>
    request<void>(`/suppliers/${id}/statements/${sid}`, { method: 'DELETE' }),
  coverAssets: (id: string, assetIds: string[], from?: string, until?: string) =>
    request<{ added: number; already_covered: number; not_found: string[] }>(
      `/contracts/${id}/assets`,
      {
        method: 'POST',
        body: JSON.stringify({
          asset_ids: assetIds,
          covered_from: from ?? null,
          covered_until: until ?? null,
        }),
      },
    ),
  updateCoverage: (contractId: string, assetId: string, patch: { covered_from?: string | null; covered_until?: string | null; notes?: string | null }) =>
    request<void>(`/contracts/${contractId}/assets/${assetId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  uncoverAsset: (contractId: string, assetId: string) =>
    request<void>(`/contracts/${contractId}/assets/${assetId}`, { method: 'DELETE' }),

  // --- Report ---
  reports: () => request<ReportInfo[]>('/reports'),
  report: (key: string, days = 90) => request<ReportResult>(`/reports/${key}?days=${days}`),
  /**
   * Percorso del CSV. Il download passa da `fetch` e non da un `<a href>`: il
   * token viaggia in un'intestazione, e un collegamento non la porterebbe — il
   * file scaricato sarebbe una pagina di errore con estensione .csv, che si
   * scopre solo aprendola.
   */
  reportCsvPath: (key: string, days = 90) => `/reports/${key}?days=${days}&fmt=csv`,

  // --- Sito ---
  //
  // Pubblico: dice quale tenant si sta guardando **prima** dell'accesso. Con più
  // clienti aperti in schede diverse, la barra degli indirizzi è esattamente il
  // posto in cui si sbaglia.
  site: () => request<SiteInfo>('/site'),
  siteHostnames: () => request<TenantHostnameRead[]>('/site/hostnames'),
  chooseHostname: (label: string) =>
    request<TenantHostnameRead[]>('/site/hostnames', {
      method: 'PUT',
      body: JSON.stringify({ label }),
    }),
  releaseHostname: (label: string) =>
    request<void>(`/site/hostnames/${encodeURIComponent(label)}`, { method: 'DELETE' }),

  // --- Connettori verso i sistemi di destinazione ---
  //
  // L'ordine delle chiamate è l'ordine del flusso: catalogo, connettore, prova
  // di collegamento, scansione, proposta di mapping, mappatura, prova a vuoto,
  // esecuzione, pianificazione. I passi saltati li rifiuta il Control Plane,
  // non questo file: un controllo che vivesse solo qui si aggirerebbe con una
  // chiamata diretta.
  connectorCatalog: () => request<ConnectorCatalog>('/connectors/catalog'),
  sourceEntity: (entity: string) => request<SourceEntity>(`/connectors/source-entities/${entity}`),
  connectors: () => request<Connector[]>('/connectors'),
  connector: (id: string) => request<Connector>(`/connectors/${id}`),
  createConnector: (payload: {
    name: string
    kind: string
    base_url: string
    config: Record<string, string>
    secret: Record<string, string>
    verify_tls: boolean
    execution?: string
    appliance_id?: string | null
    proxy?: Record<string, string>
  }) => request<Connector>('/connectors', { method: 'POST', body: JSON.stringify(payload) }),
  /**
   * Modifica un connettore. `secret` assente lascia il segreto com'è: correggere
   * un indirizzo non deve costringere a reinserire una password che sta su un
   * altro sistema.
   */
  updateConnector: (
    id: string,
    payload: Partial<{
      name: string
      base_url: string
      config: Record<string, string>
      secret: Record<string, string>
      verify_tls: boolean
      enabled: boolean
      execution: string
      appliance_id: string | null
      proxy: Record<string, string>
    }>,
  ) => request<Connector>(`/connectors/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteConnector: (id: string) => request<void>(`/connectors/${id}`, { method: 'DELETE' }),

  testConnector: (id: string) =>
    request<ConnectorTestResult>(`/connectors/${id}/test`, { method: 'POST' }),
  scanConnector: (id: string) =>
    request<{ object_types: RemoteObjectType[] }>(`/connectors/${id}/scan`, { method: 'POST' }),
  connectorObject: (id: string, objectType: string) =>
    request<RemoteObjectType>(`/connectors/${id}/objects/${encodeURIComponent(objectType)}`),
  suggestMapping: (id: string, sourceEntity: string, targetObject: string) =>
    request<MappingProposal>(
      `/connectors/${id}/suggest${qs({ source_entity: sourceEntity, target_object: targetObject })}`,
    ),

  connectorMappings: (id: string) => request<ConnectorMapping[]>(`/connectors/${id}/mappings`),
  createMapping: (id: string, body: Record<string, unknown>) =>
    request<ConnectorMapping>(`/connectors/${id}/mappings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMapping: (id: string, mappingId: string, body: Record<string, unknown>) =>
    request<ConnectorMapping>(`/connectors/${id}/mappings/${mappingId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteMapping: (id: string, mappingId: string) =>
    request<void>(`/connectors/${id}/mappings/${mappingId}`, { method: 'DELETE' }),

  dryRunMapping: (id: string, mappingId: string) =>
    request<SyncRun>(`/connectors/${id}/mappings/${mappingId}/dry-run`, { method: 'POST' }),
  runMapping: (id: string, mappingId: string) =>
    request<SyncRun>(`/connectors/${id}/mappings/${mappingId}/run`, { method: 'POST' }),
  scheduleMapping: (id: string, mappingId: string, enabled: boolean) =>
    request<ConnectorMapping>(
      `/connectors/${id}/mappings/${mappingId}/schedule${qs({ enabled })}`,
      { method: 'POST' },
    ),
  connectorRuns: (id: string, limit = 50) =>
    request<SyncRun[]>(`/connectors/${id}/runs${qs({ limit })}`),

  // --- Remediation / patching ---
  patchTasks: (params?: { ciId?: string; stato?: string }) =>
    request<PatchTask[]>(`/patching/tasks${qs({ ci_id: params?.ciId, stato: params?.stato })}`),
  patchMissing: (ciId: string) => request<PatchMissing>(`/patching/ci/${ciId}/missing`),
  patchReadiness: (ciId: string) =>
    request<PatchReadiness & { ci_id: string; platform: string; environment: string }>(`/patching/ci/${ciId}/readiness`),
  patchOverview: () => request<PatchOverview>('/patching/overview'),
  patchPropose: (body: { ci_id: string; kind: string; spec?: Record<string, unknown>; source?: string }) =>
    request<PatchTask>('/patching/tasks', { method: 'POST', body: JSON.stringify(body) }),
  patchDryRun: (id: string) => request<PatchTask>(`/patching/tasks/${id}/dry-run`, { method: 'POST' }),
  patchApprove: (id: string, body: {
    window_id?: string; scheduled_at?: string; allow_reboot?: boolean; reboot_delay_min?: number
  }) =>
    request<PatchTask>(`/patching/tasks/${id}/approve`, { method: 'POST', body: JSON.stringify(body) }),
  patchCancel: (id: string) => request<PatchTask>(`/patching/tasks/${id}/cancel`, { method: 'POST' }),
  patchRollback: (id: string) => request<PatchTask>(`/patching/tasks/${id}/rollback`, { method: 'POST' }),
  patchTask: (id: string) => request<PatchTask & { log: string }>(`/patching/tasks/${id}`),
  patchSummary: () => request<PatchSummary>('/patching/summary'),
  patchFleet: () => request<PatchFleet>('/patching/fleet'),
  patchCatalog: () => request<PatchCatalog>('/patching/catalog'),
  patchCompliance: (days = 90, environment = '') =>
    request<PatchComplianceView>(`/patching/compliance${qs({ days, environment })}`),
  patchReport: (days = 30, environment = '') => request<PatchReport>(`/patching/report${qs({ days, environment: environment || undefined })}`),
  /** The audit evidence for a CVE, a patch or a machine. */
  patchEvidence: (q: { cve?: string; patch_key?: string; ci_id?: string }) =>
    request<PatchEvidence>(`/patching/evidence${qs(q)}`),
  patchEvidenceExportPath: (q: { cve?: string; patch_key?: string; ci_id?: string }, fmt: 'xlsx' | 'pdf') =>
    `/patching/evidence/export${qs({ ...q, fmt })}`,
  /** Patch reports mailed on a cadence (also one per asset owner). */
  patchReportSchedules: () => request<PatchReportSchedule[]>('/patching/report-schedules'),
  patchReportScheduleCreate: (body: PatchReportScheduleInput) =>
    request<PatchReportSchedule>('/patching/report-schedules', { method: 'POST', body: JSON.stringify(body) }),
  patchReportScheduleUpdate: (id: string, body: PatchReportScheduleInput) =>
    request<PatchReportSchedule>(`/patching/report-schedules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  patchReportScheduleSend: (id: string) =>
    request<{ result: string }>(`/patching/report-schedules/${id}/send`, { method: 'POST' }),
  patchReportScheduleDelete: (id: string) => request<void>(`/patching/report-schedules/${id}`, { method: 'DELETE' }),
  /** Which computers CENSO really assesses for patches, and which not. */
  patchCoverage: () => request<PatchCoverage>('/patching/coverage'),
  /** Tickets on the ITSM (ServiceNow, Jira) for the patches to install. */
  patchTickets: (status?: string) => request<{ tickets: PatchTicket[]; settings: PatchTicketSettings;
    connectors: { id: string; name: string; kind: string }[] }>(`/patching/tickets${qs({ status })}`),
  patchTicketOpen: (patchKey: string) =>
    request<PatchTicket>('/patching/tickets', { method: 'POST', body: JSON.stringify({ patch_key: patchKey }) }),
  patchTicketSettings: (body: PatchTicketSettings) =>
    request<PatchTicketSettings>('/patching/tickets/settings', { method: 'PUT', body: JSON.stringify(body) }),
  /** Exceptions on patches: requested, approved by another person, expiring. */
  patchExceptions: (status?: string) =>
    request<{ exceptions: PatchException[]; settings: { self_approval: boolean } }>(`/patching/exceptions${qs({ status })}`),
  patchExceptionRequest: (body: PatchExceptionRequest) =>
    request<PatchException>('/patching/exceptions', { method: 'POST', body: JSON.stringify(body) }),
  patchExceptionDecide: (id: string, approve: boolean, note: string) =>
    request<PatchException>(`/patching/exceptions/${id}/decision`, { method: 'POST', body: JSON.stringify({ approve, note }) }),
  patchExceptionRevoke: (id: string) => request<PatchException>(`/patching/exceptions/${id}/revoke`, { method: 'POST' }),
  patchExceptionRenew: (id: string, days: number, reason: string) =>
    request<PatchException>(`/patching/exceptions/${id}/renew`, { method: 'POST', body: JSON.stringify({ days, reason }) }),
  patchExceptionSettings: (selfApproval: boolean) =>
    request<{ self_approval: boolean }>('/patching/exceptions/settings', { method: 'PUT', body: JSON.stringify({ self_approval: selfApproval }) }),
  /** Rollout policies: what, where ring after ring, when — approved once. */
  patchPolicies: () => request<PatchPolicy[]>('/patching/policies'),
  patchPolicyCreate: (body: PatchPolicyInput) =>
    request<PatchPolicy>('/patching/policies', { method: 'POST', body: JSON.stringify(body) }),
  patchPolicyUpdate: (id: string, body: PatchPolicyInput) =>
    request<PatchPolicy>(`/patching/policies/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  patchPolicyApprove: (id: string) => request<PatchPolicy>(`/patching/policies/${id}/approve`, { method: 'POST' }),
  patchPolicyRunNow: (id: string) => request<PatchPolicyRun>(`/patching/policies/${id}/run`, { method: 'POST' }),
  patchPolicyRuns: (id: string) => request<PatchPolicyRun[]>(`/patching/policies/${id}/runs`),
  patchPolicyRunStop: (runId: string) => request<PatchPolicyRun>(`/patching/policies/runs/${runId}/stop`, { method: 'POST' }),
  patchPolicyDelete: (id: string) => request<void>(`/patching/policies/${id}`, { method: 'DELETE' }),
  /** The tenant's SLA policy for patches. */
  patchSla: () => request<PatchSla>('/patching/sla'),
  patchSlaSave: (body: PatchSlaPolicy) => request<PatchSla>('/patching/sla', { method: 'PUT', body: JSON.stringify(body) }),
  /** The life of each missing patch: new, fixed, reopened, mean time to remediate. */
  patchLifecycle: (days = 90) => request<PatchLifecycle>(`/patching/lifecycle${qs({ days })}`),
  patchDetections: (q: { status?: 'open' | 'fixed' | 'removed'; ci_id?: string; patch_key?: string; days?: number } = {}) =>
    request<PatchDetection[]>(`/patching/lifecycle/detections${qs(q)}`),
  patchDetection: (id: string) => request<PatchDetectionDetail>(`/patching/lifecycle/detections/${id}`),
  patchReportExportPath: (kind: 'executive' | 'full', fmt: 'pdf' | 'xlsx', days: number, environment = '') =>
    `/patching/report/export${qs({ kind, fmt, days, environment: environment || undefined })}`,
  patchJobPlan: (body: PatchJobTargets) =>
    request<PatchJobPlan>('/patching/jobs/plan', { method: 'POST', body: JSON.stringify(body) }),
  patchJobCreate: (body: PatchJobInput) =>
    request<PatchJobDetail>('/patching/jobs', { method: 'POST', body: JSON.stringify(body) }),
  patchJobs: () => request<PatchJob[]>('/patching/jobs'),
  patchJob: (id: string) => request<PatchJobDetail>(`/patching/jobs/${id}`),
  patchJobApprove: (id: string) =>
    request<PatchJobDetail>(`/patching/jobs/${id}/approve`, { method: 'POST' }),
  patchJobDryRun: (id: string) =>
    request<PatchJobDetail>(`/patching/jobs/${id}/dry-run`, { method: 'POST' }),
  patchJobCancel: (id: string) =>
    request<PatchJobDetail>(`/patching/jobs/${id}/cancel`, { method: 'POST' }),
  patchJobRetry: (id: string) =>
    request<PatchJobDetail>(`/patching/jobs/${id}/retry`, { method: 'POST' }),
  patchWindows: () => request<MaintenanceWindow[]>('/patching/windows'),
  patchWindowCreate: (w: MaintenanceWindow) =>
    request<MaintenanceWindow>('/patching/windows', { method: 'POST', body: JSON.stringify(w) }),
  patchWindowUpdate: (id: string, w: MaintenanceWindow) =>
    request<MaintenanceWindow>(`/patching/windows/${id}`, { method: 'PUT', body: JSON.stringify(w) }),
  patchWindowDelete: (id: string) =>
    request<void>(`/patching/windows/${id}`, { method: 'DELETE' }),

  // --- Firmware serviti in TFTP dalle sonde ---
  firmwareCatalog: () => request<FirmwareCatalog>('/network/firmware'),
  firmwareProbes: () => request<FirmwareProbeDTO[]>('/network/firmware/probes'),
  firmwareBeginUpload: (body: { name: string; size: number; sha256?: string }) =>
    request<FirmwareUploadState>('/network/firmware/uploads', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  firmwareUploadStatus: (uploadId: string) =>
    request<FirmwareUploadState>(`/network/firmware/uploads/${uploadId}`),
  firmwareAppendChunk: (uploadId: string, offset: number, chunk: Blob) =>
    putBytes<FirmwareUploadState>(
      `/network/firmware/uploads/${uploadId}${qs({ offset })}`,
      chunk,
    ),
  firmwareFinalize: (uploadId: string) =>
    request<FirmwareFileDTO>(`/network/firmware/uploads/${uploadId}/finalize`, { method: 'POST' }),
  firmwareSetProbes: (fileId: string, appliance_ids: string[]) =>
    request<FirmwareFileDTO>(`/network/firmware/${fileId}/probes`, {
      method: 'PUT',
      body: JSON.stringify({ appliance_ids }),
    }),
  firmwareDelete: (fileId: string) =>
    request<void>(`/network/firmware/${fileId}`, { method: 'DELETE' }),
  /** Move a file's expiry, or remove it (null = never). */
  firmwareSetExpiry: (fileId: string, expires_at: string | null) =>
    request<FirmwareFileDTO>(`/network/firmware/${fileId}/expiry`, {
      method: 'PUT',
      body: JSON.stringify({ expires_at }),
    }),
  /** Default expiry of the files uploaded from now on (days, 0 = never). */
  firmwareSetRetention: (days: number) =>
    request<{ retention_days: number }>('/network/firmware/settings', {
      method: 'PUT',
      body: JSON.stringify({ days }),
    }),
}

export interface PatchTask {
  id: string
  ci_id: string
  ci_name?: string | null
  platform: string
  kind: string
  source: string
  spec: Record<string, unknown>
  status: string
  allow_reboot: boolean
  reboot_required: boolean
  dry_run: Record<string, unknown> | null
  result: Record<string, unknown>
  scheduled_at: string | null
  window_id: string | null
  approved_by: string | null
  requested_by: string
  rollback_of: string | null
  created_at: string | null
  approved_at?: string | null
  started_at?: string | null
  finished_at?: string | null
  job_id?: string | null
  /** The check after the install (patch_jobs_service.verify). */
  verification?: PatchVerification | null
  /** Why a proposed task did not go (a guard at approval), or why it failed. */
  message?: string | null
  dry_run_requested?: boolean
  /** Stato dell'agente del CI (per capire perché un task resta in attesa). */
  agent_online?: boolean | null
  agent_version?: string | null
  agent_last_seen?: string | null
  agent_update?: boolean
}

export interface PatchVerification {
  status: 'waiting_inventory' | 'to_assess' | 'verified' | 'still_missing' | 'reboot_pending'
    | 'no_inventory' | 'unverified'
  checked_at?: string
  still_missing?: string[]
  remaining?: number
}

/** One change of a task: a package or a Windows update, from → to. */
export interface PatchChange {
  name: string
  from?: string
  to?: string
  title?: string
  severity?: string
  security?: boolean
  size_bytes?: number
  reboot?: boolean
  result?: string
}

export interface PatchReadiness {
  ready: boolean
  blocked: boolean
  reasons: string[]
  missing_tags: string[]
  agent_id: string | null
  agent_name: string | null
  agent_version: string | null
  agent_online: boolean | null
  agent_last_seen: string | null
  engine: number | null
  manager: string
  /** What updates third-party programs on the machine (winget, choco); null = not known yet. */
  software_managers: string[] | null
  running: { task_id: string; kind: string; since: string } | null
}

export interface PatchFleetHost extends PatchReadiness {
  ci_id: string
  name: string
  ci_type: string
  platform: string
  os: string
  environment: string
  missing: number
  kb: number
  packages: number
  software: number
  critical: number
  exploited: number
  overdue: number
  accepted: number
  worst_severity: string
  /** The machine's risk: its worst missing patch, 0–100 (patch_risk_service). */
  risk?: number
  risk_label?: string
  oldest_days: number
  end_of_life: boolean
  up_to_date: boolean
  within_sla: boolean
  last_patch_at: string | null
  last_patch_status: string | null
}

export interface PatchFleet {
  hosts: PatchFleetHost[]
  environment_key: string
  sla_days: Record<string, number>
}

export interface PatchCatalogEntry {
  key: string
  kind: 'kb' | 'package' | 'software'
  name: string
  title: string
  product: string
  ecosystem: string
  fixed: string
  platforms: string[]
  severity: string
  exploited: boolean
  cves: string[]
  cve_count: number
  host_count: number
  hosts: Array<{ ci_id: string; name: string }>
  first_seen: string
  age_days: number
  sla_days: number
  overdue: boolean
  priority: number
  priority_label: 'urgent' | 'high' | 'medium' | 'low'
  /** The reasons of the risk score (patch_risk_service), on the worst machine. */
  risk_factors?: PatchRiskFactor[]
  /** The highest EPSS among its CVEs: probability of exploitation in 30 days. */
  epss?: { score: number; percentile: number } | null
  /** The open ticket on the ITSM for this patch, if any. */
  ticket?: { number: string; url: string; system: string } | null
  installable: boolean
}

export interface PatchRiskFactor {
  factor: 'severity' | 'kev' | 'epss' | 'criticality' | 'production' | 'exposed' | 'overdue'
  points: number
  detail: string
}

export interface PatchCatalog {
  patches: PatchCatalogEntry[]
  sla_days: Record<string, number>
  /** Whether a ticket connector is set (Patching → Ticket). */
  ticketing?: boolean
}

export interface PatchComplianceGroup {
  environment: string
  hosts: number
  up_to_date: number
  within_sla: number
  missing: number
  critical: number
  exploited: number
  overdue: number
  end_of_life: number
  /** The score of every missing patch on every machine, summed (0243). */
  risk: number
  /** Machines whose worst missing patch is urgent. */
  urgent_hosts: number
}

export interface PatchTrendPoint {
  day: string
  hosts: number
  up_to_date: number
  within_sla: number
  missing: number
  critical: number
  exploited: number
  overdue: number
  risk: number
  urgent_hosts: number
}

export interface PatchComplianceView {
  total: PatchComplianceGroup
  by_environment: PatchComplianceGroup[]
  trend: PatchTrendPoint[]
  sla_days: Record<string, number>
}

export interface PatchJobProgress {
  total: number
  waiting: number
  blocked: number
  running: number
  applied: number
  needs_reboot: number
  failed: number
  canceled: number
  verified: number
  still_missing: number
}

export interface PatchJob {
  id: string
  name: string
  kind: 'security_all' | 'all_updates' | 'selected' | 'reboot' | 'third_party'
  spec: { kbs?: string[]; packages?: string[]; software?: string[] }
  targets: { tag: string; machines: number; skipped: Array<{ ci_id: string; name: string; reason: string }> }
  schedule: 'now' | 'window' | 'at'
  window_id: string | null
  window_name: string | null
  scheduled_at: string | null
  allow_reboot: boolean
  reboot_delay_min: number
  status: string
  phase: 'to_approve' | 'scheduled' | 'running' | 'blocked' | 'done' | 'done_reboot' | 'done_errors' | 'canceled'
  progress: PatchJobProgress
  created_by: string
  created_at: string | null
  approved_by: string | null
  approved_at: string | null
}

export interface PatchJobDetail extends PatchJob {
  tasks: PatchTask[]
}

export interface PatchJobTargets {
  kind: string
  spec?: { kbs?: string[]; packages?: string[]; patch_keys?: string[] }
  ci_ids?: string[]
  tag?: string
}

export interface PatchJobInput extends PatchJobTargets {
  name?: string
  schedule: 'now' | 'window' | 'at'
  window_id?: string
  scheduled_at?: string
  allow_reboot: boolean
  reboot_delay_min: number
}

export interface PatchJobPlanRow {
  ci_id: string
  name: string
  platform: string
  environment: string
  missing: number
  reasons: string[]
  blocked: boolean
  ready: boolean
  task_kind: string | null
  task_spec?: Record<string, unknown>
  /** Every install the machine gets (KBs and programs are two on Windows). */
  tasks: Array<{ kind: string; spec: Record<string, unknown> }>
  skip: string | null
}

export interface PatchJobPlan {
  kind: string
  spec: Record<string, unknown>
  machines: PatchJobPlanRow[]
  will_run: number
  blocked: number
  skipped: number
}

export interface PatchReport {
  period: { days: number; since: string; until: string }
  /** The environment of the report («-»: machines without one), '' for all. */
  environment: string
  /** Every environment of the fleet, to choose from. */
  environments: string[]
  sla_days: Record<string, number>
  environment_key: string
  kpi: {
    hosts: number
    up_to_date: number
    up_to_date_pct: number
    within_sla: number
    within_sla_pct: number
    missing: number
    patches: number
    urgent: number
    exploited: number
    overdue: number
    end_of_life: number
    not_patchable: number
    needs_reboot: number
    median_age_days: number
    installs_applied: number
    installs_failed: number
    success_pct: number
    updates_installed: number
    machines_patched: number
    mean_install_minutes: number
    verified_pct: number | null
    reboots: number
    jobs: number
    new: number
    fixed: number
    reopened: number
    superseded: number
    open_detections: number
    mttr_days: number | null
    mttr_median_days: number | null
    fixed_within_sla_pct: number | null
    coverage_pct: number | null
    computers: number
    covered: number
    network_only: number
    stale: number
    exceptions_active: number
    exceptions_requested: number
    exceptions_expiring: number
    policy_cycles: number
    policy_cycles_stopped: number
    tickets_opened: number
    tickets_closed: number
    risk_total: number
    urgent_hosts: number
    /** The first daily snapshot of the period with a risk, null before 0243. */
    risk_start: number | null
    risk_change_pct: number | null
  }
  lifecycle: {
    mttr: Record<string, { count: number; mean: number | null; median: number | null; within_sla: number }>
    weeks: Array<{ week: string; detected: number; reopened: number; fixed: number }>
  }
  coverage: {
    total: number
    covered: number
    coverage: number | null
    by_category: Record<'agent' | 'credentials' | 'stale' | 'network_only' | 'unknown_os', number>
    by_environment: Array<{ environment: string; total: number; covered: number }>
    recent_days: number
  }
  /** The ten patches that take away most risk: their score on every machine missing them. */
  risk_reduction: Array<{ key: string; name: string; kind: string; severity: string; priority: number; host_count: number; weight: number }>
  policy_cycles: Array<{
    policy: string
    started_at: string
    status: 'running' | 'done' | 'stopped'
    message: string | null
    rings: Array<{ name: string; status: string; success: number | null; machines: number }>
  }>
  by_severity: Array<{ severity: string; open: number; overdue: number; within_sla: number; sla_days: number }>
  aging: Array<{ bucket: string; critica: number; alta: number; media: number; bassa: number; total: number }>
  by_kind: Array<{ kind: 'kb' | 'package' | 'software'; open: number }>
  by_environment: PatchComplianceGroup[]
  by_platform: Array<{ platform: string; hosts: number; up_to_date: number; within_sla: number; missing: number }>
  activity: Array<{ day: string; applied: number; needs_reboot: number; failed: number; updates: number }>
  trend: PatchTrendPoint[]
  top_patches: PatchCatalogEntry[]
  top_hosts: PatchFleetHost[]
  not_patchable: Array<{ ci_id: string; name: string; reasons: string[]; missing_tags: string[] }>
  end_of_life_hosts: Array<{ ci_id: string; name: string; os: string }>
}

export interface PatchSummary {
  compliance: { total: PatchComplianceGroup; by_environment: PatchComplianceGroup[] }
  sla_days: Record<string, number>
  environment_key: string
  hosts: number
  ready: number
  no_agent: number
  agent_offline: number
  missing_tags: number
  end_of_life: number
  patches: number
  urgent: number
  top_patches: PatchCatalogEntry[]
  failed: number
  needs_reboot: number
  jobs: PatchJob[]
  trend: PatchTrendPoint[]
}

export interface PatchMissingItem {
  /** «container»: a distribution package inside a container image — real,
   *  but fixed by rebuilding the image, not by the machine's package manager. */
  kind: 'packages' | 'kb' | 'software' | 'container'
  /** Container only: the images (applications) that carry the package. */
  images?: string[]
  fixed?: string
  /** The same key as the patch catalog (kb:KB…, pkg:name, software:program). */
  key?: string
  package?: string
  ecosystem?: string
  kb?: string
  product?: string
  version?: string
  source?: string
  severity: string
  exploited: boolean
  cve?: unknown
}

export interface PatchMissing {
  platform: string
  items: PatchMissingItem[]
}

export interface PatchOverviewHost {
  ci_id: string
  name: string
  ci_type: string
  missing: number
  kb: number
  packages: number
  software: number
  critical: number
  exploited: number
  end_of_life: boolean
  worst_severity: string
}

export interface PatchOverview {
  hosts: PatchOverviewHost[]
  totals: {
    hosts_con_patch: number
    hosts_fuori_supporto: number
    patch_totali: number
    kb: number
    packages: number
    software: number
    sfruttate: number
    critiche: number
  }
}

export interface MaintenanceWindow {
  id?: string
  name: string
  enabled: boolean
  recurrence: Record<string, unknown>
  timezone: string
  scope: Record<string, unknown>
}

export interface FirmwareFileDTO {
  id: string
  name: string
  size: number
  sha256: string
  description: string
  uploaded_by: string
  created_at: string | null
  /** When the file leaves the catalogue by itself; null = never. */
  expires_at?: string | null
  /** The row is there but the file is not (lost with a rebuild): upload it again. */
  missing?: boolean
  probe_ids: string[]
}

export interface FirmwareCatalog {
  files: FirmwareFileDTO[]
  used_bytes: number
  quota_bytes: number
  /** Default expiry of new files, in days; 0 = never. */
  retention_days?: number
}

export interface FirmwareProbeDTO {
  id: string
  name: string
  enabled: boolean
  tftp: boolean
}

export interface FirmwareUploadState {
  upload_id: string
  received: number
  size: number
  mismatch?: boolean
}

/** Il periodo di Utilizzo AI: gli ultimi N giorni, o un intervallo scelto (date ISO). */
export type AiPeriodo = { days: number } | { from: string; to: string }

function periodoQuery(periodo: AiPeriodo): string {
  return 'days' in periodo
    ? `days=${periodo.days}`
    : `from=${encodeURIComponent(periodo.from)}&to=${encodeURIComponent(periodo.to)}`
}

function fusoOrario(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}
